import type { GenericNestedObject } from '@common/utils/nestedTypedObject';
import { FundusScreeningThirdParty } from '@common/fundusScreening/thirdParties';
import { FundusScreeningManualImportLogInvitationType } from '@common/interfaces/api/fundusScreeningApi';
import { MINIMUM_PASSWORD_LENGTH } from '@common/practitionerDomain';
import { countries } from './countries/nlCountries';

export const nl = {
    CareProductTypeEnum: {
        DBC: 'DBC',
        MKC: 'MKC',
        OVP: 'OVP',
    },
    Countries: countries,
    StatusLabelEnum: {
        aborted: 'Afgebroken',
        accepted: 'Geaccepteerd',
        acceptedRequester: 'Geaccepteerd',
        analyzed: 'Geanalyseerd',
        answered: 'Beantwoord',
        answeredFirstRound: 'Beantwoord (1e ronde)',
        answeredSecondRound: 'Beantwoord (2e ronde)',
        assetNeeded: 'Dagboek nodig',
        beingGraded: 'Wordt beoordeeld',
        beingPerformed: 'Wordt uitgevoerd',
        cancelled: 'Geannuleerd',
        closed: 'Gesloten',
        completed: 'Afgerond',
        dataTransferred: 'Uitgelezen',
        dischargeCompleted: 'Ontslagbrief',
        dischargeDraft: 'Nieuw ontslag',
        dischargeSent: 'Ontslagbrief verstuurd',
        draftConsult: 'Nieuw consult',
        draftContact: 'Nieuw contact',
        draftCounseling: 'Nieuwe begeleiding',
        draftReferral: 'Nieuwe verwijzing',
        draftRequest: 'Nieuwe aanvraag',
        failed: 'Mislukt',
        inAnalysis: 'In analyse',
        inConcept: 'In concept',
        inProgress: 'In behandeling',
        intakeCompleted: 'Intake afgerond',
        intakeCompletedAdmin: 'Intake',
        intakeDraft: 'Nieuwe intake',
        intakeSent: 'Intake verstuurd',
        invited: 'Uitgenodigd',
        newContact: 'Nieuw contact',
        notYetSent: 'Nog niet verstuurd',
        rejected: 'Afgewezen',
        rejectedAndArchived: 'Gearchiveerd',
        rejectedAndResent: 'Opnieuw verwezen',
        requested: 'Aangevraagd',
        saved: 'Bewaard',
        sent: 'Verstuurd',
        sentFirstRound: 'Verstuurd (1e ronde)',
        sentSecondRound: 'Verstuurd (2e ronde)',
        started: 'Gestart',
        successful: 'Succesvol',
        supervised: 'Gecontroleerd',
        toAccept: 'In behandeling te nemen',
        toAcceptReferral: 'Te accepteren',
        toAnalyze: 'Te analyseren',
        toAnswer: 'Te beantwoorden',
        toGrade: 'Te beoordelen',
        toPerform: 'Uit te voeren',
        toProcess: 'Te verwerken',
        toSupervise: 'Te controleren',
        withAdmin: 'Bij beheerder',
        withAnalysis: 'Bij analist',
        withBiometrician: 'Bij biometrist',
        withColleague: 'Bij collega',
        withGrader: 'Bij grader',
        withPatient: 'Bij patiënt',
        withSpecialist: 'Bij specialist',
        withSupervisor: 'Bij controleur',
    },
    StatusLabelPatientTaskEnum: {
        completed: 'Afgerond',
        open: 'Uit te voeren',
    },
    VatTypesEnum: {
        exempt: 'Vrijgesteld',
        general: 'Algemeen',
    },
    abortProcessReasonsEnum: {
        cardioHolterTooSmall: 'Cardio: Holteropname te klein',
        cardioRecordingNotStarted: 'Cardio: Geen opname gestart',
        doubleEntry: 'Dubbele invoer',
        doubleReferral: 'Dubbele verwijzing',
        failed: 'Mislukt',
        languageBarrier: 'Taalbarrière',
        mistakeInReferral: 'Fout in verwijzing gemaakt',
        newQuestionnairesSent: 'Er zijn nieuwe vragenlijsten verstuurd voor de patiënt',
        noPhotosAdded: "Geen foto's toegevoegd",
        noSmartphone: 'Geen smartphone',
        noSuitableSmartphone: 'Geen geschikte smartphone',
        nonContractedCare: 'Ongecontracteerde zorg',
        other: 'Overig',
        otherMedicalPathway: 'Ander medisch traject',
        patientRefrainsFromExaminationOrNoInterest: 'Patiënt ziet af van onderzoek / Geen interesse',
        preferenceForPhysicalConsultations: 'Voorkeur voor fysieke consulten',
        softwareTooDificult: 'Software te moeilijk',
        test: 'Test',
        tooExpensiveOrExcessBurden: 'Te duur / Eigen risico belast',
        unableContactWithPatient: 'Geen contact met patiënt kunnen leggen',
        unknownReason: 'Reden onbekend',
        wrongEntry: 'Onjuiste invoer',
        wrongPatient: 'Verkeerde patiënt',
        wrongPhotos: "Verkeerde foto's toegevoegd",
    },
    absence: {
        endTimePeriod: 'Einde periode',
        startTimePeriod: 'Start periode',
    },
    accountDeactivation: {
        deactivate: 'Verwijder zorgverlener',
        employeeFromOrganization: 'Medewerker uit organisatie',
        hasActiveRequestsGp:
            'Let op, deze zorgverlener heeft nog actieve zorgprocessen. Neem contact op met Ksyos via 020  600 0060 om het account volledig te de-activeren.',
        hasActiveRequestsSupport: 'Let op, deze zorgverlener heeft nog actieve zorgprocessen.',
        hasActiveRequestsTitle: 'Actieve processen',
        infoText:
            'Deze zorgverlener heeft na de onderstaande datum (einde dag) geen toegang meer tot het Ksyos platform.',
    },
    accountRequest: {
        accountApproved: 'Account goedgekeurd',
        accountDenied: 'Account afgewezen',
        accountDeniedWithReason: 'De account aanvraag is afgekeurd met de volgende reden: ',
        accountRequest: 'Accountaanvraag',
        accountType: 'Type account',
        accountTypeEnum: {
            activeUser: 'Actieve gebruiker Ksyos platform (met login)',
            withoutAccess: 'Verwijzer zonder toegang tot Ksyos platform (bijv. ZorgDomein)',
        },
        addCareProvider: 'Zorgverlener toevoegen',
        agbCodeOrPracticeHolderInfo:
            'Je hebt een beroep gekozen waarvoor een AGB-code vereist is. Vul een AGB-code in, of kies een praktijkhouder. Voor support neem contact op via 020 600 0060',
        agbCodeOrSupervisorInfo:
            'Je hebt een beroep gekozen waarvoor een AGB-code vereist is. Vul een AGB-code in, of kies een supervisor.',
        approve: 'Goedkeuren',
        approved: 'Goedgekeurd',
        approver: 'Beoordelaar',
        approverPractitioner: 'Laten goedkeuren door',
        approverPractitionerDescription:
            'Omdat deze zorgverlener toegang krijgt tot patiëntgegevens is het nog nodig om goedkeuring te vragen aan een andere collega uit jouw organisatie. Deze collega ontvangt een email om de aanvraag te beoordelen in het Ksyos platform.',
        contactSupportInfo: 'Voor support neem contact op via 020 600 0060',
        declineAccount: 'Account afwijzen',
        declineAccountRequest: 'Accountaanvraag afwijzen',
        details: 'Gegevens',
        gender: 'Geslacht',
        identifierDeclaration: 'AGB-code',
        introPageTextPartOne: '{0} wil',
        introPageTextPartTwo:
            'als {0} toevoegen aan {1}. Omdat deze zorgverlener toegang krijgt tot patiëntgegevens is het  nodig om dit verzoek te beoordelen.',
        ksyosReviewsRequest: 'Ksyos behandelt de aanvraag',
        nameAndGenderExistAtSameOrganizationAlert:
            'Er bestaat al een account met deze naam en dit geslacht bij deze organisatie. Klopt het dat je nog een account wilt aanmaken?',
        nameFamily: 'Achternaam',
        nameGiven: 'Voornaam',
        nameInfix: 'Tussenvoegsel',
        openTheKsyosPlatform: 'Open het Ksyos platform',
        pending: 'Wacht op beoordeling',
        practiceHolder: 'Praktijkhouder',
        practitionerAwaitingApprovalFromKsyosTextForGp:
            'Het lijkt erop dat er al een accountaanvraag voor deze zorgverlener is ingediend binnen je organisatie, maar deze nog niet is beoordeeld, neem contact op met Ksyos via 020 600 0060.',
        practitionerAwaitingApprovalModalTitle: 'Account nog niet beoordeeld',
        practitionerAwaitingApprovalTextForGp:
            'Het lijkt erop dat er al een accountaanvraag voor deze zorgverlener is ingediend binnen je organisatie, maar deze is nog niet beoordeeld door {0}. Vraag de beoordelaar om de accountaanvraag te beoordelen of neem contact op met Ksyos via 020 600 0060.',
        practitionerDuplicatedModalText:
            'De gegevens komen overeen met een bestaande zorgverlener. Mogelijk heeft deze medewerker al eerder met Ksyos gewerkt. Neem contact op met Ksyos via 020 600 0060.',
        practitionerDuplicatedModalTitle: 'Deze zorgverlener bestaat al',
        practitionerDuplicatedNameAndGenderModalTextForGp:
            'Er bestaat al een account binnen jouw organisatie met deze achternaam en dit geslacht. Klopt het dat je nog een account wilt aanmaken met dezelfde gegevens? Neem contact op met Ksyos via 020 600 0060.',
        practitionerDuplicatedRegistrationCodeModalTextForGp:
            'Er bestaat al een account binnen deze organisatie met deze gegevens. Klopt het dat je nog een account wilt aanmaken met dezelfde gegevens? Neem contact op met Ksyos via 020 600 0060.',
        practitionerExistingAtExternalOrganizationAlertText:
            'Deze gegevens zijn al bekend bij Ksyos. Kloppen deze gegevens niet, neem dan contact op met Ksyos via 020 600 0060.',
        profession: 'Beroep',
        registrationCodeExistsAtExternalOrganizationAlert: 'Deze gegevens zijn al bekend bij Ksyos.',
        registrationCodeExistsAtSameOrganizationAlert:
            'Er bestaat al een account binnen deze organisatie met deze gegevens.',
        reject: 'Afwijzen',
        rejected: 'Afgewezen',
        rejectedAccountRequest: 'Afgewezen accountaanvraag',
        rejectedAt: 'Afgewezen op',
        rejectionReason: 'Reden van afwijzing',
        requestedBy: 'Aangevraagd door',
        reviewByKsyos: {
            alertWithMatches: {
                description:
                    'Kies een bestaande zorgverlener om deze aan de organisatie toe te voegen. Als je een nieuw account wilt aanmaken, selecteer dan geen zorgverlener en klik op goedkeuren.',
                title: 'De gegevens komen overeen met een al bestaande zorgverlener',
            },
            alertWithoutMatches: {
                title: 'Er was een match op het moment van de aanvraag, maar nu niet meer.',
            },
        },
        reviewRequest: 'Beoordeel verzoek',
        reviewedBy: 'Beoordeeld door',
        selectMatchingPractitionerInfoAlert:
            'De gegevens komen overeen met een bestaande zorgverlener. Selecteer een zorgverlener om toe te voegen aan de organisatie.',
        startDate: 'Startdatum',
        understandingText:
            'ik ben mij bewust dat onze organisatie zelf verantwoordelijk is voor het aanmaken van een nieuw account in het Ksyos platform. Dit account is uitsluitend bedoeld voor een medewerker binnen mijn organisatie.',
        waitingForApprovalOnAgbSameOrganizationAlertFirst: 'Bij deze organisatie wacht een ',
        waitingForApprovalOnAgbSameOrganizationAlertSecond: ' met deze AGB-code nog op beoordeling',
        waitingForApprovalOnNameAndGenderSameOrganizationAlertFirst: 'Bij deze organisatie wacht een ',
        waitingForApprovalOnNameAndGenderSameOrganizationAlertSecond:
            ' met deze naam en dit geslacht op beoordeling. Klopt het dat je nog een account wilt aanmaken?',
        willReceiveNotificationEmail: '{0} zal een activatie email ontvangen.',
        workEmail: 'Werk e-mail',
        wrongApprover: 'Beoordeling door collega',
        wrongApproverDescription:
            'Het lijkt erop dat je een link hebt gebruikt die bedoeld was voor een collega uit jouw organisatie. Kijk in het organisatieoverzicht om te zien wie deze accountaanvraag mag beoordelen.',
        zorgMailNumber: 'ZorgMail nummer',
        zorgmailNummerTooltip: 'Vraag het ZorgMail nummer aan de huisarts of praktijkmanager',
    },
    activity: {
        activityCode: 'Activiteitcode',
        performedDate: 'Uitvoerdatum',
    },
    activityTypeEnum: {
        biometrics: 'Biometrie',
        consultation: 'Consultatie',
        glaucoma: 'Glaucoom',
        grading: 'Grading',
        qualityControl: 'Kwaliteitscontrole',
        request: 'Aanvraag',
        respondingConsultation: 'Beantwoorden consult',
    },
    addCarePathSectionTitles: {
        serviceConsumerSettings: 'Aanvrager',
        serviceProviderSettings: 'Uitvoerder',
        serviceReaderSettings: 'Inzage',
        serviceSupervisorSettings: 'Controleur',
    },
    addCarePathSettingsHeaders: {
        activities: 'Activiteiten',
        activitiesRights: 'Rechten voor activiteiten',
        analysis: 'Analyse',
        canEditDashboard: 'Kan dashboard bewerken',
        canEditGroupSession: 'Kan Groepsvoorlichting management bewerken',
        canEditMdo: 'Kan MDO management bewerken',
        canExportForDato: 'Kan DATO registratie exporteren',
        canReadDashboard: 'Kan dashboard bekijken',
        canReadGroupSession: 'Kan Groepsvoorlichting management bekijken',
        canReadMdo: 'Kan MDO management bekijken',
        canReadQuestionnaireResponses: 'Kan vragenlijsten bekijken',
        canReadTotalWeightLoss: 'Kan weegmomenten bekijken',
        canRegisterGroupSessionAttendance: 'Kan de aanwezigheid van Groepsvoorlichting registreren',
        closeServiceAndSubsequentService: 'Afsluiten dienst en opvolgende dienst',
        dashboardManagement: 'Dashboard management',
        examinationAndGrading: 'Onderzoek en grading',
        exportManagement: 'Export management',
        followUpMeasurementGgz: 'Standaard nameting',
        groupMeetingManagement: 'Groepsvergadering management',
        monitoringManagement: 'Monitoring management',
        notifications: 'Notificaties',
        receiveRequest: 'Aanvraag ontvangen',
        selectConsumer: 'Aanvrager selecteren',
        selectGrader: 'Beoordelaar selecteren',
        software: 'Software',
        startRequest: 'Aanvraag starten',
        txcsIds: "TxCS ID's",
        zorgMailAndNotifications: 'ZorgMail en notificaties',
    },
    addCarePathSettingsLabelsServiceConsumer: {
        addAutoSubsequentServices: 'Automatisch starten opvolgende diensten',
        autoSubsequentServices: 'Selecteer opvolgende dienst',
        followUpMeasurementGgz: 'Standaard nameting sturen',
        isAutoCloseRequest: 'Automatisch sluiten afgehandelde verzoeken',
        isDeviation: 'Alleen bij afwijking',
        isGraderSelector: 'Kan beoordelaar selecteren',
        isRequestCreator: 'Kan een aanvraag starten',
        providerOrganizationId: 'Organisatie uitvoerder',
        providerPractitionerId: 'Zorgverlener uitvoerder',
        referralToSelf: 'Organisatie kan alleen naar zichzelf verwijzen',
        sendAfter: 'Sturen na',
        sendNotificationPractitionerWhenDeviant: 'Stuur notificatie alleen bij afwijkingen',
        sendZorgMailOnExaminationRequest: 'Stuur ZorgMail bij aanvraag onderzoek',
        sendZorgMailOnReferralIntake: 'Stuur ZorgMail bij intake',
        sendZorgMailOnReferralRequest: 'Stuur ZorgMail bij verwijzing',
        weeksForFollowUpMeasurementGgz: 'Aantal weken',
    },
    addCarePathSettingsLabelsServiceEmployment: {
        canPerform: 'Kan {0} uitvoeren',
        canRead: 'Kan {0} lezen',
        canReadAndWrite: 'Kan {0} lezen en schrijven',
        canStartProcess: 'Kan proces starten',
        isAutoCloseRequest: 'Automatisch sluiten afgehandelde verzoeken',
        isRecipient: 'Kan aanvraag ontvangen',
        isRequester: 'Kan aanvrager van proces zijn',
        read: 'lezen',
        sendNotificationPractitionerWhenDeviant: 'Stuur notificatie alleen bij afwijkingen',
        write: 'schrijven',
    },
    addCarePathSettingsLabelsServiceProvider: {
        addOnePreferredServiceConsumerOrganization:
            'De zorgverlener heeft de instelling dat zij aanvragers kunnen selecteren. Je bent daarom verplicht om minstens één organisatie op de aanvrager voorkeurslijst te zetten. Je kunt de voorkeurslijst later altijd nog aanpassen in de voorkeurslijstinstellingen.',
        canSelectCareProviderAsServiceConsumer: 'Kan ook huisarts als aanvrager selecteren',
        isAnalyst: 'Kan analyse uitvoeren',
        isBiometrician: 'Kan onderzoek uitvoeren',
        isGrader: 'Kan onderzoek beoordelen',
        isGraderSelector: 'Kan grader selecteren',
        isRequestCreator: 'Kan onderzoek starten zonder aanvraag',
        isRequestRecipient: 'Kan aanvraag ontvangen',
        isServiceConsumerSelector: 'Kan ontvanger/aanvrager selecteren',
        noOnlyReaderRole:
            'Je hebt minstens één aanvrager, controleur of uitvoerder rol nodig om de instellingen op te mogen slaan.',
        oneMustBeTrue:
            'Minstens één van de volgende mogelijkheden moet gekozen worden: Onderzoek uitvoeren en/of Onderzoek beoordelen.',
        oneMustBeTrueWithAnalyst:
            'Minstens één van de volgende mogelijkheden moet gekozen worden: Analyse uitvoeren, Onderzoek uitvoeren en/of Onderzoek beoordelen.',
        spirometerSoftwareName: 'Spirometer software naam',
        usingRestEcgMeter: 'Gebruikt rust ECG meter',
        usingSpirometer: 'Gebruikt spirometer',
    },
    addCarePathSettingsLabelsShared: {
        sendNotificationEmployment: 'Stuur notificatie naar werk e-mail',
        sendNotificationPractitioner: 'Stuur notificatie naar persoonlijke e-mail',
        spirometerSoftwareName: 'Spirometer software',
        usingRestECGMeter: 'Gebruikt rust ECG meter',
        usingSpirometer: 'Gebruikt spirometer',
    },
    addRights: {
        consumer: 'Aanvrager {0}',
        provider: 'Uitvoerder {0}',
        supervisor: 'Controleur {0}',
        whichRightsToAdd: 'Welke rechten wil je toevoegen?',
    },
    addendum: {
        add: 'Voeg addendum toe',
        addendum: 'Addendum',
        authorAndTime: '{0} om {1} - {2} ({3})',
        edifactWarning:
            'Let op: Dit is een aanpassing op een eerder verstuurde beoordeling van {0} om {1} uur door {2}.',
        edited: '{0} (aangepast)',
        formerly: 'Voorheen',
        header: 'Toevoegen addendum',
        info: 'Wij nemen binnen een werkdag contact op met de huisarts over de correctie en/of aanvulling',
        placeholder: 'Een correctie of aanvulling…',
        previousRequestAbortReason: 'Addendum toegevoegd, nieuwe conclusie is niet afwijkend',
        selectEdits: 'Selecteer wat je wilt aanpassen',
        send: 'Verstuur addendum',
    },
    address: {
        changeAddress: 'Wijzig adres',
    },
    adminProcesses: {
        allProcesses: 'Alle processen',
        exportInProgress: 'Bezig met exporteren',
        exportProcessStep: 'Exporteer processtap',
        exportType: {
            consultations: {
                consultationsAtSpecialist: 'Consulten bij specialist',
                consultationsSecondRoundAtSpecialist: 'Verstuurd 2e ronde bij specialist',
                groupName: 'Consulten (Cardio, Pulmo, Oog, Derma, GGZ)',
            },
            holterEcgExamination: {
                groupName: 'Holter ECG onderzoek',
                holterAtAnalyst: 'Holter bij analist',
                holterAtGrader: 'Holter bij grader',
                holterRead: 'Holter uitgelezen',
            },
        },
        openProcesses: 'Openstaande processen',
        processStep: 'Processtap',
    },
    agenda: {
        agenda: 'Agenda',
    },
    allergies: {
        allergies: 'Allergieën / contra-indicaties',
        codeSystem: 'Code',
        comment: 'Commentaar',
        description: 'Omschrijving',
        displayName: 'Naam',
        overview: 'Overzicht allergieën en contra-indicaties',
        statusCode: 'Status',
        valueCode: 'Waarde',
    },
    allergology: {
        advice: 'Advies',
        allResults: 'Alle uitslagen',
        allergistReferralQuestion: 'Vraag aan allergoloog',
        completeAssessment: 'Volledige beoordeling',
        conclusion: 'Conclusie',
        contraIndications: {
            absoluteContraIndications:
                'Aanwezigheid van dermografie,Gebruik van anti-allergie tabletten (antihistaminica) in de laatste 72 uur voor de huidpriktest,Zwangerschap',
            absoluteContraIndicationsTitle: 'Absolute contra-indicaties',
            disclaimer:
                '*Het gebruik van een onderhoudsbehandeling prednisolon in lage dosering <7,5 mg per dag is wel toegestaan. Betablokkers hoeven eveneens niet te worden gestaakt voorafgaand aan de test.',
            relativeContradictions:
                'Ziekte of nog herstellend van (ernstige) ziekte,Exacerbatie astma (gebruik extra medicatie) of instabiel astma,Urticaria en angio-oedeem vooraf aan de huidpriktest,Eczeem op prikplaatsen (onderarm),Lokaal gebruik van topicaal corticosteroïd (hormoonzalf) op de onderarmen in de afgelopen week,Actuele prednisolon (stoot)kuur of recent afgeronde kuur (< 1 week geleden)*',
            relativeContradictionsTitle: 'Relatieve contra-indicaties',
            subtitle: 'Voor deze zorg is een huidpriktest vereist.',
            title: 'Contra-indicaties voor huidpriktest',
            toggleContraIndicationsClose: 'Relatieve contra-indicaties inklappen',
            toggleContraIndicationsOpen: 'Relatieve contra-indicaties',
        },
        downloadFormBiometry: 'Download formulier huidpriktest',
        fillInSkinPrickTest: 'Vul huidpriktest in',
        formBiomerty: 'Formulier huidpriktest',
        gradeExamination: 'Beoordeel onderzoek',
        gradingCompleted: 'Placeholder: Beoordeling van medisch specialist',
        negativeResults: 'Negatieve uitslagen',
        positiveResults: 'Positieve uitslagen',
        printBiometryForm: 'Printen biometrieformulier',
        questionFromGP: 'Vraag van huisarts',
        reportGrader: 'Verslag medisch specialist',
        reportGraderAlert: 'De volledige beoordeling is inclusief niet-medicamenteuze adviezen',
        serviceCard: {
            cost: 'Eventuele kosten',
            step1: 'Stap 1',
            step2: 'Stap 2',
            step3: 'Stap 3',
            subTitle: 'Ondertitel',
            title: 'Luchtwegallergie',
            waitingTime: 'Wachttijd',
        },
        waitingForBiometryResults:
            'Je gaat uit het patiëntdossier voor het invullen van de waardes, het kan even duren voordat de ingevulde waardes hier staan',
        waitingForGradingResult:
            'Je gaat uit het patiëntdossier voor het beoordelen, als je de beoordeling hebt verstuurd komt deze hier te staan',
    },
    allergologyPatientTaskType: {
        questionnaire: 'Vragenlijst',
        questionnaireNotReceived: 'Vragenlijst niet ontvangen',
        remaining: 'Overig',
        remainingOther: 'Overig',
    },
    anamnesis: {
        anamnesis: 'Anamnese',
        complaints: 'Klachten',
        familyHistory: 'Familiaire voorgeschiedenis',
        history: 'Voorgeschiedenis',
    },
    askEmploymentForSpecializationsModal: {
        description:
            'Geef hier je specialisaties op, en de huisarts en POH-GGZ kunnen je consulten sturen die passen bij jouw specialisatie.',
        label: 'Mijn specialisatie(s)',
        noSpecializations: 'Ik heb geen specialisatie',
        placeHolderMessage: 'Typ en vind je specialisatie…',
        thanksMessage: 'Hartelijk dank voor het invullen!',
        title: 'Relevantere consulten ontvangen?',
    },
    audiologyPatientTaskType: {
        referral: 'Doorverwijzing',
        remaining: 'Overig',
        remainingOther: 'Overig',
    },
    autoSwitchOrganization: {
        warningModalBody:
            'Je wisselt nu van organisatie. Zorg ervoor dat je eventueel openstaand werk in andere tabbladen opslaat voordat je verdergaat.',
        warningModalTitle: 'Let op!',
    },
    availability: {
        endTime: 'Eindtijd',
        startTime: 'Starttijd',
    },
    bankAccountDetails: {
        cancel: 'Annuleren',
        current: 'Huidige rekeninggegevens',
        dataIsProcessedInAFewDays: 'Meestal verwerken we de nieuwe gegevens binnen enkele werkdagen.',
        employmentExplanation:
            'Je gaat een verzoek doen om je rekeninggegevens aan te laten passen. Het kan enkele werkdagen duren voordat je verzoek is verwerkt. De status van je verzoek zie je in Uitbetalingen of Persoonlijke instellingen.',
        employmentFollowStatusExplanation:
            'Als we je rekeninggegevens hebben aangepast zie je dat op de pagina Uitbetalingen of in je Persoonlijke instellingen. Volg daar de status van de wijziging.',
        enterBankAccountNamePlaceholder: 'Vul de tenaamstelling van de rekening in...',
        enterBankAccountNumberPlaceholder: 'Vul het rekeningnummer in...',
        new: 'Nieuwe rekeninggegevens',
        organizationExplanation:
            'Je gaat een verzoek doen om de rekeninggegevens van de organisatie aan te laten passen. Het kan enkele werkdagen duren voordat je verzoek is verwerkt. De status van je verzoek zie je in Uitbetalingen.',
        organizationFollowStatusExplanation:
            'Als we de rekeninggegevens hebben aangepast zie je dat op de pagina Uitbetalingen. Volg daar de status van de wijziging.',
        requestChange: 'Verzoek rekeninggegevens wijzigen',
        submit: 'Verzenden',
        successModalTitle: 'We gaan voor je aan het werk!',
    },
    bankAccountDetailsActionEnum: {
        request: 'Verzoek wijziging verstuurd',
        update: 'Verwerkt in Exact',
    },
    bankAccountDetailsLog: {
        action: 'Actie',
        bankAccountDetailsChangesLog: 'Logboek wijzigingen bankrekeninggegevens',
        details: 'Gegevens',
        modificationDate: 'Datum aanpassing',
        modifiedBy: 'Aangepast door',
        viewChangesLog: 'Logboek wijzigingen inzien',
    },
    bariGroupSessionGroupMeeting: {
        allPatientsPresent: 'Alle patiënten zijn bij deze groepsvoorlichting geweest',
        closeGroupSession: 'Groepsvoorlichting sluiten',
        closeGroupSessionSuccessMessage: 'De groepsvoorlichting is afgesloten',
        emptyWaitingList:
            'Er zijn op dit moment geen patiënten die ingepland moeten worden voor een Groepsvoorlichting',
        processAttendance: 'Verwerk aanwezigheid',
    },
    bariMdoGroupMeeting: {
        allMdoReportsSent: 'Alle MDO verslagen van dit MDO zijn verstuurd.',
        closeMdo: 'MDO sluiten',
        closeMdoSuccessMessage: 'Het MDO is afgesloten',
        dietitianRemark: 'Diëtistische screening',
        emptyWaitingList: 'Er zijn op dit moment geen patiënten die ingepland moeten worden bij een MDO',
        otherRemark: 'Overig',
        psychologistRemark: 'Psychologische screening',
        regularMdoRemark: 'Totaaloordeel',
    },
    bariatricsPatientTaskType: {
        additionalGuidance: 'Extra begeleiding',
        appointmentDietitian: 'Afspraak diëtist',
        appointmentDietitianPostOperation: 'Post-OK-afspraak diëtist',
        appointmentMedicalScreening: 'Afspraak medische screening',
        callBackRequest: 'Terugbelverzoek',
        cancelDietPlanAppointment: 'Post-OK afspraak diëtist annuleren',
        checkOrChangeData: 'Gegevens controleren/wijzigen',
        general: 'Algemeen',
        groupSession: 'Groepsvoorlichting',
        individualSession: 'Individuele voorlichting',
        monitorGeneralPractitionerAccount: 'Monitoren huisartsaccount',
        onHold: 'On hold',
        planAppointment: 'Afspraak inplannen',
        planning: 'Plannen',
        remaining: 'Overig',
        remainingOther: 'Overig',
    },
    batchType: {
        declaration: 'Declaratie',
        invoice: 'Facturatie',
        patientInvoice: 'Facturatie (samengesteld)',
        payout: 'Uitbetalingen',
        singularPatientInvoice: 'Patiënt (enkelvoudig)',
    },
    bittiumRecordingAnalystEnum: {
        dgtlHealth: 'DGTL Health',
        fysiologic: 'Fysiologic',
        ksyosAnalyst: 'Ksyos Analisten Organisatie',
        meddis: 'Meddis',
    },
    calendarComponent: {
        agenda: 'agenda',
        allDay: 'hele dag',
        date: 'Datum',
        day: 'dag',
        event: 'Gebeurtenis',
        month: 'maand',
        moveBack: 'Ga terug',
        moveForward: 'Ga vooruit',
        next: 'volgende',
        previous: 'vorige',
        selectDate: 'Selecteer datum',
        showMore: '+ {0} ',
        time: 'Tijd',
        today: 'vandaag',
        tomorrow: 'morgen',
        week: 'week',
        yesterday: 'gisteren',
    },
    cancelFormDialog: {
        removeProcess: 'Weet je zeker dat je {0} wilt annuleren?',
        stopCreatingProcess: 'Weet je zeker dat je het maken van {0} wilt annuleren?',
        stopEditingProcess: 'Weet je zeker dat je het bewerken van {0} wilt annuleren?',
    },
    cardioPerfectEthnicGroupEnum: {
        americanIndian: 'Amerikaans-Indiaas',
        asian: 'Aziatisch',
        black: 'Zwart',
        caucasian: 'Blank',
        hispanic: 'Latijns-Amerikaans',
        other: 'Anders',
        unknown: 'Onbekend',
    },
    cardiologyPatientTaskType: {
        inactivity: 'Inactiviteit',
        remaining: 'Overig',
        remainingOther: 'Overig',
    },
    careFusionEthnicGroupEnum: {
        afroCaribbean: 'Afro-Caribisch',
        asian: 'Aziatisch',
        caucasian: 'Blank',
        latinAmerican: 'Latijns-Amerikaans',
        nonCaucasian: 'Niet-blank',
        notSpecified: 'Niet aangegeven',
        other: 'Anders',
        polynesian: 'Polynesisch',
    },
    carePath: {
        addTxcsId: 'Wat is de TxCS ID voor deze zorgverlener?',
        backToOrganizationOverview: 'Terug naar organisatie overzicht',
        chosenCarePath: 'Gekozen zorgpad',
        chosenServices: 'Gekozen dienst(en)',
        missingServiceOrganizationSettings: {
            organization:
                'Het zorgpad {0} is nog niet eerder toegevoegd op organisatieniveau. Vul de instellingen voor de organisatie hier in om het zorgpad toe te voegen aan de zorgverlener.',
            serviceGroup:
                'Instellingen voor de dienst {0} missen nog op organisatieniveau. Vul deze instellingen hier in om het zorgpad toe te voegen aan de zorgverlener.',
            serviceGroups:
                'Instellingen voor de diensten {0} missen nog op organisatieniveau. Vul deze instellingen hier in om het zorgpad toe te voegen aan de zorgverlener.',
        },
        multipleChoicesPossible: 'Meerdere keuzes mogelijk',
        noEmailSelected: 'Er is nog geen e-mailadres geselecteerd',
        noServiceOrganizationSettings: 'Er zijn geen organisatieinstellingen voor deze service',
        organizationCanNowUse: '{0} kan nu gebruik maken van',
        overlapData: 'De gegevens komen overeen met een bestaande zorgverlener. ',
        overlapDataMultipleUsers: 'De gegevens komen overeen met bestaande zorgverleners. ',
        overlapDataSingleUser: 'De gegevens komen overeen met een bestaande zorgverlener: {0}. ',
        overlapSamePractitionerSameOrganization:
            'De ingevulde zorgverlener staat al geregistreerd bij de geselecteerde organisatie.',
        overlapSearchProfile: 'Zoek het profiel om toe te voegen aan de organisatie.',
        practitionerCanNowUse: '{0} bij {1} kan vanaf nu gebruik maken van',
        preferredServiceOrganizationConsumersRequiredMessage:
            'Er zijn zorgverleners met de instelling dat zij aanvragers kunnen selecteren. Je bent daarom verplicht om minstens één organisatie op de aanvrager voorkeurslijst te zetten.',
        requesterPreferenceList: 'Aanvrager voorkeurslijst',
        returnToPersonalSettings: 'Terug naar persoonlijke instellingen',
        role: 'Rol',
        roleWithin: 'Rol binnen {0}',
        selectAtLeastOneProfileMessage:
            'Alle rechten zijn uitgezet. Je moet minimaal 1 recht aan hebben staan om de instellingen op te kunnen slaan.',
        selectOrSearchProfession: 'Selecteer beroep of typ om te zoeken..',
        selectSpecialization: 'Specialisatie selecteren',
        specializationWithin: 'Specialisatie binnen {0}',
        stop: 'Stopzetten',
        stopEpisodeCategory: 'Alle {0} diensten stopzetten',
        stopServiceGroup: '{0} stopzetten',
        stopServiceGroupText:
            'Stopt de zorgverlener met {0}? Je kunt hier de dienst stopzetten. De zorgverlener kan dan niet meer werken binnen deze dienst.',
        turnOffRight: 'Rechten uitzetten',
        txcsId: 'TxCS ID',
        whichCarePathToAdd: 'Welk zorgpad wil je toevoegen?',
        whichProfessionToAdd: 'Welk beroep heeft de zorgverlener?',
        whichSubcategoryToAdd: 'Welke dienst(en) wil je toevoegen?',
    },
    carePathInformationRequest: {
        availableOn: 'Ik ben bereikbaar op (dag/tijd)',
        interestedInCarePath: 'Ik heb naast {0} ook interesse in',
        receiveInformation: 'Ontvang informatie op',
        thanks: 'Bedankt voor het aanvragen van informatie over {0}',
        wantsToBeCalled: 'Benader mij telefonisch voor meer informatie',
        willReceiveEmail: 'Je ontvangt van ons binnen enkele momenten een e-mail in je inbox',
    },
    carePlan: {
        anamnese: 'Anamnese',
        consult: 'Consultatie',
    },
    changeEmailAddresssForInvitationModal: {
        failed: "Het versturen van een of meer van de programma's is mislukt, je moet een nieuwe uitnodiging aanmaken",
        pending: 'Binnen enkele momenten kun je de aanvraag opnieuw versturen',
        success: 'Je kunt de aanvraag opnieuw versturen',
    },
    checkGeneralPractitionerModal: {
        text: 'Controleer of de huisartspraktijk juist is en wijzig wanneer nodig.',
    },
    common: {
        Id: 'ID',
        NrOfResults: '{0} Resultaten',
        PerPage: 'Per pagina',
        ResultRange: 'Resultaat {0} - {1}',
        ResultRangeOfTotal: 'Resultaat {0} - {1} van {2}',
        aberrant: 'Afwijkend',
        accept: 'Bevestig',
        actions: 'Actie',
        activeSince: 'Actief sinds',
        activeUntil: 'Actief tot en met',
        activity: 'Activiteit',
        activityType: 'Zorgverlenertype',
        actors: 'Actors',
        add: 'Toevoegen',
        addAddendum: 'Addendum toevoegen',
        addHere: 'Vul deze hier in.',
        addItem: '{0} toevoegen',
        addTo: 'Voeg toe',
        addendum: 'Addendum',
        admin: 'Beheer',
        advanced: 'Geavanceerd',
        advancedSettings: 'Geavanceerde instellingen',
        advancedUpdated: 'Geavanceerd (aangepast)',
        all: 'Alle',
        allItems: 'Alle {0}',
        amount: 'Aantal',
        anamnesis: 'Anamnese',
        and: 'en',
        andOr: 'en/of',
        archived: 'Gearchiveerd',
        areYouSure: 'Weet je het zeker?',
        around: 'Omstreeks {0}',
        assign: 'Draag over',
        at: 'bij',
        authorizations: 'Autorisatie(s)',
        automatically: 'Automatisch...',
        back: 'Terug',
        batchRun: 'Batchrun',
        batchRunNumber: 'Runnummer',
        betweenDates: 'Tussen data:',
        birthDate: 'Geboortedatum',
        both: 'Beide',
        bsn: 'BSN',
        by: 'door',
        cancel: 'Annuleer',
        cancelForm: 'Wil je annuleren?',
        cancelling: 'Annuleren',
        capsLockWarning: 'Let op, je Caps Lock toets staat aan',
        careGroup: 'Zorggroep',
        carePaths: 'Zorgpaden',
        change: 'Pas aan',
        changePassword: 'Wijzig wachtwoord',
        checkContactInfo: 'Controleer of de ingevulde contactgegevens van de patiënt correct zijn.',
        chooseItem: 'Kies {0}',
        city: 'Plaats',
        cityPlaceholder: 'Voer woonplaats in...',
        clearFilters: 'Filters wissen',
        clickAndChooseFromList: 'Klik en kies uit de lijst...',
        clientSafe: 'ClientSafe',
        close: 'Sluiten',
        closeShort: 'Sluit',
        closedAt: 'Gesloten voor of op',
        closes: 'Sluit af',
        code: 'Code',
        colleague: 'collega',
        complaints: 'Klachten',
        completedOn: 'Voltooid op',
        conclusion: 'Conclusie',
        conditions: 'Condities',
        configuredOn: 'Ingesteld op',
        confirmRemove: 'Ja, verwijder',
        consumers: 'Toon afnemers',
        contactDetails: 'Contactgegevens',
        contactPatient: 'Contactgegevens patiënt',
        continue: 'Doorgaan',
        controle: 'Controle',
        copy: 'Kopieer tekst',
        country: 'Land',
        countryPlaceholder: 'Voer land in...',
        created: 'Aangemaakt',
        creation: 'Creatie',
        creationDate: 'Creatiedatum',
        currency: 'EUR',
        dashboard: 'Dashboard',
        data: 'Data',
        date: 'Datum',
        date_ago: 'geleden',
        date_currentMonth: 'Huidige maand',
        date_day: 'dag',
        date_days: 'dagen',
        date_hour: 'uur',
        date_hours: 'uur',
        date_last: 'Afgelopen',
        date_minute: 'minuut',
        date_minutes: 'minuten',
        date_month: 'maand',
        date_months: 'maanden',
        date_seconds: 'seconden',
        date_today: 'Vandaag',
        date_tomorrow: 'Morgen',
        date_year: 'jaar',
        date_years: 'jaar',
        date_yesterday: 'Gisteren',
        dateAdded: 'Datum toegevoegd',
        dateFormat: 'dd-mm-jjjj',
        datePlaceholder: 'dd-mm-jjjj',
        dateUpdated: 'Aangepast',
        declarationInsurer: 'Declaratie bij ZV',
        decline: 'Afwijzen',
        deleteAll: 'Alles wissen',
        deleteEndDate: 'Einddatum verwijderen',
        deleteSearchTerms: 'Zoektermen wissen',
        describeOrChooseFromList: 'Omschrijf of klik en kies uit de lijst...',
        deselect: 'Deselecteer',
        details: 'Gegevens',
        detailsItem: '{0} details',
        disabled: 'Uitgeschakeld',
        download: 'Download',
        edit: 'Bewerk',
        editItem: '{0} bewerken',
        email: 'E-mail',
        emailAddress: 'E-mailadres',
        emailForInvitation: 'E-mailadres voor de uitnodiging',
        emergencyNumber: 'Noodnummer',
        enabled: 'Ingeschakeld',
        endDate: 'Einddatum',
        endedBy: 'Beëindigd door',
        enterAnItem: 'voer een {0} in',
        enterValuePlaceholder: '{0} invullen..',
        episodeCategory: 'Vakgebied',
        ethnicity: 'Etniciteit',
        example: 'Voorbeeld',
        export: 'Exporteren',
        exportData: 'Data exporteren',
        exported: 'Geëxporteerd',
        file: 'Bestand',
        filename: 'Bestandsnaam',
        fillIn: 'Vul in',
        fillInLater: 'Later invullen',
        fillInUsername: 'Gebruikersnaam invullen...',
        filter: 'Filter',
        filterByName: 'Filter op naam',
        filterOn: 'Filteren op',
        filterOnActivity: 'Filter op activiteit...',
        filterOnEpisodeCategory: 'Filter op vakgebied',
        filterOnGeneralPractitioner: 'Filter op huisarts...',
        filterOnGroup: 'Filter op groep...',
        filterOnOrganization: 'Filter op organisatie...',
        filterOnPractitioner: 'Filter op zorgverlener...',
        filterOnService: 'Filter op dienst',
        filterOnServiceType: 'Filter op type',
        filterOnSingular: 'Filter op',
        financialData: 'Financiële gegevens',
        findAPatient: 'Zoek een patiënt',
        findItem: '{0} zoeken',
        fold: 'Inklappen',
        for: 'voor',
        from: 'van',
        fromCapitalized: 'Van',
        functionalStatus: 'Functionele statussen',
        funding: 'Bekostigingen',
        gender: 'Geslacht',
        general: 'Algemeen',
        generalError: 'Er is iets misgegaan.',
        goBack: 'Ga terug',
        goTo: 'Naar {0}',
        goToDefaultPage: 'Klik hier om naar startpagina te gaan.',
        hasYetToBeConfigured: 'Moet nog worden ingesteld',
        healthInsurer: 'Zorgverzekeraar',
        healthInsurers: 'Zorgverzekeraars',
        helpRequest: 'Hulpvraag',
        hide: 'Verberg',
        home: 'Home',
        homeNumber: 'Thuis nummer',
        houseLetter: 'Huisletter',
        houseNo: 'Huisnr.',
        houseNumber: 'Huisnummer',
        houseNumberAndSuffix: 'Huisnummer en toevoeging',
        houseNumberSuffix: 'Toevoeging',
        ifKnown: 'Indien bekend',
        inactivePartner: 'Betrekking inactief',
        inactiveSince: 'Inactief sinds',
        inapplicable: 'n.v.t.',
        invoice: 'Bekostiging',
        invoiceRuleCreated: 'Factuurregel(s) aangemaakt',
        involvedPractitioner: 'Betrokken zorgverlener',
        isActive: 'Actief',
        isGenerated: 'Automatisch gegenereerd',
        isInactive: 'Inactief',
        isNotActive: 'Is niet actief',
        isSelected: 'Geselecteerd',
        kgoStatus: 'Opschoonstatus',
        label: 'Label',
        last: 'Laatste',
        lastLogin: 'Laatste login',
        lastLoginFrom: 'Laatste login vanaf',
        lastModified: 'Laatst gewijzigd',
        length: 'Lengte',
        lessInformation: 'Minder informatie',
        liesWith: 'Ligt bij',
        loading: 'Laden...',
        logging: 'Logs',
        logout: 'Uitloggen',
        makeActive: 'Actief maken',
        managerAuthorizations: 'Rollen & autorisaties',
        medicalProtocol: 'Medisch protocol',
        mobileNumber: 'Mobiel nummer',
        mobileTelephoneNumber: 'Mobiel telefoonnummer',
        modify: 'Wijzigen',
        monitoring: 'Monitoring',
        monthChoice: 'Maandkeuze',
        more: 'Meer',
        moreInformation: 'Meer informatie',
        move: 'Verplaats',
        myPatients: 'Mijn patiënten',
        name: 'Naam',
        nameFamily: 'Achternaam',
        nameGiven: 'Voornaam',
        nameInfix: 'Tussenv.',
        nameInfix2: 'Tussenvoegsel',
        namePatient: 'Naam patiënt',
        new: 'Nieuw',
        next: 'Volgende',
        nextStep: 'Volgende stap',
        no: 'Nee',
        noEmailConfigured: 'Er is op dit moment geen e-mailadres geconfigureerd',
        noOptionsAvailable: 'Geen opties beschikbaar',
        noRemarks: 'Geen toelichting',
        noResult: 'Geen resultaat',
        noSearchResults: 'Er zijn geen zoekresultaten',
        noShows: 'No shows',
        none: 'Geen',
        normal: 'Normaal',
        notAvailable: 'Niet beschikbaar',
        notExported: 'Niet geëxporteerd',
        notMigrated: 'Niet gemigreerd',
        notRelevant: 'Niet relevant',
        notice: 'Let op',
        notifications: 'Notificaties',
        now: 'Nu',
        numberShort: 'Nr.',
        off: 'Uit',
        officeNumber: 'Kantoor nummer',
        old: 'Oude',
        oldAdminPanel: 'Oude beheerscherm',
        on: 'Aan',
        onDate: 'op',
        oneResult: 'resultaat',
        optional: 'Optioneel',
        or: 'of',
        organizationType: 'Type organisatie',
        other: 'Anders',
        overlapMessage: 'Overlappende configuratie gevonden',
        password: 'Wachtwoord',
        payout: 'Uitbetaling',
        payoutsOnly: 'Alleen uitbetalingen',
        pdf: 'PDF',
        performSearch:
            'Zoek je proces of patiënt op met bovenstaande zoekfilters, dan verschijnt hier je zoekresultaat!',
        period: 'Periode',
        periodRequestClosed: 'Periode waarin het proces gesloten is',
        personal: 'Persoonlijk {0}',
        personalData: 'Persoonlijke gegevens',
        personalSettings: 'Persoonlijke instellingen',
        phoneNumber: 'Telefoonnummer',
        phoneNumbers: 'Telefoonnummer (mobiel/thuis)',
        pid: 'PID',
        postMeasurement: 'nameting',
        previous: 'Vorige',
        previousExports: 'Vorige exporten',
        previousStep: 'Vorige stap',
        price: 'Prijs',
        primary: 'Primair {0}',
        print: 'Print',
        proceed: 'Ga verder',
        processId: 'ProcesID',
        processNumber: 'Procesnummer',
        processType: 'Proces type',
        processesBetween: 'Processen tussen',
        processesCreatedBetween: 'Processen aangemaakt tussen',
        profession: 'Beroep',
        profile: 'Profiel',
        providers: 'Toon leveranciers',
        province: 'Provincie',
        questionnaire: 'Vragenlijst',
        readAndWrite: 'Lezen en schrijven',
        readOnly: 'Alleen lezen',
        recordingType: 'Recording type',
        referral: 'Verwijzing',
        referralQuestion: 'Hulpvraag',
        refresh: 'Verversen',
        register: 'Registreer',
        relationNumber: 'Relatienummer',
        remark: 'Opmerking',
        remarks: 'Toelichting',
        remarksOrComments: 'Een toelichting of opmerking...',
        remarksOrCommentsOptional: 'Een toelichting of opmerking (optioneel)...',
        remarksOrQuestions: 'Een toelichting, opmerkingen of vragen...',
        remove: 'Verwijder',
        removeForm: 'Wil je verwijderen?',
        removeItem: '{0} verwijderen',
        removePractitionerFirstUseTourSteps: 'Verwijder tour steps',
        removing: 'Verwijderen',
        repeat: 'Herhaal {0}',
        report: 'Verslag',
        request: 'Vraag aan',
        requested: 'Opgevraagd',
        requestedOn: 'Opgevraagd op',
        result: 'Resultaat',
        results: 'resultaten',
        retrieveTotal: 'Bepaal totaal',
        role: 'rol',
        routeNotFound: 'Gevraagd pad niet gevonden of algemene fout',
        save: 'Bewaar',
        saveAndProceed: 'Opslaan en verder',
        saveButton: 'Opslaan',
        saveDraft: 'Bewaar als concept',
        saveItem: '{0} opslaan',
        saved: 'Opgeslagen',
        savedData: 'Data is opgeslagen.',
        saving: 'Opslaan...',
        search: 'Zoek',
        searchByNameBsnBirthdate: 'Zoek op naam, BSN of geboortedatum (DD-MM-JJJJ)...',
        searchCity: 'Plaats of regio...',
        searchFor: 'Zoeken op',
        searchForSingular: 'Zoek op',
        searchIn: 'Alleen zoeken in...',
        searchName: 'Naam van huisarts of praktijk...',
        searchNameOrCity: 'Zoek op de plaats of naam van de organisatie',
        searchProcessId: 'Zoek op procesId',
        searchRefresh: 'Zoek/Ververs',
        searchResults: 'Zoekresultaten',
        searchTerm: 'Zoekterm',
        searchTermPlaceholder: 'Vul je zoekterm in...',
        searchText: 'Zoek op tekst',
        seeOverview: 'Bekijk volledig overzicht',
        select: 'Selecteer',
        selectAll: 'Alles selecteren',
        selectConsumer: 'Verstuur naar',
        selectGp: 'Selecteer de huisarts van de patiënt',
        selectItem: 'Selecteer {0}...',
        selectOne: 'Klik en kies uit de lijst...',
        selected: 'Geselecteerde',
        send: 'Verzend',
        sendForm: 'Formulier versturen',
        sendTo: 'Verstuur naar',
        sensitiveData: 'Privacygevoelig',
        sentBeforeOrOn: 'Verzonden voor of op',
        sentConsumer: 'Verzending naar afnemer',
        sentProvider: 'Verzending naar leverancier',
        sentTo: 'Verstuurd naar',
        service: 'dienst',
        services: 'Toon diensten',
        set: 'Stel in',
        settings: 'Instellingen',
        show: 'Toon',
        showAll: 'Toon alles',
        showLess: 'minder tonen',
        showMore: 'meer tonen',
        showPersonalSettings: 'Persoonlijke instellingen bekijken',
        smoker: 'Roker',
        smokingDuration: 'Duur van roken',
        some: 'Enigszins',
        specialism: 'Vakgebied',
        specificItem: 'Specifieke {0}',
        specificStartAndEndDate: 'Specifieke begin- en einddatum',
        start: 'Start',
        startDate: 'Begindatum',
        startDateAndEndDate: 'Begin- en einddatum',
        startedAt: 'Gestart voor of op',
        startedBy: 'Gestart door',
        startedOn: 'Gestart op',
        startingDate: 'Startdatum',
        state: 'Provincie',
        statistic: 'Statistiek',
        status: 'Status',
        stop: 'Stop',
        street: 'Straat',
        streetAndHouseNumber: 'Straat en huisnummer',
        streetAndNumber: 'Straatnaam + nummer',
        suffix: 'Toevoeging',
        suffixAbbreviation: 'Toev.',
        summary: 'Samenvatting',
        support: 'Ondersteuning',
        system: 'Systeem',
        task: 'Taak',
        tasks: 'Taken',
        technicalStatus: 'Technische statussen',
        telephone: 'Telefoon',
        terminate: 'Beëindigen',
        thirdPartyMessages: 'Berichten aan derden',
        tickTheRightImage: 'Kies de juiste afbeelding',
        time: 'tijd',
        timestamps: 'Timestamps',
        to: 'tot',
        to2: 'naar',
        toCapitalized: 'Tot',
        toTreat: 'Af te handelen',
        treated: 'Afgehandeld',
        tryAgain: 'Probeer opnieuw',
        txcs: 'TxCS',
        type: 'Type',
        typeToSearch: 'Typ om te zoeken...',
        uncertain: 'Onzeker',
        unchanged: 'Ongewijzigd',
        unknown: 'Niet bekend',
        until: 't/m',
        upToAndIncluding: 'tot en met',
        updated: 'Aangepast',
        uploadOrganizationStamp: 'Praktijk stempel uploaden',
        useExistingExamination: 'Gebruik een eerder onderzoek',
        useExistingExaminationOrConsult: 'Gebruik een eerder onderzoek/consult',
        user: 'Gebruiker',
        userName: 'Gebruikersnaam',
        userNumber: 'Gebruikersnummer',
        vatRateGroup: 'Btw tariefgroep',
        vektis: 'Vektis',
        view: 'Bekijk',
        viewLess: 'bekijk minder',
        viewMore: 'bekijk meer',
        viewProtocol: 'Bekijk protocol',
        website: 'Website',
        weight: 'Gewicht',
        willSendTo: 'Versturen naar',
        wrong: 'Fout',
        yearFormat: 'jjjj',
        yes: 'Ja',
        zipcode: 'Postcode',
        zipcodeAndCity: 'Postcode en plaats',
    },
    companies: {
        ksyosAllergology: 'Ksyos Allergologie',
    },
    compensationHistoryHeaders: {
        compensationChangeDesc: 'Aanpassing',
        modificationDate: 'Datum',
        modifiedBy: 'Aangepast door',
    },
    compensationPayoutLevels: {
        employment: 'zorgverlenerniveau',
        group: 'groepsniveau',
        organization: 'organisatieniveau',
        service: 'dienstniveau',
    },
    compensationPayoutRecipientEnum: {
        organization: 'Organisatie',
        practitioner: 'Zorgverlener',
    },
    compensationPayouts: {
        addFees: 'Vergoeding toevoegen',
        amount: 'Vergoeding',
        current: 'Geldig',
        fees: 'Vergoedingen',
        ignored: 'Genegeerd',
        ignoredFeesPractitionerDueToProfessionLevelPayout:
            'De onderstaande vergoedingen worden vanaf {0} genegeerd. De vergoeding moet per organisatie en per beroep worden ingesteld.',
        organizationPayoutPerProfession:
            'Vanaf {0} wordt de vergoeding per organisatie per beroep bepaald. Vergoedingen die niet per beroep zijn ingesteld, worden genegeerd.',
    },
    consult: {
        answer: 'Antwoord',
    },
    contactsAndTasks: {
        availability: 'Beschikbaarheid',
        completeTask: 'Rond af',
        contacts: 'Contacten',
        createTask: 'Maak aan',
        deleteNote: 'Notitie verwijderen',
        deleteNoteModalBody: 'Dit zal de notitie verwijderen. Weet je het zeker?',
        deleteTaskModalText: 'Kies Ja als je deze Taak wilt verwijderen. Kies Nee als je het wilt behouden.',
        deleteTaskModalTitle: 'Weet je zeker dat je de taak wilt verwijderen?',
        description: 'Beschrijving',
        editNote: 'Notitie wijzigen',
        editTask: 'Bewerk',
        emptyPatientTaskListSubtitle: 'Start een zorgpad voor een patiënt',
        emptyPatientTaskListTitle: 'Je hebt op dit moment geen openstaande taken',
        expectedDateCompleted: 'Streefdatum',
        newNote: 'Nieuwe notitie toevoegen',
        newTask: 'Nieuwe taak',
        noClosedTasksForThisPatient: 'Er zijn geen afgeronde taken voor deze patiënt',
        noContactsForThisPatient: 'Er zijn geen contacten geregistreerd voor deze patiënt',
        noOpenTasksForThisPatient: 'Er zijn geen openstaande taken voor deze patiënt',
        noResultsPatientTaskListSubtitle: 'Controleer je zoekterm en/of pas de filters aan',
        noResultsPatientTaskListTitle: 'We kunnen geen resultaten vinden',
        note: 'Notitie',
        notes: 'Notities',
        openTasks: 'Openstaande taken',
        patientTaskList: {
            birthDate: 'Geboortedatum',
            description: 'Type taak',
            patientName: 'Patiënt',
            performerPractitioner: 'Uitvoerder',
            sortDate: 'Laatste wijziging',
        },
        performer: 'Uitvoerder',
        previouslyCompleted: 'Afgeronde taken',
        registerContact: 'Registreer contact',
        saveTask: 'Aanpassen',
        task: 'Taak',
    },
    contractSettingLogs: {
        linkToLogs: 'Logboek wijzigingen inzien',
        modalTitle: 'Logboek wijzigingen prijsafspraken',
        modificationDate: 'Datum wijziging',
        modificationType: 'Type',
        modifiedBy: 'Gewijzigd door',
        modifiedFields: {
            contractPrice: 'Prijsafspraak',
            endDate: 'Einddatum',
            startDate: 'Startdatum',
        },
        newValue: 'Wijziging',
        serviceType: 'Dienst',
    },
    contractSettings: {
        addFundingRecord: 'Bekostiging toevoegen',
        emptyState: 'Deze organisatie heeft nog geen abonnement bij Ksyos',
        endDateIsLastDayOfMonth: 'Einddatum is altijd op de laatste dag van de maand',
        priceAgreement: 'Prijsafspraak',
        priceAgreements: 'Prijsafspraken',
        startDateIsFirstDayOfMonth: 'Startdatum is altijd op de eerste dag van de maand',
        subscription: 'Abonnement',
        totalPerPatientPerQuarter: 'Totaal per patiënt per kwartaal',
    },
    costInfoAndConsentModal: {
        costAndPaymentStep: {
            alert: 'De patiënt moet ingelicht zijn over de eventuele kosten en hierop akkoord geven om verder te gaan',
            checkbox: 'De patiënt is akkoord met het starten van deze dienst en de kosten die daarbij komen kijken',
            information1a: 'Een ',
            information1b: ' kost ',
            information1c: '(incl. {0}% btw)',
            information2a: 'De patient krijgt een ',
            information2b: 'factuur via e-mail of post',
            informationForeigner3a: 'De patiënt ',
            informationForeigner3b: 'betaalt zelf ',
            informationForeigner3c: 'en ',
            informationForeigner3d: 'declareert ',
            informationForeigner3e: 'bij de verzekering',
            informationPrisoner2a: 'De ',
            informationPrisoner2b: 'instelling ',
            informationPrisoner2c: 'van de patiënt ontvangt de ',
            informationPrisoner2d: 'factuur',
            informationPrisoner3a: 'De instelling declareert de kosten bij het ',
            informationPrisoner3b: 'JenV',
            informationUninsured3a: 'De patiënt ',
            informationUninsured3b: 'moet ',
            informationUninsured3c: 'zelf ',
            informationUninsured3d: 'betalen voor deze dienst',
            noPatientAgreement: 'Akkoordverklaring patiënt nog niet ingevuld',
            patientAgreement: 'Akkoordverklaring patiënt',
        },
        costInfo: 'Kosten en betalen',
        headerStep0: 'Informatie voor de patiënt',
        headerStep1: 'Facturatie',
        insuranceCheck: 'Verzekeringscheck',
        invoice: 'Facturatie',
        patientInvoicePreferenceStep: {
            alert: 'Voor online betalen logt de patiënt in met postcode en huisnummer (zonder toevoeging)',
            askInvoiceAddress: 'Facturatie per post is alleen mogelijk op een adres in Nederland',
            noEmail: 'Als de patiënt geen e-mailadres heeft',
            payViaIdeal1: 'Makkelijk',
            payViaIdeal2: 'Betalen via iDEAL',
            paymentMethodIdeal: 'iDEAL',
            paymentMethodInvoice: 'Post',
            receiveVia: 'Ontvangen via',
        },
    },
    dataChecks: {
        checkComment: 'Controle opmerking',
        checkDate: 'Controle datum',
        checkDetails: 'Controle details',
        checkType: 'Controle type',
        checks: 'KGO controles',
        duplicateOrganizations: 'Dubbele organisaties',
        filterCheckType: 'Filter controle type',
        id: 'ID',
        lastLogin: 'Laatste login',
        lastLoginTooltip:
            'Laatste login van een zorgverlener met een betrekking bij deze organisatie. Let op: als een zorgverlener meerdere betrekkingen heeft dan kan het zijn dat de zorgverlener niet specifiek op deze organisatie heeft ingelogd',
        name: 'Naam',
        organizationCheckOverview: 'Organisatie controle overzicht',
        organizationType: 'Type organisatie',
        practitionerCheckOverview: 'Zorgverlener controle overzicht',
        typeTooltip: 'Hover met je muis over de types voor meer informatie',
    },
    daysEnum: {
        Friday: 'Vrijdag',
        Monday: 'Maandag',
        Saturday: 'Zaterdag',
        Sunday: 'Zondag',
        Thursday: 'Donderdag',
        Tuesday: 'Dinsdag',
        Wednesday: 'Woensdag',
    },
    decisionSupportHip: {
        text: 'Voldoet aan voorwaarden?',
    },
    declarationMethodEnum: {
        generalContractedSettings: 'Declareer met algemene declaratie-instellingen, gecontracteerd',
        generalUncontractedSettings: 'Declareer met algemene declaratie-instellingen, niet gecontracteerd',
        insurerSettings: 'Declareer met verzekeraar-specifieke instellingen',
        none: 'Niet declareren',
    },
    dermaApp: {
        deviceId: 'Apparaat ID',
        expirationDate: 'Verloopdatum',
        logins: 'Derma app logins',
        registrations: 'Derma app registraties',
    },
    dermaModal: {
        checkingForPhotos: "We checken voor foto's...",
        connectedPhotos: "Foto's uit de Ksyos app gedownload",
        instructions:
            "Open de Ksyos Dermatologie app op het mobiele apparaat en voeg daar de foto's aan het consult toe. Nadat je de foto's hebt geüpload klik je op Ga verder.",
        title: "Upload eerst foto's in de app voor je verder gaat",
        uploadedPhotos: "Geüploade foto's",
    },
    desktopProcess: {
        action: 'Actie',
        desktopProcesses: 'Desktop processen',
        showContent: 'Toon details',
        showDesktopProcessContent: 'Toon desktop proces details',
        status: 'Status',
    },
    desktopProcessAccessReason: {
        reasonForAccess: 'Reden om desktop proces details in te zien',
    },
    deviceAccessories: {
        accessories: 'Accessoires',
        accessory: 'Accessoire',
        add: 'Accessoire toevoegen',
        details: 'Accessoire details',
        edit: 'Bewerk accessoire',
        organizationOrGroup: 'Organisatie / groep',
        referenceNumber: 'Batchnummer',
        removeFromGroup: 'Van groep ontkoppelen',
        removeFromOrganization: 'Van organisatie ontkoppelen',
        searchPlaceholder: 'Zoek op organisatie (naam/ID), groep of batchnummer...',
        serialNumber: 'Serienummer',
        type: 'Type accessoire',
        types: 'Accessoire types',
    },
    deviceAccessoryStatusEnum: {
        inStock: 'In voorraad',
        sent: 'Opgestuurd',
    },
    deviceAccessoryTypes: {
        add: 'Accessoire type toevoegen',
        details: 'Accessoire type details',
        deviceAccessoryTypes: 'Accessoire types',
        edit: 'Bewerk accessoire type',
        new: 'Nieuw accessoire type',
        sureToDelete: 'Weet je zeker dat je dit accessoire type definitief wilt verwijderen?',
    },
    deviceStatusEnum: {
        broken: 'Kapot',
        homeExamination: 'Als thuisholter',
        inStock: 'In voorraad',
        internalUse: 'Intern gebruik',
        issued: 'Uitgegeven',
        lost: 'Kwijt',
        returnToProvider: 'Terug naar leverancier',
        sentForRepair: 'Opgestuurd ter reparatie',
        withPatient: 'Bij patiënt',
        writtenOff: 'Afgeschreven',
    },
    deviceTypes: {
        add: 'Device type toevoegen',
        details: 'Device type details',
        deviceTypes: 'Device types',
        edit: 'Bewerk device type',
        isFourteenDaysRecordingSupported: 'Ondersteunt opnames van 14 dagen',
        manufacturer: 'Fabrikant',
        model: 'Model',
        new: 'Nieuw device type',
        sureToDelete: 'Weet je zeker dat je dit device type definitief wilt verwijderen?',
        version: 'Versie',
    },
    devices: {
        add: 'Device toevoegen',
        addDeviceAccessorySuccessMessage: 'Het accessoire is toegevoegd.',
        addDeviceModalTitle: 'Een device toevoegen',
        addDeviceSuccessMessage: 'Het device is toegevoegd',
        addOrganizationTooltip: 'Selecteer meerdere organisaties als het device gedeeld wordt.',
        assistant: 'Medewerker',
        details: 'Device details',
        device: 'Device',
        deviceTypePlaceholder: 'Type en kies het soort device...',
        devices: 'Devices',
        edit: 'Bewerk device',
        filterOnEpisodeCategory: 'Filter op Zorgpad',
        forRepair: 'Ter raparatie bij',
        intakeDate: 'Datum inname',
        issueDate: 'Datum uitgifte',
        lastUsed: 'Laatste gebruikt',
        logbook: 'Logboek wijzigingen inzien',
        logbookModal: {
            date: 'Datum wijziging',
            modifiedBy: 'Gewijzigd door',
            referenceType: 'Organisatie/PID/Medewerker/Ontvanger',
            status: 'Status',
            title: 'Logboek wijzigingen device',
        },
        nameOrLocation: 'Naam/locatie',
        new: 'Nieuw device',
        newStatus: 'Nieuwe status',
        noDataAvailable: 'Geen data bekend',
        notes: 'Notities',
        numberOfRecordings: 'Aantal recordings',
        organizationOrGroup: 'Organisatie / groep',
        organizationPlaceholder: 'Typ en zoek de organisatie...',
        partner: 'Partner',
        processId: 'PID',
        registered: 'Geregistreerd',
        removeDeviceFromOrganization: 'Van organisatie ontkoppelen',
        searchPlaceholder: 'Zoek op organisatie (naam/ID), groep of serienummer...',
        serialNumber: 'Serienummer',
        serialNumberPlaceholder: 'Serienummer...',
        shipmentDate: 'Verzenddatum',
        sureToDelete: 'Weet je zeker dat je deze organisatie definitief wilt verwijderen bij dit device?',
        type: 'Device type',
        usage: 'Gebruik',
    },
    diagnosis: {
        deviant: 'Afwijkend',
        diagnosis: 'Diagnose',
        diagnosisRemarks: 'Opmerking voor diagnose',
        differentialDiagnosis: 'Differentiaal diagnose',
        noAbnormality: 'Geen afwijking',
        noDiagnosisYet: 'Nog geen diagnose',
        probabilityDiagnosis: 'Waarschijnlijkheidsdiagnose',
        selectOrTypeATerm: 'Typ of klik en kies je diagnose',
        specifyDiagnosis: 'Specificeer je diagnose...',
        unableToDiagnose: 'Niet te beoordelen',
        workingDiagnosis: 'Werkdiagnose',
    },
    diagnosticReport: {
        accept: 'Accepteer rapport',
        addAttachment: 'Bijlage meesturen',
        addClarification: 'Reageer',
        additionalQuestion: 'Optionele aditionele vraag over rapport',
        additionalRemarks: 'Overige opmerkingen',
        advice: 'Advies',
        advisedPolicy: 'Geadviseerd beleid',
        assessmentBiometrics: 'Beoordeling biometrie',
        assignColleague: 'Dit consult wordt overgedragen aan je collega:',
        assignConsultation: 'Overdragen consult',
        attachment: 'Bijlage',
        cancelDiagnosticReport: 'Annuleer',
        category_consultReport: 'Antwoord',
        category_contact: 'Contact',
        category_discharge: 'Ontslagbrief',
        category_examinationReport: 'Diagnostische bevinding',
        category_intake: 'Intake-rapport',
        categoryDraft_consultReport: 'Consult',
        categoryDraft_discharge: 'Ontslag',
        categoryDraft_examinationReport: 'Onderzoek',
        categoryDraft_intake: 'Opname',
        clarification: 'Reacties',
        clarification_tooltip: 'Vraag verduidelijking over het consultatie resultaat',
        clarificationReaction: 'Antwoord',
        clarificationReaction_tooltip: 'Geef verduidelijking over het consultatie resultaat',
        clarificationSentMessage: 'Bedankt, je reactie is verstuurd',
        confirmCancelReport: 'Weet je zeker dat je dit rapport wilt annuleren?',
        dateConsult: 'Datum consult met de patient',
        diagnosticReport: 'Diagnose rapport',
        dischargeLetter: 'Ontslagbrief',
        dischargeReport: 'Ontslag',
        examination: 'Onderzoek',
        findings: 'Bevindingen',
        findingsFrontEyeSegment: 'Voorste oogsegment',
        findingsPlaceholder: 'Omschrijf je bevindingen...',
        findingsRearEyeSegment: 'Achterste oogsegment',
        generalRemark: 'Algemene opmerkingen',
        hideClarification: 'Klap de reacties in',
        hideConsultReport: 'Klap het antwoord in',
        hideContact: 'Klap het contact in',
        hideDischargeReport: 'Klap ontslagbrief in',
        hideExaminationReport: 'Klap de bevindingen in',
        hideIntakeReport: 'Klap intake-rapport in',
        intakeHeader: 'Intake',
        intakePlaceholder: 'Typ je intake-rapport...',
        intakeReport: 'Intake-rapport',
        noConsumerOfCoupledOphthalmologyConsult:
            'Je kunt het onderzoek helaas niet versturen omdat je geen rechten hebt om een consult aan te vragen. Neem contact op met de servicedesk van Ksyos via 020 600 0060.',
        noQuestion: 'Er is nog geen vraag gesteld',
        optometricDiagnosis: 'Optometrische diagnose',
        orChoosePolicyFromList: 'Of kies beleid uit lijst',
        otherRelevantInformation: 'Overige relevante informatie',
        patientEmailAddress: 'E-mailadres van de patiënt voor het versturen van de uitnodiging...',
        policy: 'Beleid',
        policyDescription: 'Omschrijving beleid',
        policyPlaceholder: 'Beleid...',
        poligraphyExam: 'Polygrafie onderzoek?',
        qualityPhotosSufficient: 'Kwaliteit foto’s voldoende',
        relationEcgAndComplaints: 'Relatie ECG en klachten',
        remarks: 'Toelichting',
        remarksGrader: 'Toelichting beoordelaar',
        report: '+ bevinding',
        resend: 'Verstuur opnieuw',
        saveAndClose: 'Opslaan en sluiten',
        saveAndSend: 'Verstuur',
        saveClarification: 'Verstuur reactie',
        saveClarificationReaction: 'Verstuur antwoord',
        saveDraft: 'Opslaan als concept',
        selectProgramme: 'Selecteer de programma´s die je wilt versturen.',
        selectTherapyLandProgramme: 'Selecteer Therapieland programma´s',
        sendToHis: 'Sluit af',
        showClarification: 'Bekijk alle reacties',
        showConsultReport: 'Bekijk het hele antwoord',
        showContact: 'Bekijk het hele contact',
        showDischargeReport: 'Klap ontslagbrief uit',
        showExaminationReport: 'Bekijk alle bevindingen',
        showIntakeReport: 'Klap intake-rapport uit',
        status_closed: 'Gesloten',
        status_draft: 'Concept',
        status_open_perfomer: 'Verstuurd',
        status_open_perfomer_answer: 'Antwoord verstuurd',
        status_open_perfomer_question: 'Open vraag',
        status_open_recipient: 'Ontvangen',
        status_open_requester: 'Verstuurd',
        status_open_requester_answer: 'Antwoord beschikbaar',
        status_open_requester_question: 'Vraag verstuurd',
        status_rejected: 'Geweigerd',
        therapyLandProgramme: 'Therapieland programma´s',
        typeConsult: 'Type consult',
        urgency: 'Urgentie',
        videoconsult: 'Videoconsult met patiënt gehad',
        visitToSpecialistNecessary: 'Bezoek aan specialist nodig',
        visitToSpecialistNecessaryNow: 'Bezoek aan specialist nu nodig?',
        workingDiagnosis: 'Werkdiagnose',
    },
    dropzone: {
        addMore: 'Voeg meer bestanden toe',
        addReferralLetter: 'Voeg een verwijsbrief toe',
        array_max: 'Voeg maximaal {0} bestanden toe',
        array_max_1: 'Voeg maximaal 1 bestand toe',
        array_min: 'Voeg minimaal {0} bestanden toe',
        array_min_1: 'Voeg minimaal 1 bestand toe',
        canOnlyUploadTheseFiles:
            'Het bestand dat je probeert te uploaden is niet toegestaan. Je kan hier alleen deze bestanden uploaden: {0}.',
        dragAndDropUploader: 'Drag and Drop uploader',
        dragFiles: 'Klik of sleep je bestanden in dit vak',
        dropFiles: 'Drop bestand',
        fileTooBig: 'Het bestand is te groot',
        fileTooSmall: 'Het bestand is te klein',
        maxFilesExceeded: 'Je kunt maximaal {0} bestanden toevoegen',
        serverNotConfigured: 'Server niet geconfigureerd',
        toUpload: 'naar upload',
        uploadError: 'Bestanden toevoegen mislukt',
    },
    embloomGroupCertificateEnum: {
        ksyos: 'Ksyos certificaat',
        mediis: 'Mediis certificaat',
    },
    embloomQuestionnaires: {
        abbreviation: 'Afkorting',
        addEmbloomQuestionnaires: 'Embloom vragenlijsten toevoegen',
        addNewQuestionnaire: 'Nieuwe vragenlijst toevoegen',
        addQuestionnaire: 'Vragenlijst toevoegen',
        autoGrading: 'Auto-Grading Embloom',
        certificate: 'Embloom Certificaat',
        checkEmail: 'Controleer met de patiënt of het bij ons bekende e-mailadres klopt',
        chooseDecisionSupport: 'Selecteer beslisondersteunen',
        chooseOtherQuestionnaire: 'Selecteer overige vragenlijsten',
        chooseQuestionnaire: 'Kies een Embloom vragenlijst',
        contactServiceDesk: 'Neem contact op met de servicedesk via 020 600 00 60 zodat wij je verder kunnen helpen.',
        decisionSupportQuestionnaires: 'Beslisondersteuners',
        description: 'Omschrijving',
        hasCertificate:
            'Er is een Embloom certificaat beschikbaar. Upload hieronder een nieuwe om het bestaande certificaat te vervangen',
        isActive: 'Actief',
        isDecisionSupport: 'Beslisondersteuner',
        isEmbloomGrading: 'Grading door Embloom',
        isStandard: 'Basisset',
        label: 'Test naam',
        loadingReport: 'Het onderzoek wordt opgehaald..',
        loadingReportFailed: 'Het onderzoek kan niet geladen worden.',
        loadingReportTakesLonger: 'Het duurt iets langer dan normaal.',
        manage: 'Beheer vragenlijsten',
        name: 'Embloom vragenlijsten',
        nameDescription: 'Naam - beschrijving',
        noCertificate: 'De gekozen group heeft geen Embloom certificaat',
        noCertificateShort: 'Embloom certificaat mist',
        noGroup: 'Er is geen groep gekozen. Daarom kan er geen gebruik gemaakt worden van Embloom',
        otherQuestionnaires: 'Overige vragenlijsten',
        patientEmail: 'E-mailadres',
        patientPermission: 'Patiënt verleent toestemming voor het onderzoek',
        pickFromList: 'Of kies uit de lijst',
        refresh: 'Vernieuw lijst aanbod',
        reload: 'Haal beschikbare tests op van Embloom',
        reportOnItsWay: 'Het onderzoek komt er aan!',
        saveQuestionnaires: 'Voeg vragenlijsten toe',
        searchByName: 'Zoek op naam...',
        selectedQuestionnaire: 'Gekozen vragenlijst',
        sendPostMeasurement: 'Nameting sturen',
        setBasicSet: 'Zet basisset',
        setToDefaultSet: 'Terugzetten naar default set',
        standardQuestionnaires: 'Kies de standaard collectie van Embloom vragenlijsten',
        testId: 'Test nummer',
        weeks: 'weken',
    },
    employment: {
        activeFrom: 'Actief vanaf',
        backToProfile: 'Terug naar profiel',
        bankAccountNumber: 'Rekeningnummer',
        bankAccountStatus: 'Status Rekeningnummer',
        canAfter: 'kan na ',
        chooseProfession: 'Beroep kiezen',
        dontHaveActiveActivities: 'De zorgverlener heeft geen actieve processen',
        emailAddress: 'Betrekking e-mailadres',
        emailAddressPayouts: 'E-mailadres voor uitbetalingen',
        emergencyNumber: 'Spoednummer',
        employment: 'Betrekking',
        employmentAdded: '{0} kan bij {1} aan de slag',
        employmentRole: {
            activeUntil: 'Actief t/m',
        },
        employments: 'Betrekkingen',
        end: 'Beëindig dienstverband',
        endAll: 'Beëindig alle dienstverbanden',
        endAllEmploymentsModalBody:
            'Weet je zeker dat je alle betrekkingen van deze zorgverlener wilt beëindigen? De zorgverlener kan dan niet meer inloggen of geselecteerd worden als aanvrager of verwijzer.',
        endEmploymentModalBody: 'Weet je zeker dat je deze betrekking van deze zorgverlener wilt beëindigen?',
        financialEmailAddress: 'Financieel e-mailadres',
        groupManager: 'Zorggroep-beheerder',
        mayHaveActiveActivities: 'Let op, de zorgverlener heeft mogelijk nog actieve processen',
        mayHaveActiveActivitiesForCareProvider:
            'Let op, er zijn mogelijk nog lopende processen op naam van deze zorgverlener',
        noEmployments:
            'Er zijn geen betrekkingen voor deze zorgverlener. Voordat er diensten en zorgplannen aan een zorgverlener kunnen worden toegevoegd dient er er eerst een betrekking te zijn.',
        notWork: ' niet meer in Ksyos bij',
        organizationManager: 'Organisatiebeheerder',
        organizationManagerExplanation:
            'Deze persoon kan de uitbetalingen aan de organisatie inzien en een wijziging van de bankrekening aanvragen.',
        overlapMessage: 'Overlappende configuratie gevonden',
        paymentDetails: 'Betaalgegevens',
        profession: 'Beroep',
        relationNumber: 'Relatienummer',
        save: 'Betrekking opslaan',
        specialism: 'Specialisme',
        subscriber: 'Tenaamstelling bankrekening',
        work: ' werken',
        workEmail: 'Werk e-mail',
        zorgmail: 'ZorgMail',
        zorgmailNummer: 'ZorgMail nummer',
    },
    employmentTaxesDetails: {
        alert: {
            collapsedTextFirstPart:
                'Ksyos erkent en respecteert de privacy rechten van de individuen wiens persoonsgegevens worden verwerkt, en verbindt zich ertoe passende maatregelen te nemen. Een van de maatregelen is dat deze aanvullende persoonsgegevens alleen toegankelijk zijn voor Ksyos medewerkers die deze dienen door te geven aan de Belastingdienst. Daarnaast worden deze persoonsgegevens veilig gedeeld met de Belastingdienst en met geen enkele andere instanties en/of individuen. Ksyos bewaart de gegevens volgens de wettelijke termijn en deze worden daarna vernietigd.',
            collapsedTextSecondPart:
                'Voor vragen over het Ksyos privacybeleid neem dan contact op met de Ksyos Functionaris gegevensbescherming via ',
            completeDetailsButton: 'Vul je gegevens aan',
            descriptionLinkText: 'uitbetaalde bedragen aan derden',
            descriptionTextFirstPart: 'Om te voldoen aan de wet',
            descriptionTextSecondPart:
                'zijn wij verplicht om persoonsgegevens over jou aan de Belastingdienst te leveren.',
            title: 'Let op! Er ontbreken nog belangrijke gegevens die nodig zijn voor je uitbetaling.',
        },
        emptyState: 'Je hebt nog geen aanvullende financiële gegevens ingevuld.',
        export: {
            addres: 'Adres',
            dateOfDisbursement: 'Datumuitbetaling',
            initials: 'Voorletters',
            nameInfix: 'Voorvoegsels',
        },
        isNaturalPersonLabel: 'Ik werk als natuurlijk persoon voor Ksyos',
        modal: {
            infoLinkText: 'persoonlijke instellingen',
            infoText: 'Deze gegevens kan je terugvinden en wijzigen onder je ',
            title: 'Aanvullende gegevens uitbetaling',
        },
        multipleOrganizationSuccessMessage: 'Bedankt! Je financiële gegevens bij alle organisaties zijn nu compleet.',
        naturalPersonQuestion:
            "Werk je als natuurlijk persoon voor Ksyos? Dit betekent dat je rechtstreeks uitbetaald wordt door Ksyos. Ook ZZP'er, eenmanszaak, maatschap of VOF vallen hieronder.",
        personalDataInfoFirstPart: 'Om te voldoen aan de wet ',
        personalDataInfoLink: 'uitbetaalde bedragen aan derden',
        personalDataInfoSecondPart:
            ' zijn wij verplicht om persoonsgegevens over jou aan de Belastingdienst te leveren.',
        singleOrganizationSuccessMessage: 'Bedankt! Je financiële gegevens bij {0} zijn nu compleet.',
        title: 'Aanvullende financiële gegevens',
    },
    episodeCategory: {
        activeSettings: 'Actieve instellingen',
        addEpisodeCategory: 'Zorgpad toevoegen',
        addServiceForEpisodeCategory: '{0} dienst toevoegen',
        applicants: 'aanvragers',
        assignRoles: 'Rollen toewijzen',
        chooseEpisodeCategory: 'Zorgpad kiezen',
        chooseService: 'Dienst kiezen',
        confirmCancelMessage: 'Wanneer je afsluit gaan alle wijzigingen verloren.',
        confirmCancelTitle: 'Weet je zeker dat je geen zorgpad wilt toevoegen?',
        episodeCategories: 'Zorgpaden',
        episodeCategory: 'Zorgpad',
        episodeCategoryHeader: 'Open een zorgpad',
        executors: 'uitvoerders',
        foldInactiveCarePath: 'Details inactieve zorgpaden inklappen',
        foldInactiveEpisodeCategory: 'Inactieve zorgpaden inklappen',
        ggzContactInformation: 'GGZ telefonische beschikbaarheid',
        hideInactiveServiceGroups: 'Inactieve diensten verbergen',
        inactiveEpisodeCategories: 'Inactieve zorgpaden',
        inactiveSettings: 'Inactieve instellingen',
        noAdvancedSettings: 'Voor dit deel zijn geen geavanceerde instellingen.',
        organizationSettings: 'Organisatie instellingen',
        showInactiveCarePath: 'Toon details inactieve zorgpaden',
        showInactiveEpisodeCategory: 'Inactieve zorgpaden tonen',
        showInactiveServiceGroup: '1 inactieve dienst tonen',
        showInactiveServiceGroups: '{0} inactieve diensten tonen',
    },
    episodeCategoryEnum: {
        allergology: 'Allergologie',
        allergy: 'Allergie',
        audiology: 'Audiologie',
        bariatrics: 'Obesitas',
        cardiology: 'Cardiologie',
        corona: 'Corona',
        dermatology: 'Dermatologie',
        endocrinology: 'Endocrinologie',
        general: 'Algemeen',
        homeconsultation: 'Thuisconsult',
        labAanvraag: 'Labaanvraag',
        mentalHealth: 'GGZ',
        nephrology: 'Nefrologie',
        neurology: 'Neurologie',
        ophthalmology: 'Oogheelkunde',
        orthopedics: 'Orthopedie',
        pilot: 'Pilot',
        pulmonology: 'Pulmonologie',
        radiology: 'Radiologie',
        somnology: 'Slaap',
        specialistConsultation: 'Meekijkconsult',
    },
    episodes: {
        adminPanel: 'Administratie paneel',
        codeCode: 'Code',
        codeSystem: 'Systeem',
        codeSystemName: 'Systeem Naam',
        comment: 'Commentaar',
        description: 'Omschrijving',
        displayName: 'Naam',
        emptyEpisode: 'Voor deze patiënt heb je nog geen {0} zorgpad',
        episodes: 'Episodes',
        icpc: 'ICPC',
        overview: 'Overzicht episoden',
        statusCode: 'Status Code',
        valueDisplayName: 'Display Naam',
    },
    errorCodes: {
        AUTOSUBSEQUENT_MISSING_PROVIDER: 'Ontbrekende zorgverlener bij volgende dienst',
        BRIGHTPLAN_ADD_ACTION_FAILED: 'Er is iets misgegaan. Probeer het opnieuw of maak een melding bij Tech.',
        BRIGHTPLAN_AUTHENTICATION_FAILED: 'Er is iets misgegaan. Probeer het opnieuw of maak een melding bij Tech.',
        BRIGHTPLAN_GET_LOCATIONS_FAILED:
            'Er is iets misgegaan bij het ophalen van de locaties. Probeer het opnieuw of maak een melding bij Tech.',
        BRIGHTPLAN_GET_OPERATION_TYPES_FAILED:
            'Er is iets misgegaan bij het ophalen van de verrichtingen. Probeer het opnieuw of maak een melding bij Tech.',
        BRIGHTPLAN_GET_PROFESSIONALS_FAILED:
            'Er is iets misgegaan bij het ophalen van de zorgverleners. Probeer het opnieuw of maak een melding bij Tech.',
        BRIGHTPLAN_USERNAME_NOT_FOUND:
            'Je hebt geen BrightPlan account. Vraag een collega om dit voor je aan te maken.',
        CAN_NOT_REMOVE_OWN_ORGANIZATION: 'Het is niet toegestaan om een eigen organisatie te verwijderen',
        DATE_OCCUPIED: 'Op deze datum en tijd staat al een afspraak ingepland',
        DUPLICATE_RECORD: 'Het record bestaat al',
        ERROR: 'Er is iets fout gegaan',
        EXACT_BAD_CREDENTIALS:
            'Het lijkt erop dat we niet de juiste gegevens hebben om verbinding te maken met Exact. Meld een bug en een van onze ontwikkelaars zal kijken hoe we dit kunnen oplossen.',
        EXACT_CONNECTION_FAILURE:
            'Het lijkt erop dat er iets mis is gegaan tijdens het verbinden met Exact. Vernieuw de pagina om er zeker van te zijn dat je een geldige Exact sessie heeft. Lukt het nog steeds niet? Meld dan een bug en een van onze ontwikkelaars zal kijken hoe we dit kunnen oplossen.',
        FORBIDDEN: 'Actie niet toegestaand',
        GROUP_ALREADY_ASSIGNED: 'Groep is al toegevoegd',
        HTTP_403: 'Je hebt onvoldoende rechten om het door jouw gevraagde item of onderdeel daarvan te openen.',
        IMAGE_MAX_FILESIZE_EXCEEDED:
            'Helaas, het toevoegen van een afbeelding groter dan {0} is niet mogelijk. Kies een kleinere afbeelding.',
        IMPORT_WRONG_FILE_TYPE: 'Importeren gefaald, verkeerde bestandsformaat',
        INSURER_SERVICE_HAS_LINKED_DECLARATION_ACTIVITY_PERIODS:
            'De verzekeraarsdienst kan niet worden verwijderd omdat er een of meer declaratie-activiteitsperiodes verbonden zijn',
        INSURER_SERVICE_HAS_LINKED_PRICES:
            'De verzekeraarsdienst kan niet worden verwijderd omdat er een of meer prijsperiodes aan verbonden zijn',
        KSYOS_ROLE_REQUIRED: 'Een Ksyos zorgverlener moet een Ksyos beheerder rol hebben',
        KSYOS_ROLE_REQUIRED_NO_RIGHTS:
            'Een Ksyos zorgverlener moet een Ksyos beheerder rol hebben, maar je hebt geen rechten om beheerder rollen toe te kennen.',
        NO_REQUEST_RECIPIENT: 'Aanvraag ontvanger is verplicht',
        NOT_CONSUMER_SUBSEQUENT_SERVICE: 'Geen afnemen van deze opvolgende dienst',
        OVERLAPPING_CONFIG: 'Overlappende configuratie gevonden',
        STILL_ATTACHED_TO_SERVICEORGANIZATIONSETTINGS:
            'Wordt nog gebruikt voor service Organisatie-instelling leverancier',
        STILL_PROVIDERS_ATTACHED: 'Kan niet verwijderen, er zitten nog beoordelaars aan vast',
        TOO_MANY_ATTACHMENTS: 'Je hebt te veel bestanden toegevoegd. Blijf binnen de limiet van {0} bestanden',
        USER_ALREADY_REGISTERED: 'Je account is al geregistreerd',
        USER_HAS_NO_EMPLOYMENT: 'Je kunt nog niet verder, er is voor jou nog geen betrekking bij een organisatie',
        USER_IS_NOT_ACTIVATED: 'Je kunt niet inloggen. Je account is niet actief',
        USER_NOT_REGISTERED: 'Je kunt nog niet inloggen. Je bent nog niet geregistreerd',
        USERNAME_EXISTS: 'Gebruikersnaam bestaat al. Kies een andere gebruikersnaam',
    },
    errorPages: {
        generalBody:
            'We hebben allemaal wel eens onze ups en downs. Ons technische team gaat het zo snel mogelijk oplossen.',
        generalNeedHelp: 'Hulp nodig? Bel',
        generalOpeningHours: 'Maandag - vrijdag van 8:30 - 17:00 uur',
        generalSendEmail: 'Of stuur een e-mail naar info@ksyos.nl',
        generalTitle: 'Er is iets verkeerd gegaan.',
        goToHome: 'Ga naar home',
        goToPatients: 'Zoek een patiënt',
        missingAuthorizationBody: 'Je hebt geen toegang (meer) tot deze pagina.',
        notFoundBody: 'Je bent naar een pagina geleid die niet bestaat. We helpen je graag weer op de juiste weg.',
        notFoundTitle: 'Oeps. Verkeerde afslag.',
        organizationId: 'Organisatie ID',
        rumSessionId: 'Sessie ID',
        unauthorizedAction: 'Je hebt geen rechten om deze actie uit te voeren.',
        userId: 'Gebruiker ID',
    },
    errorResponses: {
        authorization: 'Je hebt geen toegang (meer) tot deze pagina of dit bestand.',
        controller: 'Er is een fout opgetreden. (Technische status: {0})',
        csrfTokenMissing: 'Het beveilingsteken mist.',
        csrfTokenNoMatch: 'Het beveilingsteken is incorrect.',
        internal: 'Er is een interne server fout opgetreden.',
        joiValidation: 'Er zit een fout in de aanvraag. (Technische details: type "{0}", path "{1}")',
        noSession:
            'Je sessie is afgelopen of je was niet ingelogd. Navigeer alstublieft naar <a href="/home?view=login">login</a>.',
        notAllowed: 'Je hebt geen toegang (meer) tot deze pagina of dit bestand.',
        notFound: 'Deze pagina (of dit bestand) bestaat niet.',
        unexpected: 'Er is een onverwachte interne fout opgetreden.',
        validation: 'Er zit een fout in de aanvraag. (Technische code: {0})',
    },
    evaluation: {
        afterwardsReferPatient: 'Verwijs je deze patiënt nog door naar de specialist?',
        otherwiseReferPatient: 'Had je deze patiënt zonder Ksyos doorverwezen naar de specialist?',
        otherwiseReferPatientTitle: 'Je vraag is verstuurd',
        question: 'Zouden we je nog wat vragen mogen stellen?',
        reportHelpful: 'Ben je geholpen met het antwoord van de specialist?',
        reportInstructive: 'Heb je geleerd van het antwoord van de specialist?',
    },
    eventEcgCardioExaminationDurationEnum: {
        fourteenDays: '14 dagen',
        sevenDays: '7 dagen',
    },
    exact: {
        allBatchesComplete: 'Alle exportbestanden zijn succesvol afgerond',
        complete: 'Afgerond',
        credentialsMissing:
            'De inloggegevens voor Exact ontbreken. Rapporteer een bug, dan kijkt een van onze developers hoe dit opgelost kan worden.',
        exportDeclared: 'Bijna klaar om de gegevens naar Exact te exporteren.',
        exportFailedGeneric:
            'Het exporteren van de gegevens naar Exact is niet gelukt. Probeer het later nog eens. Lukt het dan nog niet? Rapporteer een bug, dan kijkt een van onze developers hoe dit opgelost kan worden.',
        exportFailedPermissions:
            'Het exporteren van de gegevens naar Exact is niet gelukt omdat je daarvoor geen rechten hebt in Exact. Neem contact op met de beheerder van Exact om je rechten aan te passen.',
        exportInProgress: 'Bezig met exporteren van de gegevens naar Exact. Voortgang {0}%',
        exportSuccess: 'Het exporteren van de gegevens naar Exact is gelukt! \\ (•◡•) /',
        integrationWorking: 'De koppeling met Exact werkt naar behoren.',
        logInToExport: 'Log in bij Exact om de uitbetalingen te exporteren.',
        logout: 'Uitloggen bij Exact',
        onlyTrialExportAllowed: 'Alleen proefexports kunnen worden gedaan zonder in te loggen.',
        rerun: 'Herhaling',
        rerunning: 'Opnieuw uitvoeren...',
        someBatchesUncomplete: 'Sommige exportbestanden zijn nog niet compleet!',
        uncomplete: 'Niet compleet',
        warning: 'Waarschuwing',
    },
    exchangePatientData: {
        aboveServices: 'voor bovenstaande vakgebieden',
        accessMedicalHistory: 'Medische geschiedenis inzien',
        consentDeclarationTitle: 'Toestemmingsverklaring van de patiënt',
        consentDeclarationTitleWarning: 'Er is geen toestemmingsverklaring van de patiënt ingevuld',
        consentReadLess: 'Lees minder over deze toestemmingsverklaring',
        consentReadMore: 'Lees meer over deze toestemmingsverklaring',
        consentRequiredModalTitle: 'Toestemming patiënt inzage medisch dossier',
        hasProcessesClosedByOtherRequesterOrganization:
            'Om de volledige medische geschiedenis in te kunnen zien heb je toestemming van de patiënt nodig',
        noConsent: 'géén toestemming',
        selectAllServices: 'Selecteer alle vakgebieden',
        thePatientGives: 'De patiënt geeft',
    },
    export: {
        allServices: 'Alle diensten',
        cancelDownload: 'Annuleer download',
        compositePatientInvoice: 'Patiënt (samengesteld)',
        declarationsExport: 'Declaratie Export',
        declarationsProcessExport: 'Exporteer alle declaraties van processen',
        declarationsProcessExportText:
            'Alle declaraties die zijn aangemaakt met een referentie-startdatum voor of op de opgegeven datum worden geëxporteerd behalve de DBC’s waarbij de referentie-einddatum na vandaag ligt.',
        download: 'Download export',
        downloadProgress: 'Voortgang: {0} %',
        downloadReport: 'Download rapport',
        downloading: 'Bezig met exporteren, een ogenblik geduld.',
        episodeCategoryHasFixedServices: 'N.v.t. Voor {0} declareren we altijd een vaste set aan diensten',
        export: 'Exporteer',
        exportForDato: 'Exporteer patiëntdata voor DATO-registratie',
        exportFundusExaminations: 'Export fundus onderzoeken',
        exportFundusFiles: 'Export Fundus bestanden',
        exportFundusRequests: 'Export fundusscreening aanvragen',
        exportHistoryHide: 'Verberg exportgeschiedenis',
        exportHistoryShow: 'Bekijk exportgeschiedenis',
        exportingOneMomentPlease: 'Bezig met exporteren, een ogenblik geduld. Voortgang: {0} %',
        exports: 'Exports',
        fundusBestanden: 'Fundus bestanden',
        invoicesExport: 'Facturatie export',
        invoicesProcessExport: 'Exporteer alle facturen van processen',
        oldDeclarationMethodOnly: 'alleen oude declaratiemethode',
        paymentExport: 'Uitbetalingen export ',
        profession: 'Rol',
        professionChoice: 'Specifieke rol',
        role: 'Rol',
        roleAll: 'Alle rollen',
        roleChoice: 'Rollen',
        roleSpecific: 'Specifieke rol',
        runDate: 'Exportdatum',
        runNumber: 'Runnummer',
        selectOneOrMoreService: 'Selecteer een of meerdere diensten',
        serviceConsumerChoice: 'Diensten voor afnemers',
        serviceConsumerNo: 'Selecteer niet op diensten afnemers',
        serviceProviderChoice: 'Diensten voor leveranciers',
        serviceProviderNo: 'Selecteer niet op diensten leveranciers',
        serviceSupervisorChoice: 'Diensten voor supervisors',
        serviceSupervisorNo: 'Selecteer niet op diensten supervisors',
        servicesForDeclaration: 'Te declareren dienst(en)',
        singularPatientInvoice: 'Patiënt (enkelvoudig)',
        specificService: 'Specifieke dienst',
    },
    exporter: {
        error: {
            columnName: 'Foutmelding',
            entryMessage:
                "Fout: PID {0} kan niet worden geëxporteerd omdat het een ongeldige waarde in het veld '{1}' bevat. Neem contact op met team Hi Five voor hulp.",
            pageName: 'Foutmeldingen',
        },
    },
    featureFlags: {
        addFeatureFlag: 'Voeg feature flag toe',
        availableInFrontend: 'Beschikbaar in frontend',
        confirmDelete: 'Weet je zeker dat je deze feature flag wilt verwijderen?',
        confirmToggleOff: 'Weet je zeker dat je deze feature flag wilt deactiveren?',
        confirmToggleOn: 'Weet je zeker dat je deze feature flag wilt activeren?',
        featureFlagActive: 'Actief',
        featureFlagDescription: 'Omschrijving',
        featureFlagDescriptionHelper:
            'Omschrijf het doel van de feature flag in het engels, bijv: "Enable new service for all users"',
        featureFlagKey: 'Flag naam',
        featureFlagKeyHelper: 'bijv. ROLLOUT_NEW_SERVICE',
        featureFlagType: 'Type',
        featureFlagTypeValue: 'Waarde',
        header: 'Feature Flags',
        noFeatureFlags: 'Er zijn geen feature flags beschikbaar.',
    },
    finance: {
        activity: 'Verrichting',
        activityCode: 'Zorgactiviteit',
        activityDate: 'Uitvoerdatum',
        activityPaid: 'Betaald',
        activityUnpaid: 'Onbetaald',
        amount: 'Bedrag',
        bookYear: 'Boekingsjaar',
        btw: 'BTW',
        chooseActivities: 'Kies de activiteiten',
        chooseServices: 'Kies de diensten',
        confirmationMessageReprocess: 'Weet je zeker dat je {0} processen opnieuw wilt verwerken?',
        counterBooking: 'Tegenboeking',
        dateOfPayment: 'Geplande datum uitbetaling',
        dateTooltip:
            'Datum van uitbetaalrecord. Deze datum zal gelijk zijn aan de datum waarop de activiteit is uitgevoerd.',
        declaration: 'Declaratie',
        declarationDate: 'Declaratiedatum',
        declarationInvoices: 'Financieel (totaal)',
        declarations: 'Declaraties',
        declare: 'Declareren',
        declareToInsurer: 'Declareren',
        declared: 'Gedeclareerd',
        employmentTaxesDetailsExport: {
            createdBy: 'Aangemaakt door',
            declarationYear: 'Aangifte jaar',
            exportDate: 'Exportdatum',
            exportFailed: 'Export onsuccesvol',
            exportIsRequested: 'Export aangevraagd',
            requestNewExportModal: {
                alert: 'Let op! Vraag voordat je op Exporteer klikt aan je Teamlead Finance of de aangifte al heeft plaatsgevonden.',
                body: 'Om ervoor te zorgen dat we het systeem niet te veel belasten, wordt deze export in de nacht gestart.\nMorgenochtend staat de nieuwe export in de tabel en kan je de export downloaden.',
                header: 'Aanvraag nieuwe export UBD',
            },
            ubd: 'UBD',
            ubdExportAlert:
                'Zorg dat je de mailberichten van de rechtsvormwijzigingen verwerkt voordat je de aangifte opstuurt aan de Belastingdienst',
        },
        emptyState: 'Uitbetalingen worden op deze pagina weergegeven nadat de eerste uitbetaling heeft plaatsgevonden.',
        expectedPayoutDate: 'Verwachte uitbetalingsdatum',
        exportPaymentsActivities: 'Exporteer alle uitbetalingen van activiteiten',
        exportStatus: 'Exportstatus',
        exports: 'Exports',
        finance: 'Financiën',
        financialReport: 'Rapportage Financieel',
        financing: 'Bekostiging',
        financingDetails: 'Bekostigingsgegevens',
        financingType: 'Soort bekostiging',
        financingTypeHeadings: {
            compositePatientInvoices: 'Samengestelde patiëntfacturen',
            declarations: 'Declaraties',
            invoices: 'Facturen',
            singularPatientInvoices: 'Enkelvoudige patiëntfacturen',
        },
        flex: 'Flex',
        freeOfCharge: 'Onbekostigd',
        fundingAccordingToSettings: 'Bekostigen volgens instellingen',
        fundingReport: 'Rapportage Bekostiging',
        fundingServicePart: {
            activities: 'Bekostigde activiteiten',
            all: 'Hele dienst',
            allInfo:
                'Een proces van deze dienst wordt in zijn geheel bekostigd. Als er een declaratie of factuur wordt aangemaakt door deze regel kunnen daarna geen andere bekostigingsvormen meer toegepast worden op hetzelfde proces.',
            invoiceServiceCode: 'Overschrijf dienstcode bij facturen',
            invoiceServiceName: 'Overschrijf dienstnaam bij facturen',
            invoiceServiceType: 'Overschrijf diensttype bij facturen',
            longName: 'Bekostigd deel van de dienst',
            removeError:
                'Deelbekostiging kon niet worden verwijderd omdat deze in gebruik is bij een factuur- of declaratieregel',
            shortName: 'Deelbekostiging',
            specific: 'Bekostigt alleen specifieke activiteiten',
            specificInfo:
                "Als er volgens deze regel een declaratie wordt aangemaakt, worden alleen specifieke activiteiten als 'bekostigd' gemarkeerd. De andere activiteiten van het proces kunnen vervolgens nog via andere verzekeraars-afspraken-regels bekostigd worden, wat kan leiden tot andere declaraties.",
        },
        hideActivities: 'Verberg activiteiten',
        inNameOf: 'Ten name van',
        institution: 'Instelling',
        insurerDeclarations: 'Declaraties zorgverzekeraars',
        invoice: 'Facturatie',
        invoiceDate: 'Facturatiedatum',
        invoiced: 'Gefactureerd org/grp',
        invoicedPatient: 'Gefactureerd patient',
        invoices: 'Facturaties',
        invoicesGroups: 'Facturaties zorggroepen',
        invoicing: 'Factureren',
        lessFilters: 'Minder filters',
        moreFilters: 'Meer filters',
        noFinancialStatus: 'Er zijn geen financiële gegevens bekend',
        noFinancing: 'Geen bekostiging',
        noValidEmploymentsForPayments: 'Je hebt geen geldige betrekking om betalingen te genereren.',
        numberOfPayouts: 'Aantal',
        old: 'Oud',
        organizationType: 'Type organisatie',
        overviewPayouts: 'Overzicht uitbetalingen',
        paid: 'Uitbetaald',
        paidDate: 'Datum uitbetaald',
        paymentReference: 'Betalingskenmerk/referentie',
        payments: 'Uitbetalingen',
        payout: 'Uitbetaling',
        payoutsExportTooltip:
            'Wanneer dit leeg wordt gelaten, wordt de maand waarin de activiteit heeft plaatsgevonden genomen',
        payoutsReport: 'Rapportage Uitbetaling',
        performFinancingSearch: 'Zoek een bekostiging met behulp van bovenstaande zoek filters',
        period: 'Periode',
        previousPayments: 'Eerdere uitbetalingen',
        priceAgreement: 'Prijsafspraak',
        processing: 'In bewerking',
        providerOrganization: 'Leveranciersorganisatie',
        reasonForAccessModel: {
            label: 'Waarom heb je de privacygevoelige gegevens nodig?',
            placeholder: 'Geef hier een geldige reden...',
            subTitle:
                'Een deel van de geselecteerde gegevens is privacygevoelig. Deze gegevens mag je alleen met een geldige reden downloaden.',
            title: 'Privacygevoelige gegevens',
        },
        reasonNoPayment: 'Reden geen uitbetaling',
        reasonNoPaymentShort: 'Reden geen uitbet.',
        report: 'Rapport',
        reports: 'Rapportages',
        reprocessByShort: 'Opn. verw. door',
        reprocessFinancing: 'Opnieuw verwerken bekostiging',
        reprocessPayout: 'Opnieuw verwerken uitbetaling',
        reprocessed: 'Opgewerkt',
        reprocessedOn: 'Verwerkt op',
        reprocessingFinancing:
            'De processen zullen opnieuw verwerkt worden. Het kan enige tijd duren voor dit gereed is.',
        reprocessingPayouts:
            'De zorg activiteiten zullen opnieuw verwerkt worden. Het kan enige tijd duren voor dit gereed is.',
        requestExportAgain: 'Export opnieuw aanvragen',
        requesterGroup: 'Aanvragende groep',
        runCreatedAt: 'Rundatum',
        runNumber: 'Run',
        runNumberShort: 'Runnr.',
        searchForTooltip: `Voor meerdere processen, plak hier een met komma's gescheiden PID-lijst`,
        sensitiveDataInfoText:
            'Privacygevoelige gegevens staat standaard niet geselecteerd. Heb je die toch nodig, dan word je gevraagd hier een reden voor op te geven.',
        showActivities: 'Toon activiteiten',
        specification: 'Specificaties',
        specsAvailableOn: 'Omstreeks {0}, zijn de specificaties van deze uitbetaling hier beschikbaar.',
        subtotal: 'Subtotaal',
        suppressEmployeePayoutOverview: 'Inzien uitbetalingen blokkeren voor medewerkers',
        suppressEmployeePayoutOverviewTooltip:
            'Gebruik deze instelling als in CS wordt uitbetaald aan de medewerker maar de uiteindelijke uitbetaling naar de organisatie gaat. In Exact is dan het bankrekeningnummer van de organisatie gekoppeld aan dat van de medewerker.',
        toBeDeclared: 'Te declareren',
        toBeInvoiced: 'Te factureren org/grp',
        toBeInvoicedPatient: 'Te factureren patient',
        toBePaid: 'Uit te betalen',
        toIban: 'Naar IBAN',
        totalPayable: 'Totaal uit te betalen',
        totalPayableExclBTW: 'Totaal (excl BTW)',
        upcomingPayments: 'Aankomende uitbetaling',
    },
    fundingErrorCode: {
        all_rules_failed_custom_control_query: 'Geen passende declaratieregel gevonden door custom control queries',
        canNotGetDateFirstProvidingActivity: 'Ontbrekende datum voor de eerste zorgactiviteit',
        cannotDetermineProcedurePerformedActivity: 'Kan uitgevoerde activiteit van onderzoek niet bepalen',
        cannotDetermineReportPerformedActivity: 'Kan uitgevoerde activiteit van rapport niet bepalen',
        cannotDetermineRequestPerformedActivity: 'Kan uitgevoerde activiteit van aanvraag niet bepalen',
        generalError: 'Onbekende fout',
        invalidAgb: 'Aanvrager of supervisor heeft geen geldige AGB-code',
        invalidInsurerSettings: 'Verzekeraarsinstellingen zijn ongeldig',
        invalidInvoiceSettings: 'Facturatie-instellingen zijn ongeldig',
        missingCompensationCharge: 'Ontbrekend tarief',
        missingPerformerAgbCode: 'Ontbrekende poortfunctionaris subtraject',
        missingPerformerRelationNumber: 'Relatienummer van uitvoerder ontbreekt',
        missingPoortfunctionaris: 'Zorgtraject gestart zonder poortfunctionaris',
        missingReferrerAgbCode: 'Ontbrekende AGB-code bij verwijzer',
        missingRequesterRelationNumber: 'Relatienummer van aanvrager ontbreekt',
        multipleDeclarationsSameCareProductType: 'Meerdere declaraties met zelfde zorgproducttype voor een process',
        noDeclarationRules: 'Geen declaratieregels ingesteld',
        noFundingResult: 'Geen bekostigingsresultaat',
        noInsuranceHistory: 'Geen verzekeringsgeschiedenis beschikbaar',
        noInsurerServiceDeclarationActivities: 'Geen verzekeraar declaratie-activiteiten beschikbaar',
        noInsurerSettings: 'Geen verzekeraarsinstellingen',
        noPatientInvoiceSettings: 'Patiëntfacturatie-instellingen ontbreken',
        noRelationNumberAvailable: 'Geen relatienummer beschikbaar',
        ozpDbcOverlapTooLarge: 'DBC/OZP overlap te groot',
        serviceTypeNotSupported: 'Diensttype wordt niet ondersteund bij aanmaken uitgevoerde activiteit',
        uzoviNotLinkedToInsurer: 'UZOVI niet gekoppeld aan verzekeraar',
        uzoviNotValid: 'De uzovi is ongeldig',
        waitingForPreviousFundingRecord: 'Wacht op financiele verwerking van voorgaand proces',
        waitingForReProcessing: 'Wacht op opnieuw verwerken',
        zeroOrNegativeInvoiceAmount: 'Factuurbedrag kleiner of gelijk nul',
    },
    fundingErrorInformationEnum: {
        conscientiousObjection: 'Gemoedsbezwaard',
        foreignInsurance: 'Buitenland verzekerde patiënt',
        invoicingAtGpPractice: 'Factuur voor organisatie',
        other: 'Anders...',
        patientDied: 'Overleden patiënt',
        rmo: 'RMO',
        uninsuredPatient: 'Onverzekerde patiënt',
        wlz: 'WLZ',
    },
    fundingLog: {
        data: 'Data',
        fundingLog: 'Bekostigingslogs',
        fundingStep: 'Bekostigingsstap',
        patientId: 'Patiënt ID',
        performedDatetime: 'Uitvoerdatum',
    },
    fundingLogMutation: {
        doubledProcess: 'Dubbel proces',
        explainTheReason: 'Leg de reden uit',
        incorrectSettings: 'Verkeerde instellingen',
        modalTitle: 'Reden van mutatie voor logboek',
        other: 'Andere',
        reasonNoFunding: 'Reden geen bekostiging ingesteld',
        selectAReason: 'Selecteer een reden...',
        test: 'Test',
        unfunded: 'Onbekostigd',
        unpaid: 'Onbetaald',
        writeHere: 'Beschrijf hier de reden...',
    },
    fundingSettings: {
        add: 'Nieuwe instellingen',
        addFundingSetting: 'Bekostiging toevoegen',
        alreadyFunded: 'Al bekostigd',
        alreadyFundedExplanation:
            'Al bekostigd: Deze dienst wordt gratis geleverd als voorafgegaan door andere dienst.',
        alternativeFunding: 'Alt. bekostiging',
        alternativeFundingExplanation:
            'Alternatieve bekostiging: De dienst wordt op een andere manier bekostigd. Vul geen bedrag in bij compensatie.',
        fundingSetting: 'Bekostigingsinstelling',
        fundingSettingExplanation:
            'Bekostigingsinstelling: Hier regel je of een dienst gefactureerd wordt aan de organisatie',
        invoice: 'Factuur',
        invoiceExplanation:
            'Factuur: De betreffende organisatie ontvangt een factuur. Vul een bedrag in bij compensatie.',
    },
    fundingType: {
        declareToInsurer: 'Declareren bij zorgverzekeraar',
        freeOfCharge: 'Niet bekostigen',
        fundingAccordingToSettings: 'Bekostigen volgens instellingen',
    },
    fundusNoShows: {
        checkSearchTerm: 'Controleer je zoekterm en/of verander het filter',
        filterOnCareGroup: 'Filter op zorggroep',
        filterOnGeneralPractitoner: 'Filter op huisarts',
        importFileToSeeNoShows: 'Importeer een oproepbestand om eventuele no shows te zien',
        noFundusShowsSubtible: 'Zodra er weer no shows zijn, krijg je hier bericht van',
        resultsNotFound: "We kunnen geen resultaten vinden voor '{0}'",
        thereAreCurrentlyNoFundusNoShows: 'Op dit moment zijn er geen no shows',
    },
    fundusScreening: {
        brightPlanActionDescription:
            "Tijdens het onderzoek maken we foto's van jouw ogen (netvlies). Zo kunnen beschadigingen aan jouw ogen die er kunnen zijn worden opgespoord. De afspraak duurt ongeveer 10 minuten.",
        manualImport: {
            cell: 'cel',
            cells: 'cellen',
            form: {
                callingFile: 'Oproepbestand',
                excelFileButtonLabel: 'Kies Excel bestand',
                excelFileFieldLabel: 'Excel bestand',
                resetButtonLabel: 'Leeg alle velden',
                submitButtonLabel: 'Importeer',
                thirdPartyFieldLabel: 'Derde partij',
            },
            inviteType: {
                [FundusScreeningManualImportLogInvitationType.initialInvitation]: 'Oproep',
                [FundusScreeningManualImportLogInvitationType.repeatedInvitation]: 'Herhaaloproep',
            },
            logbook: {
                emptyState: 'Er zijn nog geen oproepbestanden geïmporteerd',
                modalTitle: 'Logboek verwerkte oproepbestanden',
                openLogbookButtonText: 'Bekijk logboek',
                tableColumns: {
                    careGroupName: 'Zorggroep',
                    download: 'Download',
                    importDate: 'Importdatum',
                    importedBy: 'Geïmporteerd door',
                    importedFilename: 'Geïmporteerd bestand',
                    invitationType: 'Type oproep',
                    thirdPartyName: 'Derde partij',
                },
            },
            pageSubtitle: 'Importeer vanuit Excel',
            pageTitle: 'Fundusscreening importeren',
            successfulImport: 'Je Excel bestand is geïmporteerd',
            thirdParty: {
                [FundusScreeningThirdParty.vipLiveFundusManualImport]: 'VipLive',
                [FundusScreeningThirdParty.healthConnectedFundusManualImport]: 'Health Connected',
            },
        },
    },
    genderEnum: {
        female: 'Vrouw',
        male: 'Man',
        other: 'Anders',
        unknown: 'Onbekend',
    },
    genderEnumInitial: {
        female: 'V',
        male: 'M',
        other: 'A',
        unknown: 'O',
    },
    genderSpirometerEnum: {
        female: 'Vrouw',
        male: 'Man',
    },
    gradingSummaryEnum: {
        deviant: 'Afwijkend',
        normal: 'Normaal',
        unjudgable: 'Niet te beoordelen',
    },
    groupMeetingType: {
        bariGroupSession: 'Groepsvoorlichting Obesitas',
        bariMdo: 'MDO',
    },
    groupSettings: {
        alreadyFinanced: 'Al bekostigd',
        alreadyFinancedTooltip: 'Deze dienst wordt gratis geleverd als voorafgegaan door andere dienst',
        groupSettings: 'Groepinstellingen',
        isAlternativeFinancing: 'Alternatieve bekostiging',
        noShowsImported: 'Import No shows',
        nonAvailable: 'Geen groepen beschikaar',
        serviceGroupSettings: 'Groepsinstellingen',
        supervisionPercentage: 'Supervisie percentage',
    },
    groups: {
        bankAccountNumber: 'Rekeningnummer',
        details: 'Groep details',
        exportGroups: 'Groepen exporteren ({0})',
        financialEmailAddress: 'Financieel e-mailadres',
        financialEmailAddressTooltip: 'E-mailadres voor facturatie- en uitbetalingspecificaties',
        group: 'Groep',
        groupExport: 'Zorggroep export',
        groupName: 'Groepsnaam',
        groupOverview: 'Groepsoverzicht',
        groups: 'Groepen',
        isActive: 'Actief',
        nameGroup: 'Naam groep',
        nameOrTxcsId: 'Groepsnaam of ID',
        noActiveCarePaths: 'Deze groep heeft nog geen zorgpaden',
        noGroupsAvailable: 'Geen groepen beschikbaar',
        subscriber: 'Tenaamstelling bankrekening',
    },
    help: {
        faq: 'FAQ',
        manual: 'Handleiding',
        support: 'Ondersteuning',
    },
    hipRomTable: {
        endorotation: 'Endorotatie',
        exoratation: 'Exorotatie',
        extension: 'Extensie',
        flexion: 'Flexie',
        leftHip: 'Linker heup',
        rightHip: 'Rechter heup',
    },
    holter: {
        addDiary: 'Dagboek toevoegen',
        allActiveRecordings: 'Actief',
        allEcgRecordings: 'Alle ECG opnames',
        batteryPercentage: 'Resterende lading accu',
        cancelExamination: 'Annuleer onderzoek',
        chooseDuration: 'Klik en kies de opnameduur...',
        clearEcgRecorder: 'Leegmaken ECG recorder',
        clearRecorder: 'Maak recorder leeg',
        configured: 'Ingesteld',
        confirmationMessageClearHolter:
            'Weet je zeker dat je deze recorder wilt leegmaken? We zullen dan ook meteen het onderzoek annuleren.',
        detectRecorder: 'Detecteer recorder',
        diary: 'Dagboek',
        ecgRecordersOverview: 'ECG recorder overzicht',
        fax: 'Fax',
        handedIn: 'Uitgelezen',
        history: 'Geschiedenis',
        patientName: 'Naam patiënt',
        percentageCompleted: '% Voltooid',
        printDiary: 'Print dagboek',
        recorder: 'Recorder',
        recorderId: 'Serienummer',
        recorders: 'Recorders',
        sendDiary: 'Verstuur',
        sent: 'Verstuurd',
        startDownloading: 'Start met uitlezen',
        sureToClear: 'Weet je het zeker. De ECG opname wordt verwijderd en het onderzoek wordt afgebroken.',
        uploadDiaryLater: 'Dagboekje later uploaden',
        uploaded: 'Geüpload',
        withAnalyst: 'Bij analist',
    },
    holterCardioExaminationDurationEnum: {
        oneDay: '1 dag',
        threeDays: '3 dagen',
        twoDays: '2 dagen',
    },
    home: {
        action_inquiry: 'Aanvragen',
        action_performSelf: 'Zelf uitvoeren',
        chooseStartResearch_action: 'Wat wil je doen?',
        chooseStartResearch_consultation: 'Welk consult wil je starten?',
        chooseStartResearch_contact: 'Welke registratie wil je doen?',
        chooseStartResearch_counseling: 'Welke begeleiding wil je starten?',
        chooseStartResearch_examination: 'Welk onderzoek wil je starten?',
        chooseStartResearch_inquiry: 'Welk onderzoek wil je aanvragen?',
        chooseStartResearch_referral: 'Welke verwijzing wil je starten?',
        noServices: 'Er zijn geen keuzes gevonden voor deze dienst.',
        patientBarLabel: 'Voor welke patiënt?',
        startOne: 'Begin een',
        startService: 'Start',
        type_consultation: 'Consult',
        type_contact: 'Contact',
        type_counseling: 'Begeleiding',
        type_examination: 'Onderzoek',
        type_inquiry: 'Aanvraag',
        type_labAanvraag: 'Labaanvraag',
        type_other: 'Anders',
        type_referral: 'Verwijzing',
        type_referralSmall: 'Verwijzing',
    },
    homeConsultation: {
        addControlText: 'Wanneer het wordt verstuurd, zal een aanvraag naar de patiënt gaan.',
        editControlText: 'De aanvraag is verstuurd naar de patiënt.',
        homeConsultation: 'Thuis consult',
        photo1: 'Maak een overzichtsfoto',
        photo2: 'Maak een foto van dichtbij',
        photo3: 'Maak eventueel een derde foto die jou nuttig lijkt',
        photo4: 'Maak eventueel een vierde foto die jou nuttig lijkt',
        showControlText: 'De resultaten',
    },
    identifierDeclarationOrRegistration: {
        addIdentifierDeclarationOrSupervisorToContinue: 'Vul een AGB-code in, of kies een supervisor.',
        addIdentifierDeclarationToContinue: 'Vul een AGB-code in.',
        addIdentifierRegistrationBigToContinue: 'Vul een BIG-nummer in.',
        addNewIdentifierDeclaration: 'Nieuwe AGB-code invullen',
        chooseAProfession: 'Kies een beroep.',
        enterValue: 'Vul deze in.',
        enterValueToContinue: 'Vul deze in om verder te gaan.',
        howToAddIdentifierDeclaration: 'Hoe wil je de AGB-code aanpassen?',
        identifierDeclaration: 'AGB-code',
        identifierDeclarationAndIdentifierRegistrationBig: 'AGB-code of BIG-nummer',
        identifierDeclarationAndIdentifierRegistrationBigRequiredBecauseOfProfession:
            'Je hebt een beroep gekozen waarvoor een AGB-code en BIG-nummer vereist zijn.',
        identifierDeclarationOrReferenceRequired:
            'Je hebt een beroep gekozen waarvoor een AGB-code van jou of van een collega vereist is',
        identifierDeclarationPractitionerId: 'AGB-code van collega',
        identifierDeclarationRequiredBecauseOfProfession:
            'Je hebt een beroep gekozen waarvoor een AGB-code vereist is.',
        identifierHeader: 'BIG / KP & AGB',
        identifierRegistrationBig: 'BIG-nummer',
        identifierRegistrationKp: 'KP-nummer',
        identifierSavedIntoPractitionerProfile:
            'Wanneer je een {0} invult dan wordt deze opgeslagen in het profiel van de zorgverlener.',
        identifierSavedIntoPractitionerProfilePlural:
            'Wanneer je {0} invult dan worden deze opgeslagen in het profiel van de zorgverlener.',
        newIdentifierDeclaration: 'Nieuwe AGB-code',
        searchForNameOrAGB: 'Zoeken op naam of AGB-code',
        searchOnNameOrIdentifierDeclaration: 'Zoeken op naam of AGB-code',
        supervisorRequiredBecauseOfProfession: 'Je hebt een beroep gekozen waarvoor een supervisor vereist is.',
        usesIdentifierDeclarationPractitionerId: 'Werkt onder AGB-code van collega',
    },
    importActionsEnum: {
        renew: 'Vervangen',
        update: 'Bijwerken',
    },
    imports: {
        addFailed: "Toevoegen van het bestand '{0}' is fout gegaan, probeer opnieuw",
        authenticationDevices: 'Hardware tokens',
        bankAccountConflicts:
            'De nieuwe rekeninggegevens zijn niet geimporteerd. Er zijn rekeninggevens in dit bestand die niet overeen komen met de rekeninggegevens bekend in ClientSafe. Verbeter de gegevens in Exact en importeer het bestand opnieuw',
        bankAccountDetails: 'Bankrekeningen',
        bankAccountDetailsTooltipText:
            'Exporteer bankrekening informatie uit Exact. Verwijder eerste regels zodat in de eerste rij minimaal de kolommen: Code, Bankrekening en Tenaamstelling rekening komen te staan. Exporteer als CSV bestand',
        corruptedDataMessage: 'Het bestand heeft een verkeerd format en kan niet uitgelezen worden',
        dhd: 'DHD',
        episodeCategoryNotValid: 'Selecteer een geldig zorgpad.',
        fundusScreening: 'Fundusscreening',
        import: 'Import',
        importDevicesSuccessMessage: 'nieuwe apparaten zijn succesvol geregistreerd',
        importSuccess: 'Succesvol {0} regels geïmporteerd',
        importedNoShowsPeriods: 'Geüploade no show periodes',
        infoAlertNoShow: 'Op dit moment zijn er geen no-shows voor jouw praktijk',
        insurerUzovis: "Verzekeraars uzovi's",
        insurers: 'Verzekeraars',
        invalidDataMessage: 'Het bestand bevat invalide data',
        migrationOrganizations: 'Migratie organisaties',
        migrationOrganizationsImportOrganizationNotFound:
            'Inlezen mislukt, {0} is niet een bestaande organisatie in CS. Er hebben geen updates plaatsgevonden.',
        migrationOrganizationsImportSucces: 'Succesvol geïmporteerd',
        noBankAccountsUpdated: 'Alle rekeninggegevens zijn actueel, 0 wijzigingen geïmporteerd',
        noShowPatients: 'No show patiënten',
        noShows: 'No shows',
        notRequestedBankAccountConflicts: 'Bankgegevens niet aangevraagd en niet overeenkomend',
        oneBankAccountImported: '1 wijziging succesvol geïmporteerd',
        pkscFile: 'PKSC bestand',
        relationNumbers: 'Koppelen relaties exact',
        requestedBankAccountConflicts: 'Bankrekeninggevens aangevraagd maar niet overeenkomend',
        severalBankAccountsImported: '{0} wijzigingen succesvol geïmporteerd',
        txscIdFile: "Bestand met TxCS ID's",
        uzovi: 'UZOVI',
    },
    incomingMessages: {
        incomingMessages: 'Binnenkomende berichten',
        messageSender: 'Bericht afzender',
        messageType: 'Bericht type',
        patient: 'Patiënt',
        pid: 'Proces id',
        practitioner: 'Zorgverlener',
        receivedAt: 'Binnengekomen op',
        status: 'Status',
        statusMessage: 'Status bericht',
    },
    insuranceHistory: {
        endDate: 'Einddatum',
        insuranceHistory: 'Verzekeringsgeschiedenis',
        insurancePolicyNumber: 'Verzekeringspolisnummer',
        message_IdentifierSocialSecurityNotFound: 'Er zijn geen verzekeringsgegevens bekend van het opgegeven BSN',
        message_IncorrectBirthDate: 'De geboortedatum hoort niet bij het opgegegeven BSN',
        message_NoActiveInsurance: 'Er is geen actieve verzekering gevonden',
        message_NoUniquePersonIdentified:
            'Er zijn meedere personen gevonden die voldoen aan de opgegeven criteria. Vul a.u.b. het BSN in en probeer opnieuw',
        message_NotAllowed: 'Er zijn verzekeringsgegevens gevonden maar je bent niet geautoriseerd om deze in te zien',
        message_ServiceDown: 'Er is een fout opgetreden bij het ophalen van de verzekeringsgegevens',
        message_UnknownInsurerCode: 'Er is een actieve verzekering gevonden maar de verzekeraar is onbekend',
        message_VecozoError: 'Er is een fout opgetreden bij het ophalen van de verzekeringsgegevens',
        message_success: 'De verzekeringsgegevens zijn opgehaald',
        retrieve: 'Haal verzekeringsgegevens op',
        startDate: 'Begindatum',
    },
    insurers: {
        access: 'Toegang',
        add: 'Voeg verzekeraar toe',
        addCeilingYear: 'Toevoegen plafond jaar',
        cannotRemoveInsurer: 'De verzekeraar kan niet worden verwijderd omdat er een of meer gekoppelde uzovis zijn',
        careLimitation: 'Zorgbeperking',
        careProductType: 'Zorgproducttype',
        ceilingAmount: 'Plafondbedrag',
        contractedCareCannotBeStarted: 'Gecontracteerde zorg kan niet worden gestart',
        currentPrice: 'Actuele prijs',
        declarationCode: 'Declaratiecode',
        declarationMethod: 'Declaratiemethode',
        ensureTeamLeadsAreAware: 'LET OP! Controleer of alle teamleads op de hoogte zijn van deze situatie',
        exportUntilDate: 'Exporteer tot en met',
        exportUntilDateTooltip: 'Declaraties van processen aangemaakt na deze datum, worden niet geëxporteerd',
        hasLinkedDeclarationRules:
            'De verzekeraar kan niet worden verwijderd omdat er een of meer declaratieregels aan verbonden zijn',
        insurer: 'Verzekeraar',
        insurerUzoviOverlapMessage:
            'Deze uzovi is al verbonden met verzekeraar {0} voor de periode. Verwijder of beëindig deze verbintenis eerst.',
        insurerUzovis: "Verzekeraars uzovi's",
        insurers: 'Verzekeraars',
        latestInsurer: 'Laatste verzekeraar',
        linkedUzovis: 'Gekoppelde uzovis',
        ovelappedTurnoverCeiling: 'Er is al een omzetplafond ingevoerd voor {0} {1} {2}',
        paymentPeriod: 'Betalingsverwachting',
        paymentPeriodPlaceholder: 'Betalingsverwachting in dagen',
        paymentPeriodTooltip:
            'Het verwachte aantal dagen waarna de zorgverzekeraar betaalt, nadat bij Vektis de declaratie is ingediend. Dit aantal wordt gebruikt voor de liquiditeitsprognose.',
        periodOfCareLimitation: 'Periode van zorgbeperking',
        phoneForCareMediation: 'Telefoonnummer voor zorgbemiddeling',
        phoneNumberCareMediation: 'Telefoonnummer zorgbemiddeling',
        supplementPriceActivity: 'Toeslagprijs',
        turnoverCeiling: 'Omzetplafond',
        turnoverCeilingTooltip:
            "Het omzetplafond, is het maximaal toegestane bedrag dat bij de zorgverzekeraar gedeclareerd mag worden voor een jaar. Het bedrag wordt gebruikt in de 'Omzetplafond rapportage'.",
        year: 'Jaar',
    },
    intakeReport: {
        notYetCompleted:
            'Je hebt nog geen intake-rapport ingevuld. Typ of kopieer het intake-rapport in het tekstveld of zet een bestand met het intake-rapport in het vak voor bestanden.',
    },
    interapy: {
        calculatedValue: 'Automatisch berekende waarde',
        clientComplaintsDescription: 'Beschrijving klachten door cliënt',
        comorbidity: 'Comorbiditeit',
        courseOfComplaints: 'Beloop klachten',
        depression: 'Depressie',
        distress: 'Distress',
        dsmDisorder: 'DSM stoornis',
        dsmPrevalentComplaint: 'DSM prevalente klacht',
        dsmSuspicion: 'DSM vermoeden',
        durationOfComplaints: 'Duur klachten',
        fear: 'Angst',
        indication: 'Indicatie',
        patientEmail: 'E-mailadres patiënt',
        purposeOfTreatment: 'Doel behandeling',
        riskOfDrugAbuse: 'Risico op middelengebruik',
        riskOfPsychosis: 'Risico op psychose',
        riskOfSuicide: 'Risico op suïcide',
        sendPostMeasurement: 'Nameting sturen',
        seriousness: 'Ernst',
        somatisation: 'Somatisatie',
    },
    jobStatusEnum: {
        failed: 'Gefaald',
        running: 'Draait',
        scheduled: 'Gepland',
        success: 'Succesvol',
    },
    jobs: {
        duration: 'Duur (seconden)',
        id: 'Id',
        jobs: 'Jobs',
        name: 'Job naam',
        output: 'Resultaat',
        params: 'Parameters',
        ranAfterDate: 'Vanaf datum',
        ranBeforeDate: 'Tot datum',
        runNow: 'Direct uitvoeren',
        scheduledOn: 'Geplande datum',
        startedOn: 'Gestart',
        status: 'Status',
    },
    journals: {
        comment: 'Commentaar',
        completeJournalsOverview: 'Volledig journalen overzicht',
        entryCode: 'Code',
        entryCodeSystem: 'Code Systeem',
        entryDisplayName: 'Naam',
        episodes: 'Journaalregels episodes',
        journals: 'Actieve episodes',
    },
    kisSetting: {
        kisSetting: 'KIS Instellingen',
        kisType: 'Type KIS',
        serverAddress: 'Server adres',
        serverPort: 'Server poort',
        username: 'Gebruikersnaam',
        usesKis: 'Maakt gebruik van een KIS',
    },
    labResults: {
        codeDisplayName: 'Code',
        codeSystem: 'Code Systeem',
        comment: 'Commentaar',
        description: 'Omschrijving',
        labResults: 'Diagnostische bepalingen',
        noLabResultsFound: 'Er zijn geen diagnostische bepalingen aangetroffen.',
        overview: 'Overzicht diagnostische bepalingen',
        selectAll: 'Alles selecteren',
        valueUnit: 'Eenheid',
        valueValue: 'Uitslag',
    },
    login: {
        accountRegisteredHelp: 'Je kunt nu inloggen.',
        dontRememberUserNameOrEmailAddress: 'Weet je je gebruikersnaam of e-mailadres ook niet meer?',
        error: {
            BAD_QUALITY_PASSWORD: 'Je gekozen wachtwoord is te makkelijk om te raden. Kies een ander wachtwoord.',
            NO_PASSWORD: 'Kies een wachtwoord om verder te kunnen',
            PASSWORD_CONTAINS_FREQUENTLY_USED_PASSWORD:
                'Maak je wachtwoord langer en/of probeer veelvoorkomende (wacht)woorden te vermijden',
            PASSWORD_CONTAINS_PERSONAL_INFORMATION:
                'Maak je wachtwoord langer en/of probeer persoonlijke data (namen, datum etc.) in je wachtwoord te vermijden',
            PASSWORD_CONTAINS_REPEATED_SUBSEQUENCE:
                'Maak je wachtwoord langer en/of probeer herhalingen en/of opeenvolgende reeksen (abab / 123) te vermijden ',
            PERSONAL_INFORMATION_IN_PASSWORD:
                'Je gekozen wachtwoord bevat persoonlijke gegevens waardoor het makkelijk te raden kan zijn. Kies een ander wachtwoord.',
            SHORT_PASSWORD: `Minimaal ${MINIMUM_PASSWORD_LENGTH} karakters, probeer eens willekeurige woord(en) toe te voegen aan je wachtwoord`,
            VERY_SHORT_PASSWORD: `Minimaal ${MINIMUM_PASSWORD_LENGTH} karakters, probeer willekeurige woord(en) toe te voegen aan je wachtwoord`,
        },
        installTotpSoftware: 'Heb je de Google Authenticator app nog niet geïnstalleerd? Download hier:',
        linkAuthenticator: 'Koppel Google Authenticator app',
        loginAgain: 'Opnieuw inloggen',
        pickAPassword: 'Kies een wachtwoord',
        register: 'Activeren',
        registerYourAccount: 'Account activeren',
        registerYourAccountHelp:
            'Activeer je account met je gebruikersnaam en unieke activatiecode van twaalf letters.',
        registrationCode: 'Registratiecode',
        scanTotpQrcodeHelp:
            'Open de Google Authenticator app en scan de onderstaande code. Voer daarna de zes-cijferige code die verschijnt in om je account te activeren.',
        signIn: 'Inloggen',
        signInHelpCall: 'Bel',
        signInHelpOrMail: 'of mail',
        signInHelpQuestion: 'Hulp nodig?',
        signInHelpSchedule: '(8:30 - 17:00) ',
        totpToken: 'Google Authenticator code',
        totpTokenInactivity: 'Lukt het niet om je code in te vullen?',
        welcome: 'Welkom {0}, voer de Google Authenticator code in',
    },
    logs: {
        thirdPartyMessages: 'Berichten aan derden',
    },
    maritalStatusEnum: {
        A: 'Nietig verklaard',
        D: 'Gescheiden na huwelijk',
        I: 'Tijdelijke overeenkomst',
        L: 'Wettelijk gescheiden',
        M: 'Getrouwd',
        P: 'Polygaam',
        S: 'Nooit getrouwd',
        T: 'Geregistreerd partnerschap',
        U: 'Niet getrouwd',
        UNK: 'Onbekend',
        W: 'Weduwe/weduwenaar',
    },
    medications: {
        addMedication: 'Medicatieregel toevoegen',
        addMedicationsFromHis: 'Medicatie meesturen uit HIS',
        addMedicationsManually: 'Handmatig medicatie toevoegen',
        addRequestMedication: 'Voeg medicatie toe',
        completeMedicationRecord: 'Volledig medicatiedossier',
        currentMedication: 'Actuele medicatie',
        description: 'Omschrijving',
        dosageInstruction: 'Voorschrift',
        dosageUnit: 'Eenheid',
        dosageValue: 'Aantal',
        dose: 'Dosis',
        endDate: 'Einddatum',
        frequency: 'Frequentie',
        medInstrCode: 'Instructie Code',
        medInstrName: 'Instructie Naam',
        medInstrSystem: 'Instructie Systeem',
        medication: 'Medicatie',
        medicationNamePlaceholder: 'Vul de medicatienaam in...',
        medicationPresciptionPlaceholder: 'Vul het voorschrift in...',
        medications: 'Medicaties',
        mkcodeCode: 'Code',
        mkcodeSystem: 'Code Systeem',
        mkcodeSystemName: 'Code Naam',
        modalBody:
            'Je hebt aangegeven dat de patiënt medicatie gebruikt, maar je hebt geen medicatie ingevuld. Weet je zeker dat je het consult wilt verzenden zonder medicatie?',
        modalTitle: 'Verzenden zonder medicatie',
        name: 'Naam',
        numberOfMedicationsManuallyAdded: '({0} toegevoegd)',
        numberOfMedicationsSelected: '({0} geselecteerd)',
        overview: 'Overzicht medicatie',
        patientUsesMedication: 'Patiënt gebruikt(e) medicatie',
        patientUsesMedicationTooltip:
            'Voeg relevante medicatie toe. Denk aan bijvoorbeeld medicatie voor de behandeling van hart- en vaatziekten en diabetes, en psychomedicatie.',
        quantity: 'Hoeveelheid',
        quantityCode: 'Hoeveelheid Code',
        quantityCodeSystem: 'Hoeveelheid Systeem',
        quantityDisplayName: 'Hoeveelheid Naam',
        quantityValue: 'Hoeveelheid Waarde',
        reason: 'Reden',
        sendMedications: 'Stuur een overzicht van de medicatie mee',
        startDate: 'Startdatum',
    },
    months: {
        index: {
            abbreviation: {
                0: 'jan',
                1: 'feb',
                2: 'mrt',
                3: 'apr',
                4: 'mei',
                5: 'jun',
                6: 'jul',
                7: 'aug',
                8: 'sep',
                9: 'okt',
                10: 'nov',
                11: 'dec',
            },
        },
    },
    neurologyPatientTaskType: {
        remaining: 'Overig',
        remainingOther: 'Overig',
    },
    noPayoutReasonEnum: {
        NoActiveEmployment: 'Geen actieve betrekking',
        aborted: 'Proces afgebroken',
        ledgerNumber: 'Grootboekrekening ontbreekt',
        markedAsUnpaid: 'Gemarkeerd als onbetaald',
        missingAmount: 'Bedrag ontbreekt',
        noEmailAddress: 'Geen e-mailadres',
        payoutByTxcs: 'Uitbetaling door TxCS',
        providerName: 'Naam van dienst leverancier ontbreekt',
        providerOrganization: 'Organisatienaam van leverancier ontbreekt',
        relationNumber: 'Relatienummer van leverancier(sorganisatie) ontbreekt',
        waitingForConsultOrGrading: 'Wacht op consult of grading',
        waitingForFollowUpActivity: 'Wacht op vervolgactiviteit',
        waitingForReProcessing: 'Wacht op opnieuw verwerken',
        waitingforOsasc2: 'Wacht op OSASC2',
        zeroAmount: 'Nul bedrag',
    },
    notFundedReason: {
        abortedProcess: 'Afgebroken proces',
        financedByPreviousService: 'Al bekostigd door voorgaande dienst',
        freeOfCharge: 'Onbekostigd',
        fundedByOldPatientInvoicing: 'Bekostigd met oude patiëntfacturatie',
        isAlternativeFinancing: 'Alternatieve bekostiging',
        manual: 'Handmatig',
        migration: 'Proces gemigreerd vanuit TxCS',
        noConsentPatient: 'De patiënt heeft geen toestemming gegeven voor de kosten',
        noDeclarationSetForInsurerService: 'Geen declaratie ingesteld bij verzekeraar',
        noFundingDueToActivitySettings: 'Niet bekostigd volgens activiteitsinstellingen',
        noInsurersConfigured: 'Geen verzekeraars geconfigureerd',
        notFunded: 'Niet bekostigd ingesteld',
        parallelDeclaration: 'Parallelle declaratie',
        serviceNotFundable: 'Dienst niet te bekostigen',
        uninsuredOrNonBillableActivity: 'Onverzekerde of niet te factureren activiteit',
    },
    ophthalmology: {
        andere_bevindingen: 'Andere bevindingen',
        bsnCheckMatchingMessage: `Er wordt een automatische controle uitgevoerd of het BSN van de patiënt overeenkomt met de naam van de fundusfoto’s.`,
        geadviseerd_beleid: 'Geadviseerd beleid',
        maculopathie: 'Maculopathie',
        noBsnMatchingValidationMessage: `Het BSN van de geselecteerde foto's komt niet overeen met het BSN van de patiënt, daarom is de fotoselectie (gedeeltelijk) niet toegevoegd. Pas de selectie aan en probeer het opnieuw.`,
        retinopathie: 'Retinopathie',
        toelichting: 'Toelichting',
        unknownBsnMessage: `Het BSN van de patiënt is onbekend. Er wordt geen automatische controle gedaan of de juiste foto’s worden toegevoegd.`,
        urgentie: 'Urgentie',
    },
    ophthalmologyPatientTaskType: {
        remaining: 'Overig',
        remainingOther: 'Overig',
    },
    orderSupplies: {
        additionalNotes: 'Aanvullingen / opmerkingen',
        deliveryLocation: 'Afleverlocatie',
        order: 'Bestelling',
        orderSuppliesForEpisodeCategory: '{0} - materialen bestellen',
        placeOrder: 'Plaats bestelling',
        product: {
            plural: 'Producten',
            singular: 'Product',
        },
        selectProduct: 'Selecteer product',
        selectProducts: 'Selecteer je product(en)',
        whatDoYouNeed: 'Wat heb je nodig?',
    },
    organization: {
        accountRequests: 'Accountaanvragen',
        activeCarepaths: 'Actieve zorgpaden',
        add: 'Organisatie toevoegen',
        addAgbCode: 'Extra AGB-code toevoegen',
        addCount: 'Aantal toevoegen',
        addOrganization: 'Organisatie toevoegen',
        addOrganizationRelationship: 'Organisatierelatie toevoegen',
        addStaffMember: 'Medewerker toevoegen',
        added: 'Geregistreerd op',
        address: 'Adres',
        agbCode: 'AGB-code',
        agbCodes: 'AGB-code/begin- einddatum',
        alias: 'Roepnaam',
        areContactsAndTasksEnabled: 'Ondersteuning',
        areContactsAndTasksEnabledTooltip:
            'Ondersteuning bij het aanmaken van taken en het registreren van contacten voor Ksyos-medewerkers. Het beroep moet een polikliniekmedewerker zijn.',
        autoFill: {
            agbOnlyNumbersError: 'AGB-code mag alleen cijfers bevatten',
            copySelection: 'Geselecteerde gegevens overnemen',
            inactiveOrganizationError: 'Organisatie is niet actief',
            invalidAgbError: 'Ongeldige AGB-code opgegeven',
            noOrganizationFoundError: 'Geen organisatie gevonden',
            searchAgb: 'Zoek op AGB-code',
            searchResult: 'Zoekresultaat',
            searchVektis: 'Zoeken in Vektis',
            selectAll: 'Selecteer alles',
        },
        bankAccountNumber: 'Rekeningnummer',
        bankAccountStatus: 'Status rekeningnummer',
        canGetStarted: '{0} kan met Ksyos aan de slag.',
        changeName: 'Organisatienaam wijzigen',
        changeNameAskStartDate: 'Vanaf welke datum moet de nieuwe naam in gaan?',
        changeNameExplanation:
            'Een gewijzigde naam heeft effect op de financiële processen. We sturen een automatische mail naar de financiële afdeling om hen van de wijziging op de hoogte te brengen.',
        changeNameStartDate: 'Ingangsdatum',
        chooseOrganization: 'Organisatie kiezen',
        city: 'Plaats',
        cityOrZip: 'Plaatsnaam of postcode',
        contactDetails: 'Contactgegevens',
        dataErrorStats: {
            active: 'Actieve organisaties',
            agbCodeErrorTitle: 'Organisaties met AGB-code fouten',
            archived: 'Gearchiveerde organisaties',
            attentionRequired: 'Vereist aandacht',
            cleaningStatus: 'Opschoonstatus',
            duplicateAddress: 'Dubbel adres',
            duplicateAgbCode: 'Dubbele AGB-code',
            duplicateKvkNumber: 'Dubbel KVK-nummer',
            duplicateNameStatutory: 'Dubbele statutaire naam',
            duplicateOrganizationTitle: 'Organisaties met gegevens die meermaals voorkomen',
            expiredAgb: 'AGB-code verlopen',
            inProgress: 'In behandeling',
            invalidAgb: 'AGB-code ongeldig',
            missingAgb: 'AGB-code mist',
            tooltips: {
                active: 'Aantal organisaties die op actief staan',
                archived: 'Aantal organisaties die op gearchiveerd staan',
                attentionRequired: 'Aantal organisaties met opschoonstatus: Vereist aandacht',
                duplicateAddress: 'Aantal organisaties met een dubbel adres',
                duplicateAgbCode: 'Aantal organisaties met een dubbele AGB-code',
                duplicateKvkNumber: 'Aantal organisaties met een dubbel KVK-nummer',
                duplicateNameStatutory: 'Aantal organisaties met een dubbele statutaire naam',
                expiredAgb: 'Aantal organisatie met een verlopen AGB-code volgens Vektis',
                inProgress: 'Aantal organisaties met opschoonstatus: In behandeling',
                invalidAgb: 'Aantal organisaties waarbij de AGB-code niet aanwezig is in Vektis',
                missingAgb: 'Aantal huisartspraktijken waarbij de AGB-code mist',
                totalAgbErrors: 'Totaal aantal organisaties met AGB fouten',
                totalDuplicateErrors: 'Totaal aantal organisaties met dubbele fouten',
                unverified: 'Aantal organisaties zonder opschoonstatus',
                verified: 'Aantal organisaties met opschoonstatus: Geverifieerd',
            },
            totalAgbErrors: 'Totaal AGB fouten',
            totalDuplicateErrors: 'Totaal dubbele fouten',
            totalOrganizations: 'Totaal aantal organisaties',
            unverified: 'Geen aandacht vereist',
            verified: 'Geverifieerd',
        },
        details: 'Organisatiegegevens',
        dossierNotAccessible: 'Dossier niet inzichtelijk voor huisarts',
        edit: 'Organisatie bewerken',
        emailAddressPayouts: 'E-mailadres voor uitbetalingen',
        emailAddressRequestInbox: 'E-mailadres aanvragen',
        emailRequestFin: 'E-mailadres inbox aanvragen & financiële afdeling',
        emergencyNumber: 'Spoednummer',
        emergencyNumberTooltip:
            'Als het praktijknummer ook het spoednummer is, vul dan het praktijknummer ook als spoednummer in.',
        existingOrganizations: 'Bedoel je misschien een bestaande organisatie?',
        exportData: 'Te exporteren data',
        exportOrganizations: 'Organisaties exporteren',
        fallBackAnalysisOrganization: 'Fall back analyse organisatie',
        findOrganization: 'Zoek organisatie',
        foldInactiveOrganization: 'Inactieve organisaties inklappen',
        futureEmploymentsTitle: 'Zorgverleners met rechten in de toekomst',
        generalInformation: 'Algemene gegevens',
        healthcareInstitution: 'Zorginstelling',
        highPrivacyLevel: 'Hoog privacy niveau',
        highPrivacyLevelTooltip:
            'Als dit aanstaat zijn patiënten dossiers niet inzichtelijk voor de huisarts van de patiënt',
        houseNumber: 'Huisnummer',
        houseNumberSuffix: 'Huisnummer toevoeging',

        identifierDeclaration: 'AGB-code',
        inWhichOrganization: 'In welke organisatie wil je het oogonderzoek uitvoeren voor deze patiënt?',
        inactiveEmploymentsTitle: 'In het verleden werkzaam bij deze organisatie',
        isMijnKsyosActive: 'Pilot MijnKsyos',
        isMijnKsyosActiveTooltip:
            'Patiënten zullen worden uitgenodigd voor de pilot MijnKsyos. Deze instelling geldt alleen voor optiekzaken en heeft geen effect op het gebruik van MijnKsyos bij de volgende zorgpaden: slaap, audiologie, dermatologie.',
        kgoStatus: 'KGO status',
        kgoStatuses: {
            attentionRequired: 'Vereist aandacht',
            inProgress: 'In behandeling',
            verified: 'Geverifieerd',
        },
        ksyosEmployees: 'Ksyos medewerkers',
        kvkNumber: 'KVK-nummer',
        location: 'Plaatsnaam',
        modal: {
            deactivateKsyosEmployment: 'De medewerker zal geen werkzaamheden meer kunnen uitvoeren.',
            logbookAction: 'Actie',
            logbookActionDescription: {
                insert: 'Toegevoegd',
                update: 'Gewijzigd',
            },
            logbookChangedBy: 'Gewijzigd door',
            logbookDateOfChange: 'Datum wijziging',
            logbookTitle: 'Logboek wijzigingen patiëntenaantallen',
        },
        name: 'Organisatienaam',
        nameOrAgbCode: 'Organisatienaam of AGB-code',
        nameOrTxcsId: 'Organisatienaam of ID',
        nameOrganization: 'Naam organisatie',
        nameStatutory: 'Statutaire naam',
        namesHistory: 'Naamswijzigingen',
        newCount: 'Nieuw aantal',
        noActiveCarePaths: 'Deze organisatie heeft nog geen zorgpaden',
        noActiveCareProviders: 'Deze organisatie heeft nog geen zorgverleners',
        noPatientCount: 'Nog geen aantallen ingevuld',
        noSearchResultOrganization: 'We hebben geen organisatie gevonden op basis van je zoekactie',
        notes: 'Opmerkingen',
        notifications: {
            canOnlyBeReceivedByOrganization: 'Kan alleen op organisatie e-mail worden ontvangen',
            consultation: 'Notificaties consult',
            determineSettingsAsOrganization:
                "Wanneer je voor een dienst 'Kan alleen op organisatie e-mail worden ontvangen' instelt op Ja, kunnen zorgverleners met deze dienst niet meer een eigen e-mail invoeren bij hun notificatievoorkeuren.",
            examination: 'Notificaties onderzoek',
            notifications: 'Notificaties',
            organizationEmailNotSet:
                'Er is nog geen standaard organisatie e-mail ingevuld. Wil je dat hier notificaties op worden ontvangen?',
            receiveOn: 'Ontvangen op',
            referral: 'Notificaties verwijzing',
            setOrganizationEmail: 'Vul de organisatie e-mail dan hier in.',
        },
        numberOfPatients: 'Aantal ingeschreven patienten...',
        organization: 'Organisatie',
        organizationConsumer: 'Organisatie, aanvrager',
        organizationData: 'Gegevens bij de organisatie',
        organizationEmailAddress: 'Organisatie E-mailadres',
        organizationGroup: 'Organisatie/Groep',
        organizationId: 'OrganisatieId',
        organizationLevelPayoutsStartDate: 'Startdatum uitbetalingen o.b.v. contract 2025',
        organizationLevelPayoutsStartDateTooltip:
            'Dit is een tijdelijke financiële instelling die de vernieuwde uitbetalingen voor de contracten van 2025 bepaalt. De datum is meestal de ingangsdatum van het nieuwe contract van de organisatie.',
        organizationList: 'Organisatie lijst',
        organizationOverview: 'Organisatie overzicht',
        organizationPatientCount: 'Aantal patiënten ingeschreven bij de organisatie',
        organizationPayoutManagers: {
            logbookAction: 'Actie',
            logbookActionDescription: {
                delete: 'Verwijderd',
                insert: 'Toegevoegd',
            },
            logbookModificationDate: 'Datum wijziging',
            logbookModifiedBy: 'Gewijzigd door',
            logbookPractitionerName: 'Naam',
            logbookTitle: 'Logboek wijzigingen Organisatiebeheerders',
            noOrganizationManagersMessage:
                'Deze organisatie heeft geen organisatiebeheerder. Voeg deze rol toe onder de persoonlijke instellingen van de zorgverlener.',
            title: 'Organisatiebeheerders',
        },
        organizationRelationships: 'Organisatierelaties',
        organizationSpecialist: 'Organisatie, specialist',
        organizationStats: 'Organisatiestatistieken',
        organizationStructureType: 'Organisatiestructuur',
        organizationStructureTypeHeader: 'Type',
        organizationType: 'Type organisatie',
        organizations: 'Organisaties',
        patientCount: 'Patiëntenaantallen',
        patientCountExplainer:
            'Het aantal geldt ook voor alle peildata die erop volgen, tot een nieuwe peildatum en aantal wordt vastgelegd.',
        patientCountReferenceDateExplainer: 'Peildatum voor nieuwe aantal ingeschreven patiënten…',
        pendingApprovalOf: 'Wacht op beoordeling door {0}',
        phoneNumber: 'Telefoonnummer',
        practitionerAddedWillNotUseCS: '{0} kan geselecteerd worden als aanvrager en zal dan ZorgMail ontvangen',
        practitionerAddedWillUseCS: '{0} ontvangt een activatie e-mail op {1}',
        practitionerDataAtOrganization: 'Gegevens zorgverlener bij de organisatie',
        practitioners: 'Zorgverleners',
        practitionersWithFutureEmployments: 'Zorgverleners met rechten in de toekomst',
        practitionersWithPastEmployments: 'Zorgverleners met beëindigd dienstverband',
        previousNames: 'Vorige naam',
        referenceDate: 'Peildatum',
        rejectedAccountRequestAt: 'Afgewezen op {0}',
        rejectedAccountRequests: 'Afgewezen accountaanvragen',
        relationNumber: 'Relatienummer',
        relationNumberAbbeviation: 'Rel. nr.',
        reviewMatch: 'Beoordeel match',
        save: 'Opslaan',
        select: 'Organisatie selecteren',
        showInactiveOrganization: 'Inactieve organisaties tonen',
        showRoles: 'Rollen tonen',
        state: 'Provincie',
        statutoryName: 'Statutaire naam',
        street: 'Straat',
        streetName: 'Straatnaam',
        subscriber: 'Tenaamstelling bankrekening',
        successful: 'Gelukt!',
        txcsId: 'Organisatie ID',
        type: 'Type',
        url: 'Website',
        vektisCompare: {
            allDataMatches: 'Alle gegevens komen overeen met Vektis',
            copyDetails: 'Neem gegevens over',
            dataDoesntMatch: 'Deze gegevens van AGB-code {0} komen niet overeen met Vektis',
            inactiveOrganizationError: 'De organisatie met AGB-code {0} is niet actief',
            invalidAgbCode: 'De AGB-code {0} is ongeldig',
            noAgbProvided: 'Er is geen AGB-code ingevoerd voor deze organisatie. Vul deze eerst in.',
            noOrganizationFound: 'Geen organisatie gevonden voor AGB-code {0}',
        },
        verification: 'Verificatie',
        viewOrganization: 'Organisatie bekijken',
        zip: 'Postcode',
    },
    organizationDataErrorType: {
        agbStartDateMismatch: 'Vektis startdatum matcht niet',
        duplicateAddress: 'Dubbel adres',
        duplicateAgbCode: 'Dubbele AGB-code',
        duplicateKvkNumber: 'Dubbel KVK-nummer',
        duplicateNameStatutory: 'Dubbele statutaire naam',
        expiredAgb: 'AGB-code verlopen',
        invalidAgb: 'AGB-code ongeldig',
        missingAddress: 'Adres mist',
        missingAgb: 'AGB-code mist',
        missingKvkNumber: 'KVK-nummer mist',
        missingNameStatutory: 'Statutaire naam mist',
        tooltips: {
            agbStartDateMismatch: 'The startdatum van de AGB-code valt voor de startdatum in Vektis',
            duplicateAddress:
                'Het adres van deze organisatie komt overeen met 1 of meer organisaties. Leg een relatie tussen de organisaties of wijzig het adres.',
            duplicateAgbCode:
                'De AGB-code van deze organisatie komt overeen met 1 of meer organisaties. Leg een relatie tussen de organisaties of wijzig de AGB-code.',
            duplicateKvkNumber:
                'Het KVK-nummer van deze organisatie komt overeen met 1 of meer organisaties. Leg een relatie tussen de organisaties of wijzig het KVK-nummer.',
            duplicateNameStatutory:
                'De statutaire naam van deze organisatie komt overeen met 1 of meer organisaties. Leg een relatie tussen de organisaties of wijzig de statutaire naam.',
            expiredAgb: 'Volgens het AGB-register is de AGB-code verlopen',
            invalidAgb: 'De AGB-code is niet gevonden in het AGB-register',
            missingAddress: 'De postcode en/of huisnummer ontbreekt',
            missingAgb: 'De AGB-code ontbreekt en is verplicht voor dit type organisatie',
            missingKvkNumber: 'Het KVK-nummer ontbreekt',
            missingNameStatutory: 'De statutaire naam ontbreekt',
            vektisAddressMismatch: 'De postcode en huisnummer komen niet overeen met de gegevens van Vektis',
            vektisKvkNumberMismatch: 'Het KVK-nummer komt niet overeen met de gegevens van Vektis',
            vektisNameStatutoryMismatch: 'De statutaire naam komt niet overeen met de gegevens van Vektis',
        },
        vektisAddressMismatch: 'Vektis adres matcht niet',
        vektisKvkNumberMismatch: 'Vektis KVK-nummer matcht niet',
        vektisNameStatutoryMismatch: 'Vektis statutaire naam matcht niet',
    },
    organizationLogs: {
        effectiveDate: 'Ingangsdatum',
        linkToLogs: 'Logboek wijzigingen inzien',
        modalTitle: 'Logboek wijzigingen organisatiegegevens',
        modificationDate: 'Datum wijziging',
        modificationType: 'Type wijziging',
        modifiedBy: 'Gewijzigd door',
        modifiedField: {
            agbCodes: 'AGB-codes',
            identifierDeclaration: 'AGB-code',
            kgoStatus: 'KGO status',
            kvkNumber: 'KVK-nummer',
            nameStatutory: 'Statutaire naam',
            organizationName: 'Organisatienaam',
            organizationType: 'Organisatietype',
        },
        newValue: 'Nieuw',
        oldValue: 'Oud',
    },
    organizationRelationshipTypeEnum: {
        isBranchOf: 'Is vestiging van',
        isDuplicateOf: 'Is duplicaat van',
        isDuplicatedBy: 'Is gedupliceerd door',
        isHoedOf: 'Is HOED van',
        isParentOf: 'Is hoofdvestiging van',
        isPartOfHoed: 'Is onderdeel van HOED',
    },
    organizationStructureTypeEnum: {
        branch: 'Vestiging',
        hoed: 'HOED',
        organization: 'Organisatie',
    },
    organizationSwitchModal: {
        confirmSwitch: 'Weet je zeker dat je wilt wisselen?',
        fromTo: 'Je verlaat nu {0} en wilt verder gaan in {1}.',
        leavingSelectedPage: 'Je verlaat daarmee ook de huidige pagina.',
        leavingSelectedPatient: 'Je verlaat daarmee ook je huidige geselecteerde patiënt.',
        switchOrganization: 'Verwisselen van je organisatie',
    },
    organizationTypeEnum: {
        addictionCareCentre: 'Verslavingszorg Centrum',
        audiologistCenter: 'Audiologisch Centrum',
        centerForMedicalSpecialistCare: 'Centrum voor Medisch Specialistische Zorg',
        dentistPractice: 'Tandartsenpraktijk',
        dietitianPractice: 'Diëtistenpraktijk',
        ggzInstitution: 'GGZ-instelling',
        gpPractice: 'Huisartsenpraktijk',
        gpWeekendCentre: 'Huisartsenpost',
        hearingAidCenter: 'Hoorwinkel',
        hospital: 'Ziekenhuis',
        independentPractice: 'Vrijgevestigde praktijk',
        ksyosHospital: 'Ksyos Ziekenhuis',
        medicalDiagnosticCenterLaboratory: 'Medisch Diagnostisch Centrum  (Laboratorium)',
        medicalSpecialistCompany: 'Medisch Specialistisch Bedrijf',
        nursingFacility: 'Verpleeginstelling',
        optometryPractice: 'Optometriepraktijk',
        other: 'Overig',
        pharmacy: 'Apotheek',
        physicalTherapyPractice: 'Fysiotherapiepraktijk',
        psychiatryCenter: 'Psychiatriepraktijk',
        psychologyPracticePsychotherapyPractice: 'Psychologiepraktijk / Psychotherapiepraktijk',
        skinTherapyPractice: 'Huidtherapiepraktijk',
        speechTherapyPractice: 'Logopediepraktijk',
        testOrganization: 'Testorganisatie',
    },
    orthOProcedure: {
        complaints_and_or_reason_of_visit: 'Klachten en/of reden bezoek',
        convergencetest: 'Convergentietest',
        convert_to_first_ortho: 'Omzetten naar eerste onderzoek',
        convert_to_subsequent_ortho: 'Zet om naar vervolgonderzoek',
        dynamic_skiascopy: 'Dynamische skiascopie',
        family_history: 'Familiaire voorgeschiedenis',
        four_dot_test: 'Four Dot-test',
        general_health_and_medication: 'Algemene gezondheid en medicatie',
        general_observation_face: 'Algemene observatie gezicht',
        glasses_bagolini: 'Glazen van Bagolini',
        light_reflex: 'Lichtreflex',
        no_previous_ortho:
            'Let op! Er is nog geen eerste Orthoptisch onderzoek geweest in de laatste 6 maanden bij Ksyos. Je kunt dit negeren als er een eerste Orthoptisch onderzoek buiten Ksyos is geweest.',
        ocular_history: 'Oculaire voorgeschiedenis',
        previous_ortho: 'Er is al een Orthoptisch onderzoek gestart in de laatste 6 maanden',
        prismatest_15: '15^ prismatest',
        pupil_reactions: 'Pupilreacties',
        referrer_other: 'Overig verwijzer',
        reverse_prismatest: 'Omgekeerde prismatest',
        stereotest: 'Stereotest',
    },
    orthOVerwijzerEnum: {
        consultationBureau: 'Consultatiebureau',
        generalPractitioner: 'Huisarts',
        optician: 'Opticien',
        optometrist: 'Optometrist',
        other: 'Overig',
        ownInitiative: 'Op initiatief van de patiënt',
        schoolDoctor: 'Schoolarts',
    },
    osas: {
        MRA: 'MRA',
        PAP: 'PAP',
        SPT: 'SPT',
        alternativeAddressCheckboxText: 'Verstuur recorder naar ander adres',
        deliveryAddressRecorder: 'Afleveradres recorder',
        ess: 'ESS',
        firstConsultSaved: 'Het eerste contact is opgeslagen.',
        fosq10: 'FOSQ-10',
        giveToThePatient: 'De slaaprecorder zelf aan de patiënt geven',
        giveToThePatientAdminDetails: 'Meegegeven door praktijk',
        giveToThePatientKsyos: 'De slaaprecorder zelf aan de patiënt geven',
        giveToThePatientKsyosAdminDetails: 'Meegegeven door praktijk',
        handoverSleepRecorder: 'Het eerste contact is opgeslagen. Geef je patiënt de slaaprecorder mee.',
        hsdq: 'HSDQ',
        lifestyle: 'Advies leefstijl verandering',
        nightowl: '(i.c.m. een app op de smartphone van de patiënt)',
        patientHasSmartphone: 'Patiënt beschikt over een smartphone',
        questionnairesSentTo: 'Vragenlijsten verstuurd naar',
        reasonChanged: 'Reden wijziging',
        sendByKsyos: 'De slaaprecorder door Ksyos laten verzenden naar de patiënt',
        sendByKsyosAdminDetails: 'Verzonden door Ksyos',
        sendByNsi: 'De slaaprecorder door het NSI laten verzenden naar de patiënt',
        sendSleepRecorder: 'Slaaprecorder verzenden',
        sleepRecorderType: 'Slaaprecorder type',
        startOsasc2: 'Start tweede poliklinisch consult',
        watchPatGetSerialNumberError: 'Kan niet worden opgehaald uit CloudPAT',
        watchPatSerialNumberNotFilledIn: 'Niet ingevuld',
        watchpat: '(geen app of smartphone nodig)',
    },
    osasNotitie: {
        filesLabel: 'Bestand(en)',
        informGeneralPractitionerLabel: 'Huisarts op de hoogte brengen',
        informHeader: 'Informeren',
        noteTextLabel: 'Notitie',
        noteTextPlaceholder: 'Geef de notitie een omschrijving',
        noteTitleLabel: 'Notitie titel',
        noteTitlePlaceholder: 'Geef de notitie een korte titel...',
    },
    osasPermission: {
        cantCreateOsasvcOsasc2Missing:
            'Het vervolgconsult kan niet worden gestart omdat het tweede poliklinisch consult voor deze patiënt niet is gesloten of is geannuleerd.',
        cantCreateOsasvcOsasoMissing:
            'Het vervolgconsult kan niet worden gestart omdat het slaaponderzoek voor deze patiënt nog niet is afgerond of is geannuleerd.',
        patientPermission: 'De patiënt geeft toestemming voor de verwijzing',
        patientPermissionExtraDetails_1: 'Waar patiënt staat bedoelen wij patiënt en/of gemachtigde.',
        patientPermissionExtraDetails_2: 'De kosten voor deze zorg tellen mee voor het volledige eigen risico.',
        patientPermissionExtraDetails_2_fat: 'Eigen risico:',
        patientPermissionExtraDetails_3:
            'De patiënt ontvangt voor de kosten van deze zorg een factuur. De kosten kunnen worden gedeclareerd bij de zorgverzekeraar. Een eventuele eigen bijdrage wordt altijd kwijtgescholden.',
        patientPermissionExtraDetails_3_fat: 'Factuur:',
        patientPermissionExtraDetails_4:
            'Relevante medische gegevens worden gedeeld met de betrokken zorgverleners en zorgorganisaties.',
        patientPermissionExtraDetails_4_fat: 'Gegevens:',
        patientPermissionExtraDetails_5:
            'E-mail en/of telefoonnummer worden gedeeld met de betrokken zorgverleners en zorgorganisaties. Deze worden gebruikt voor het maken van een afspraak, sturen van een factuur en aanvullende informatie over deze dienst.',
        patientPermissionExtraDetails_5_fat: 'Contactgegevens:',
        patientQuestionnaire:
            'De patiënt wil meewerken aan het verbeteren van de zorgdienst en ontvangt een vragenlijst per e-mail',
        patientQuestionnaireExtraDetails_1: 'Ervaringen van patiënten helpen om onze zorg te verbeteren.',
        patientQuestionnaireExtraDetails_2: 'De patiënt ontvangt eenmalig een vragenlijst via e-mail.',
        referralPermission: 'Toestemming voor de verwijzing',
    },
    osasQuestionnaires: {
        absolutely: 'Helemaal wel',
        afterLunch: 'Tijdens zittend na een lunch zonder alcohol',
        annoyingItchy:
            'Wanneer ik in bed ga liggen heb ik een vervelend kriebelig, brandend of jeukend gevoel in mijn benen',
        concerned: 'Ik maak me zorgen over de gevolgen van mijn slechte slaap (bijvoorbeeld voor mijn gezondheid)',
        confusedHalfAwake:
            'Ik word regelmatig verward halfwakker en maak dan slaande bewegingen. Ik herinner mij er later vrijwel niets van',
        consequencesAfterBadNight:
            'Vooral na een slechte nacht heb ik overdag last van 1 of meer van deze gevolgen: vermoeidheid, slaperigheid, slecht humeur, zwakke concentratie, geheugenproblemen, gebrek aan energie',
        difficultyBeingActiveAtNight: 'Activiteit in de avond',
        difficultyBeingActiveInTheMorning: 'Activiteit in de ochtend',
        difficultyConcentratingOnWork: 'Concentreren',
        difficultyDrivingAVehicleLongDistances: 'Rijden lange afstand',
        difficultyDrivingAVehicleShortDistances: 'Rijden korte afstand',
        difficultyRememberingThings: 'Herinneren',
        difficultyVisitingFriendsAndFamily: 'Bezoek ontvangen',
        difficultyWakingUp:
            'Ik val pas tegen de ochtend in slaap en heb dan grote moeite om ’s morgens bijtijds wakker te worden. In het weekeinde slaap ik lang uit',
        difficultyWatchingVideoOrFilm: 'Films kijken',
        dryMouth: 'Ik word ’s morgens met een droge mond wakker',
        fallAsleepRepeatedly: 'In de loop van de dag val ik herhaaldelijk in slaap',
        fatigueDuringTheDay: 'Ik heb overdag last van vermoeidheid',
        functionLessWell: 'Omdat ik te weinig slaap krijg, functioneer ik overdag minder goed',
        haveRelationshipsDeteriorated: 'Beïnvloeding relaties',
        iDontFeelRestedAfterNap: 'Na een dutje overdag voel ik me niet uitgerust',
        iDontGetEnoughSleep: 'Ik krijg onvoldoende slaap, ondanks dat ik volop gelegenheid heb om lang te slapen',
        inAPublicEnvironment: 'Zitten in een openbare instelling',
        kickingLegMovements:
            'Tijdens mijn slaap heb ik last van schoppende beenbewegingen die ik onmogelijk kan onderdrukken',
        lieAwake: 'Ik lig ’s nachts lang wakker',
        moreThanTenHours:
            'Ik slaap ’s nachts gewoonlijk meer dan 10 uur, heb moeite om ’s morgens wakker te worden en doe overdag ook dutjes',
        moveLegs: 'Tijdens mijn slaap lig ik met mijn benen of armen te bewegen',
        needForIntimacy: 'Intimiteit',
        nightmares: 'Ik heb last van nachtmerries of angstige dromen',
        normalTime:
            'Ik slaap slecht omdat het me niet lukt om een normale tijd in te slapen en ’s morgens op een normale tijd wakker te worden',
        notAtAll: 'Helemaal niet',
        poorSleep: 'De kwaliteit van mijn slaap is slecht en ik voel me ’s morgens dan ook niet uitgerust',
        questionnaireReport: 'Rapportage vragenlijsten',
        restingInTheAfternoon: 'Tijdens liggende rust in de middag',
        restlessLegs:
            'Als ik “rusteloze benen” heb, kan ik dit gevoel onderdrukken door te lopen of mijn benen te strekken',
        screaming:
            'Ik word ’s nachts plotseling wakker, met een schreeuw en/of hevig transpirerend, en maak een angstige indruk',
        sittingInACarAsPassenger: 'Als passagier in de auto',
        sleepAttacks: 'Ik krijg overdag slaapaanvallen, die zó hevig zijn dat ik ze niet kan weerstaan',
        sleepBadlyDuringDay: 'Als ik ’s nachts wakker moet blijven, slaap ik overdag slecht',
        sleepWalking: 'Ik heb last van slaapwandelen',
        snoreLoudly: 'Ik snurk luid tijdens mijn slaap',
        sometimes: ' Soms wel, soms niet',
        sometimesInjured: 'Ik heb mezelf tijdens mijn slaap wel eens verwond, zonder me dat achteraf te herinneren',
        stopBreathing: 'Ik stop met ademen tijdens mijn slaap',
        suffocation: 'Ik schrik ’s nachts wakker met het gevoel te stikken',
        talkingToSomeone: 'Zitten en praten met iemand',
        timeVaries: 'Mijn tijd van inslapen wisselt sterk van dag tot dag',
        troublesFallingAsleep: 'Ik kan ’s avonds moeilijk in slaap komen',
        unintentionallyFallAsleepDuringDay:
            'Ik val overdag ongewild in slaap, vooral in een saaie situatie (bijvoorbeeld bij een saai tv-programma',
        urgeToMoveLegs: 'Als ik rustig zit, vooral ’s avonds, voel ik de drang om mijn benen te bewegen',
        usuallyNot: 'Meestal niet',
        usuallyYes: 'Meestal wel',
        vividDream:
            'Ik heb regelmatig een levendige droom waarin ik word aangevallen en me dan verdedig met wilde bewegingen',
        waitingInTheCarInTraffic: 'In de auto in een stilstaande file',
        whenReading: 'Tijdens zitten en lezen',
        whenWatchingTv: 'Tijdens televisie kijken',
    },
    outclinicDashboard: {
        editSnooze: 'Wijzig sluimerdatum',
        hideNonAlarms: "Verberg PID's zonder alarm",
        hideSnoozed: "Verberg gesluimerde PID's",
        late: 'Te laat',
        modalConfirmRemove: 'Weet je zeker dat je de sluimer wil verwijderen?',
        modalDatepickerLabel: 'Sluimer tot datum',
        modalTitle: 'Sluimeren voor PID {0}',
        modalTitleEdit: 'Gesluimerde PID {0}',
        modalTitleRemove: 'Verwijder sluimer voor PID {0}',
        noResults: 'Er zijn geen processen die voldoen aan je zoekcriteria',
        notApplicable: 'n.v.t.',
        planned: 'Ingepland',
        removeSnooze: 'Verwijder sluimer',
        snoozeProcess: 'Sluimer PID',
        snoozedUntil: 'Gesluimerd tot {0}',
        title: 'Dashboard {0}',
        toBePlanned: 'In te plannen',
        toDo: 'Uit te voeren',
    },
    outpatientClinic: {
        tasks: {
            allTasks: 'Alle taken',
            assign: 'Wijs toe',
            assignSelectedTasksTo: 'Wijs geselecteerde {0} toe aan',
            assignTo: 'Wijs toe aan',
            category: 'Categorie',
            categoryName: {
                consultants: 'Consulent',
                consults: 'Spreekuren',
                inactivity: 'Inactiviteit',
                planning: 'Plannen',
                referrals: 'Verwijzen',
                remaining: 'Onbekend',
            },
            changesSuccessfullySaved: 'Wijzigingen succesvol opgeslagen',
            editExpectedDateCompleted: 'Wijzig streefdatum',
            editPerformer: 'Wijzig uitvoerder',
            errorFetchingTasks: 'Er is een fout opgetreden bij het ophalen van de taken. Probeer het later opnieuw.',
            newExpectedDateCompleted: 'Nieuwe streefdatum',
            plural: 'taken',
            selectedAmountTasks: 'Je hebt {0} {1} geselecteerd',
            singular: 'taak',
            taskManagement: 'Taken management',
        },
    },
    patient: {
        add: 'Patiënt toevoegen',
        addBSN: 'Vul dan ook het BSN in',
        addBSNAndBirthDate: 'Met het BSN en de geboortedatum kunnen we de patiënt voor je vinden',
        addBirthDate: 'Vul dan ook de geboortedatum in',
        addCareProvider: 'Huisarts toevoegen',
        address: 'Adres',
        age: 'Leeftijd',
        alwaysMakeReadable: 'Maak altijd leesbaar',
        birthDatePlaceholder: 'dd-mm-jjjj',
        birthDateSearch: 'Vul eerst de geboortedatum in',
        birthDateShort: 'Geb. datum',
        blurHeader: 'Niemand kan meelezen',
        blurMessage: 'De lijst met patiëntnamen is onleesbaar gemaakt, om de privacy van je patiënten te beschermen',
        brp: 'BRP',
        careProvider: 'Huisarts',
        careProvider_unknown: 'Huisarts onbekend',
        careProviderEdit: 'Huisarts wijzigen',
        careProviderOfPatient: 'Huisarts van de patiënt',
        careProviderOrganization: 'Huisartsenpraktijk',
        careProviderPractitioner: 'Huisarts',
        careProvidersTeam: 'Zorgteam',
        createNew: 'Nee, maak een nieuwe patiënt aan',
        createNewPatient: 'Wil je een nieuwe patiënt aanmaken?',
        createPatient: 'Maak patiënt aan',
        creatingMedicalRecord: 'Aanmaken dossier...',
        dateSearch: 'Datum formaat: (dd-mm-jjjj)',
        deceasedDate: 'Datum overlijden',
        deliveryAddress: 'Afleveradres',
        details: 'Persoonsgegevens',
        detailsCounselling: 'Patiëntgegevens',
        displayOwnPatientsOnly: 'Toon alleen mijn patiënten',
        downloadMedicalRecord: 'Download dossier',
        duplicateName:
            'Er bestaat reeds een patiënt met dezelfde naam en geboortedatum. Wijzig de gegevens of open het bestaande dossier van de ',
        duplicateNameSearchBar:
            'We hebben een patiënt gevonden met dezelfde naam en geboortedatum. Open het bestaande dossier van de ',
        duplicateSocialSecurity:
            'Er bestaat reeds een patiënt met het ingevoerde BSN. Voer een ander BSN in of open het bestaande dossier van de ',
        duplicateSocialSecuritySearchBar:
            'We hebben de patiënt gevonden, maar de geboortedatum komt niet overeen met het ingevoerde BSN. Open het bestaande dossier van de ',
        editCareProvider: 'Wijzig huisarts',
        editCareProvidersTeam: 'Wijzig zorgteam',
        editPatient: 'Wijzig patiëntgegevens',
        editPatientConsent: 'Wijzig toestemmingen inzage',
        emailForContact: 'E-mailadres voor afstemmen contact',
        emergencyNumber: 'Spoednummer',
        extraSearch: 'Verder zoeken',
        fillInManually: 'Handmatig invoeren',
        foundPatient: 'Patiënt gevonden',
        foundPatients: 'Patiënten gevonden',
        getNameAndInsurance: 'Gegevens ophalen',
        goToPrivateMode: 'Ga naar de privacy-stand',
        healthInsurer: 'Zorgverzekeraar',
        homePhoneNumber: 'Thuis nummer',
        houseLetterShort: 'Letter',
        houseNumberSuffixShort: 'Toev.',
        iHaveCheckedTheData: 'Ik heb de gegevens gecontroleerd',
        identifierInsurance: 'Verzekering',
        identifierSocialSecurity: 'BSN',
        identifierSocialSecurityLong: 'Burgerservicenummer',
        insuranceOrAddressSearch: 'Zoek verder via polisnummer of postcode en huisnummer',
        insurancePolicyNumber: 'Polisnummer zorgverzekering',
        insurancePolicyNumberShort: 'Polisnr.',
        isDeceased: 'Overleden',
        makeReadable: 'Maak leesbaar',
        maritalStatus: 'Burgerlijke staat',
        mergeChoice: 'Gebruik welke waarde',
        mergeChoiceClientSafe: 'CS',
        mergeChoiceHIS: 'HIS',
        mergeCurrentValues: 'Huidge waarde in ClientSafe',
        mergeExplanation:
            'De patiënt die je uit je HIS hebt geïmporteerd bestond reeds in ClientSafe. De gegevens uit je HIS wijken echter af van de gegevens die in ClientSafe zijn opgeslagen. Maak hieronder voor de betreffende velden een keuze welke waarde gebruikt moet worden.',
        mergeField: 'Categorie',
        mergeNewHisValues: 'Waarde geïmporteerd uit HIS',
        mergeSSO: 'Gegevens samenvoegen',
        moreDetails: 'Meer details',
        nameCareProvider: 'Naam huisarts',
        nameFamily2: 'Tweede achternaam',
        nameInfix2: 'Tweede achternaam tussenv.',
        nameUsual: 'Roepnaam',
        newPatient: 'Nieuwe patiënt',
        newPatientBody: 'We vullen automatisch de patiëntgegevens aan waar mogelijk',
        newPatientHeader: 'Registreer je patiënt',
        noBSN: 'Geen BSN bekend?',
        noBSNPleaseCheckData: 'Geen BSN bekend. Controleer of dit de juiste gegevens zijn.',
        noMatchVecozoTryAgain: 'Combinatie niet gevonden. Probeer opnieuw en voer de gegevens opnieuw in.',
        noResult:
            'We hebben geen patiënt gevonden met {0}. Zoek op zowel BSN en geboortedatum om de patiënt aan te maken.',
        noResultBirthdate: 'deze geboortedatum',
        noResultBsn: 'dit BSN',
        noResultName: 'deze naam',
        noResultsBody: 'Controleer je zoekopdracht of vul meer gegevens in dan zoeken we verder',
        noResultsHeader: 'We hebben deze patiënt nog niet gevonden',
        noSearchResultPatientslist: 'We hebben geen patiënten gevonden op basis van je zoekactie',
        noValidInsurance: 'Geen geldige verzekering',
        patient: 'Patiënt',
        patientEmailAddress: 'E-mailadres patiënt',
        patientHomePhoneNumber: 'Thuis nummer patiënt',
        patientMobilePhoneNumber: 'Mobiel nummer patiënt',
        patientName: 'Patiënt naam',
        patientPermissionCheckbox: 'Ik heb toestemming van de patiënt om de gegevens te wijzigen',
        patientPhoneNumber: 'Telefoonnummer patiënt',
        patientSearchList: 'Zoek patiënt op BSN, geboortedatum, naam...',
        patientSelectOrAdd: 'Bedoel je deze patiënt',
        patientUnconfirmed: 'Onbevestigde patiënten',
        patients: 'Patiënten',
        policyNumber: 'Polisnummer',
        practice: 'Praktijk',
        practiceData: 'Praktijkgegevens',
        practiceEmergencyNumber: 'Spoednummer',
        practiceName: 'Naam praktijk',
        practiceNumber: 'Praktijknummer',
        proceed: 'Ja, ga verder met deze patiënt',
        recover: 'Herstel',
        registerDeath: 'Overlijden registreren',
        registerGP: 'Inschrijving bij huisarts',
        registerPatient: 'Registreer patiënt',
        results: 'Resultaten',
        save: 'Patiënt opslaan',
        savePatientData: 'Bewaar patiëntgegevens',
        search_in_organization: 'Zoek alleen binnen eigen organisatie',
        searchByPatientSocialSecurity: 'Zoek patiënt op BSN',
        searchName: 'Zoek op voor- en achternaam',
        searchPatient: 'Zoek patiënt',
        selectGP: 'Type en kies een huisart',
        unRegisterGP: 'Uitschrijving',
        vecozo: 'Verzekeringsgegevens',
        whoIsCareProvider: 'Wie is de huisarts?',
        zip: 'Postcode',
    },
    patientAccess: {
        chooseNotification: 'Kies een melding...',
        notPossibleToAccess:
            'Het is op het moment niet mogelijk om nog een medisch dossier in te zien. Neem contact op met de Privacy Functionaris of de teamlead van Tech.',
        notification: 'Melding',
        notificationDetails: 'Melding details',
        patientAccessLog: 'Patiënt toegangslog',
        reason: 'Reden',
        reasonForAccess: 'Reden om data van de patiënt in te zien',
        reasonForAccessDefaultOptions: {
            checkRequest: 'Controle aanvraag',
            failedExamination: 'Mislukt onderzoek',
            internalImprovingService: 'Intern gebruik - verbeteren van de dienstverlening',
            patientmixUp: 'Patiënt verwisseling',
            requestRhythmStripsRequestGP: 'Opvragen ritmestroken op verzoek van huisarts',
            requestRhythmStripsRequestMedicalSpecialist: 'Opvragen ritmestroken op verzoek van medisch specialist',
            uncertaintyResult: 'Onduidelijkheid over de uitslag',
            viewTechnicalNote: 'Inzien technische opmerking',
        },
    },
    patientDataRequest: {
        clientSafeCarepaths: 'Zorgpaden van ClientSafe',
        concernsGgz: 'betreft GGZ dossier',
        concernsNsi: 'betreft NSI dossier',
        mergeError: 'Samenvoeging mislukt',
        noFile: 'Geen dossier',
        notInPdf: 'Niet in PDF',
        pdfClientSafe: 'PDF ClientSafe',
        pdfTXCS: 'PDF TxCS',
        present: 'Aanwezig',
        requestFile: 'Dossier verzoeken',
    },
    patientDataRequestStatusEnum: {
        open: 'Open ',
        processed: 'Verwerkt',
    },
    patientInvoiceServicePrices: {
        addPricePeriod: 'Prijsperiode toevoegen',
        patientInvoicing: 'Patiëntfacturatie',
    },
    patientSatisfactionQuestions: {
        doneBetter: 'Wat kan anders en/of beter?',
        enoughInformation: 'Kreeg je van Ksyos voldoende informatie over het slaaponderzoek?',
        explanationClear: 'Vond je de uitleg van de huisarts over het slaaponderzoek duidelijk?',
        explanationUnderstandable: 'Was de uitleg van de arts begrijpelijk?',
        feltHelped: 'Voelde je je goed geholpen?',
        opportunityToAskQuestions: 'Was er voldoende gelegenheid om vragen te stellen?',
        repeatDigitalExamination: 'Zou je op basis van je ervaring nogmaals een digitaal onderzoek doen?',
        satisfiedWith: 'Waar ben je tevreden over?',
        scoreFreeText: 'Toelichting',
        scoreKsyosSleep: 'Welk cijfer geef je aan Ksyos Slaap?',
        sleepExaminationFreeText: 'Toelichting',
        videoConsultFreeText: 'Toelichting',
    },
    patientSso: {
        birthDate: 'dd-mm-jjjj',
        birthDateLabel: 'Voer geboortedatum in',
        findPatientNoBsn: 'Patiëntgegevens vinden zonder BSN',
        gender: 'Selecteer geslacht',
        houseNumber: 'Voer huisnummer in',
        identifierSocialSecurity: 'Voer BSN in',
        insurancePolicyNumber: 'Voer polisnummer in',
        nameFamily: 'Voer achternaam in',
        nameInfix: 'Tussenv.',
        patientNotFound: 'We konden de patiënt niet vinden',
        patientNotFoundInvalid:
            'We konden de patiënt niet vinden, controleer of de ingevulde gegevens correct zijn en probeer het anders opnieuw.',
        patientNotFoundServiceDown:
            'We kunnen niet alle persoonsgegevens verifiëren. Vul onderstaande informatie aan indien bekend.',
        patientNotFoundServiceDownNoBsn:
            'We kunnen op dit moment geen patiëntgegevens voor je ophalen. Vul onderstaande informatie aan indien bekend.',
        radioLabel_address: 'Postcode en huisnummer',
        radioLabel_identifierSocialSecurity: 'BSN',
        radioLabel_insurance: 'Polisnummer',
        radioLabel_other:
            'Geen van bovenstaande gegevens zijn bekend. Maak een nieuwe patiënt aan op basis van geslacht en achternaam',
        searchOption: 'Patiëntgegevens ophalen via',
        searchPatient: 'Zoek de patiënt op door onderstaande velden in te vullen',
        withoutBsnFindPatient:
            'Ook zonder BSN kunnen we vaak patiëntgegevens voor je ophalen, vul de velden hieronder in',
        zip: 'Voer postcode in',
    },
    payoutActivityType: {
        biometrician: 'Biometrie',
        grader: 'Grading',
        performer: 'Uitvoer',
        requester: 'Aanvraag',
        specialist: 'Consultatie',
        supervisor: 'Kwaliteitscontrole',
    },
    payoutType: {
        biometrician: 'Biometrist',
        grader: 'Grader',
        performer: 'Uitvoerder',
        requester: 'Aanvrager',
        specialist: 'Specialist',
        supervisor: 'Controleur',
    },
    pdfTemplate: {
        attachment: {
            pdfEncryptedWarning: 'PDF is versleuteld en kan niet weergegeven worden',
        },
    },
    personalSettings: {
        addBothToContinue: 'Vul beide in om verder te gaan.',
        addIdentifier: '{0} invullen',
        addRights: 'Rechten toevoegen aan {0}',
        alertMissingServiceOrganizationSettings:
            'Instellingen voor de nieuwe rechten die je hebt toegevoegd missen nog op organisatieniveau. Vul deze instellingen hier in om de nieuwe rechten toe te voegen aan de zorgverlener.',
        carePathDetails: {
            activeSince: 'Actief sinds',
            automaticallyAssigned: 'Automatisch toegekend',
            careGroup: 'Zorggroep',
            carePath: 'Zorgpad',
            carePathDetails: 'Details zorgpaden',
            dateOfRightRequest: 'Datum aanvraag recht',
            requestedByPractitioner: 'Zelf aangevraagd',
            rights: 'Rechten',
            serviceRightObtained: 'Recht verkregen',
        },
        contactDetails: 'Contactgegevens',
        email: 'E-mail',
        emailAlt: 'Alternatieve e-mail voor notificaties',
        emailNotifications: 'E-mailnotificaties',
        emergencyNumber: 'Spoednummer',
        extraEdifacts: 'Extra EDIFACT berichten',
        followUpMeasurementGgz: 'Standaard nameting',
        ggz: {
            availability: 'Beschikbaarheid',
            availabilityExplanation:
                'Geef hier aan wanneer je gebeld kunt worden voor een telefonisch consult. Zo weten andere zorgverleners wanneer je beschikbaar bent.',
            consultation: 'GGZ consult',
            examination: 'GGZ onderzoek',
            phoneAvailability: 'Telefonische beschikbaarheid',
            phoneAvailabilityExplanationAsAccountManager:
                'Geef hier aan wanneer de zorgverlener gebeld kan worden voor een telefonisch consult. Zo weten andere zorgverleners wanneer hij of zij beschikbaar is.',
        },
        notifications: {
            addAnotherWorkEmail:
                'Je hebt nog de mogelijkheid om een extra werk e-mail in te vullen. Wil je hier notificaties op ontvangen?',
            addAnotherWorkEmailAccountManager:
                'Er is nog de mogelijkheid om een extra werk e-mail in te vullen. Wil je dat hier notificaties op worden ontvangen?',
            consumer: {
                consultation: 'Resultaat consult ({0})',
                contact: 'Resultaat contact ({0})',
                counseling: 'Resultaat begeleiding ({0})',
                examination: 'Resultaat onderzoek ({0})',
                other: 'Resultaat ({0})',
                referral: 'Afgewezen verwijzing / Intake of ontslag rapport beschikbaar ({0})',
                referralSmall: 'Afgewezen verwijzing klein ({0})',
            },
            emailNotificationSettingAtOrganizationalLevel: 'hier aanpassen.',
            personalEmail: 'persoonlijke e-mail',
            provider: {
                consultation: 'Nieuwe consultvraag ({0})',
                contact: 'Nieuw contactmoment ({0})',
                counseling: 'Nieuwe begeleiding ({0})',
                examination: 'Nieuwe aanvraag onderzoek ({0})',
                other: 'Nieuw proces ({0})',
                referral: 'Nieuwe verwijzing ({0})',
                referralSmall: 'Nieuwe verwijzing klein ({0})',
            },
            receiveAt: 'Ontvangen bij',
            receiveOn: 'Ontvangen op',
            sendOnDeviantResult: 'Alleen afwijkende uitslagen',
            sendOnNormalAndDeviantResult: 'Zowel afwijkende als normale uitslagen',
            setWorkEmail: 'Vul deze dan hier in.',
            setWorkEmailAccountManager: 'Vul de werk e-mail dan hier in.',
            settingsDeterminedByOrganization:
                'Deze instellingen zijn vastgesteld op organisatieniveau. Neem voor het aanpassen van deze instellingen contact op met de servicedesk van Ksyos via 020 600 0060.',
            settingsDeterminedByOrganizationManager: 'Deze instellingen zijn vastgesteld op organisatieniveau.',
            settingsDeterminedByOrganizationManagerEdit:
                'Deze instellingen zijn vastgesteld op organisatieniveau. Je kunt de instellingen ',
            supervisor: {
                examination: 'Nieuwe supervisie ({0})',
            },
            workEmail: 'werk e-mail',
            workEmailAlt: 'alternatieve werk e-mail',
            workEmailNotSet:
                'Je hebt nog geen persoonlijk werk e-mail ingevuld. Wil je hier notificaties op ontvangen?',
            workEmailNotSetManager:
                'Er is nog geen persoonlijke werk e-mail ingevuld. Wil je dat hier notificaties op worden ontvangen?',
        },
        organizationStamp: {
            addStamp: 'Praktijkstempel toevoegen',
            addStampExplanation:
                'Je hebt nog geen praktijkstempel toegevoegd. Heb je een praktijkstempel? Voeg dan hier een foto toe van je praktijkstempel.',
            formTitle: 'Zorgverlener praktijkstempel',
            isUploading: 'Praktijkstempel wordt geüpload...',
            modalHeader: 'Praktijkstempel toevoegen aan andere zorgverleners?',
            modalText:
                'Sommige huisartsen binnen de organisatie hebben nog geen praktijkstempel. Wil je deze praktijkstempel ook toevoegen aan hun persoonlijke instellingen?',
            modalYes: 'Ja, voeg praktijkstempel toe',
            save: 'Voeg praktijkstempel toe',
        },
        preferenceList: {
            addFavoriteOrganizations:
                'Zijn er bepaalde organisaties waar je vaak een {0} naar toe stuurt? Voeg hier favorieten aan je verzendlijst toe.',
            addOrganizations: 'Voeg organisaties toe',
            applicant: 'aanvrager',
            closeConfirmationModalMessage:
                'Wanneer je afsluit zullen de organisaties die je hebt geselecteerd niet aan je verzendlijst worden toegevoegd.',
            closeConfirmationModalTitle: 'Weet je zeker dat je de organisaties niet wilt toevoegen?',
            copyPreferencesFromColleagues: "Voorkeuren van collega's overnemen",
            deletePreferenceListCancelMessage: 'Annuleren',
            deletePreferenceListConfirmMessage: 'Verwijderen',
            deletePreferenceListMessageFour: 'Je kunt deze dienst niet opslaan zonder voorkeurslijst.',
            deletePreferenceListMessageOne:
                'Bij deze zorgverlener staat ingesteld dat hij of zij een aanvrager kan selecteren. Hier is een voorkeurslijst voor nodig. ',
            deletePreferenceListMessageThree:
                'Je hebt voor deze zorgverlener ingesteld dat hij of zij een aanvrager kan selecteren. Er is dan een voorkeurslijst met aanvragers nodig. ',
            deletePreferenceListMessageTwo:
                'Als je de voorkeurslijst verwijdert, kan de zorgverlener geen aanvragers meer selecteren.',
            deletePreferenceListTitle: 'Voorkeurslijst verwijderen',
            nationWide: 'Landelijk netwerk',
            preferenceList: 'Voorkeurslijst',
            preferenceLists: 'Voorkeurslijsten',
            searchNationWide: 'Zoeken in landelijk netwerk',
            searchOnCityZipCodeOrState: 'plaats, postcode of provincie...',
            searchOnNameOrOrganization: 'Zoek op naam, of organisatie...',
            searchOnNameSpecializationOrOrganization: 'Zoek op naam, specialisatie of organisatie...',
            searchOnSpecialistOrOrganization: 'Zoek op specialist of organisatie...',
            searchOnSpecializationOrOrganization: 'Zoek op specialisatie of organisatie...',
            searchOnSpecializationSpecialistOrOrganization: 'Zoek op specialisatie, specialist of organisatie...',
            searchPreferredAndNationWide: 'Zoeken in voorkeurslijst en landelijk netwerk',
            specialistProfession: 'Beroep specialist',
            specialization: 'Specialisatie',
        },
        save: 'Opslaan',
        selectSupervisor: 'Supervisor selecteren',
        sendZorgMailOnExaminationRequest: 'Verstuur bij onderzoek aanvraag',
        sendZorgMailOnReferralIntake: 'Verstuur bij verwijzing intake',
        sendZorgMailOnReferralRequest: 'Verstuur bij verwijzing',
        specializationsTooltip:
            'De specialisaties worden getoond in de voorkeurslijst en het landelijk netwerk. Een aanvrager zou dan op basis van een specialisatie een persoon kunnen kiezen voor het versturen van de dienst.',
        subsequentServiceTooltip:
            'Alleen wanneer de zorgverlener de rechten heeft voor een opvolgende dienst kan deze dienst worden opgestart',
        yourZorgMail: 'Je ZorgMail',
        zorgMail: 'ZorgMail',
        zorgMailNumberExplanation:
            'Bij een ingevuld ZorgMail nummer ontvang je automatisch een EDIFACT bericht bij uitslagen van onderzoeken en consulten en bij ontslag na een verwijzing.',
        zorgMailPlaceholder: 'Vul ZorgMail nummer in',
    },
    physicalExamination: {
        consultationPlace: 'Dit consult vindt plaats',
        physicalExamination: 'Lichamelijk onderzoek',
        retrognathism: 'Retrognathie',
        tongueSize: 'Tonggrootte',
        tonsilSize: 'Tonsilgrootte',
    },
    practitioner: {
        add: 'Zorgverlener toevoegen',
        addExisting: 'Bestaande zorgverlener toevoegen',
        addMultiple: 'Zorgverlener toevoegen',
        addNew: 'Nieuwe zorgverlener toevoegen',
        addOrRemoveMultiple: 'Zorgverleners toevoegen of verwijderen',
        adminSettings: 'beheerder settings',
        chooseAtLeastOneRole: 'Kies minstens één rol',
        chooseRole: 'Klik en kies een rol',
        confirmCancelAddKsyosEmployeeTitle: 'Weet je zeker dat je geen medewerker wilt toevoegen?',
        confirmCancelAddPractitionerTitle: 'Weet je zeker dat je geen zorgverlener wilt toevoegen?',
        confirmCancelMessage: 'Wanneer je afsluit gaan alle wijzigingen verloren.',
        consumingService: 'Afname dienst',
        consumingServices: 'Afname diensten',
        couldNotFindAnyone: 'We konden niemand vinden.',
        details: 'Zorgverlener gegevens',
        dutch: 'Nederlands',
        employment: 'Dienstverband',
        english: 'Engels',
        expiresOn: 'Verloopt op',
        exportPractitioners: 'Zorgverleners exporteren',
        findPractitioner: 'Zoek zorgverlener',
        hasDeclarableQualification: 'Zorgverlener heeft een declarabele kwalificatie',
        identifierDeclaration: 'AGB-code',
        identifierHeader: 'BIG / KP & AGB',
        identifierRegistrationBig: 'BIG-nummer',
        identifierRegistrationKp: 'KP-nummer',
        inYourOrganization: 'in jouw organisatie',
        isActive: 'Actief',
        isManager: 'Ksyos beheerder',
        language: 'Selecteer taal',
        lastActivityDate: 'Datum laatste activiteit',
        lastLogin: 'Laatste login',
        nameOrAgbOrUid: 'Naam, AGB-code of Ksyos-nummer',
        nameOrId: 'Naam of ID',
        namePractitioner: 'Naam zorgverlener',
        noActiveCarePaths: 'Deze zorgverlener heeft nog geen actieve zorgpaden',
        noEmployments:
            'Let op, de zorgverlener heeft nog geen betrekking bij een organisatie. De zorgverlener kan nu niet inloggen.',
        noSearchResultGeneralPractitioner: 'We hebben geen huisarts gevonden op basis van je zoekopdracht',
        password: 'Wachtwoord',
        practitioner: 'Zorgverlener',
        practitionerDataErrorDetails: {
            clientSafeLastName: 'ClientSafe achternaam',
            vektisLastName: 'Vektis achternaam',
        },
        practitionerDataErrorType: {
            expiredAgb: 'AGB-code verlopen',
            invalidAgb: 'AGB-code ongeldig',
            missingAgb: 'AGB-code mist',
            tooltips: {
                expiredAgb: 'Volgens het AGB-register is de AGB-code verlopen',
                invalidAgb: 'De AGB-code is niet gevonden in het AGB-register',
                missingAgb: 'De AGB-code ontbreekt en is verplicht voor dit type zorgverlener',
                vektisNameMismatch:
                    'De achternaam van de zorgverlener komt niet overeen met de naam in het AGB-register',
            },
            vektisNameMismatch: 'Vektis naam matcht niet',
        },
        practitionerEdit: 'Zorgverlener bewerken',
        practitionerInactiveWarning: 'Deze zorgverlener is inactief',
        practitioners: 'Zorgverleners',
        providingService: 'Levering dienst',
        providingServices: 'Levering diensten',
        registeredOn: 'Geregistreerd op',
        registration: 'Registratie',
        registrations: 'Registraties',
        remove: 'Zorgverlener verwijderen',
        repeatPassword: 'Herhaal wachtwoord',
        returnToOrganizationOverview: 'Terug naar organisatie overzicht',
        role: 'Selecteer beroep ...',
        save: 'Zorgverlener opslaan',
        searchAgainOrAddNew: 'Zoek opnieuw of voeg een nieuwe zorgverlener toe.',
        showInactivePractitioners: 'Inactieve zorgverleners tonen',
        statusLanguage: 'Status en taal',
        supervisingService: 'Supervisie dienst',
        theRegistrationCodeIs: 'De registratiecode is',
        theRegistrationCodeIsExpired: 'De registratiecode is verlopen',
        txcsLogin: 'TxCS Login',
        uid: 'Ksyos-nummer',
        unregistered: 'Ongeregistreerd',
        withoutActiveEmployment: 'Zonder geldig dienstverband',
    },
    practitionerListStatusBadges: {
        accountWithoutLogin: 'Account zonder login',
        activationEmailIsGoingToBeSentOn: 'Activatiemail wordt verzonden op {0}',
        activationEmailWasSentOn: 'Activatiemail is verzonden op {0}',
        endedBy: 'Beëindigd per {0}',
        noCarePathRights: 'Geen zorgpadrechten',
        noZorgmail: 'Geen zorgmail',
    },
    practitionerSso: {
        links: 'Koppelingen',
        noSsoAdmin: 'De zorgverlener heeft geen actieve koppelingen.',
        noSsoPractitioner: 'Je hebt geen actieve koppelingen.',
        singleSignOn: 'Single sign-on koppelingen',
    },
    preferredServiceOrganizationProvider: {
        message_wrong: 'Organizaties zijn niet toegevoegd',
    },
    procedure: {
        aborted: 'Afbreken',
        attachments: 'Bijlages',
        cancelProcedure: 'Annuleer',
        confirmCancelProcedure: 'Weet je zeker dat je dit onderzoek wilt annuleren?',
        customControlStatus: 'Dienst specifieke',
        downloadDiary: 'Download dagboek',
        downloadRecording: 'Download recording',
        editPeformer: 'Procedure biometrist aanpassen',
        files: 'Bestanden',
        finishedSupervision: 'Controle afgerond',
        gradingSent: 'Grading en verzending naar afnemer',
        hideProcedure: 'Klap het onderzoek in',
        linkToExamination: 'Betreft het onderzoek van',
        new: 'Nieuw onderzoek',
        noServiceConsumerMessage:
            'Huisarts ({0}) van patiënt is geen afnemer van dit onderzoek. Neem voor meer informatie contact op met Ksyos.',
        performer: 'Uitvoerder',
        procedure: 'Onderzoek',
        saveAndSend: 'Verstuur',
        saveDraft: 'Tussentijds opslaan',
        sendExtraAttachments: 'Stuur extra bestanden mee',
        sentToGrader: 'Verzending naar grader',
        showProcedure: 'Bekijk het hele onderzoek',
        status_aborted: 'Afgebroken',
        status_completed: 'Afgesloten',
        sureToCancelAttachments:
            'Weet je zeker dat je het sturen van extra bestanden wilt annuleren? De bestanden worden niet bewaard.',
        useExamination: 'Kies onderzoek',
        useExaminationQuestion: 'Wil je dit consult doen o.b.v. een eerder ontvangen onderzoek?',
        userNoBiometrician: 'Gebruiker is geen biometrist en kan geen onderzoek toevoegen',
    },
    processActivity: {
        applyModification: 'Voer correctie door',
        cancel: {
            remove: 'Weet je zeker dat je {0} wilt verwijderen?',
            removeDefault: 'Weet je zeker dat je dit wilt verwijderen?',
            stopEditing: 'Weet je zeker dat je het bewerken van {0} wilt annuleren?',
            stopEditingDefault: 'Weet je zeker dat je het bewerken wilt annuleren?',
            stopModifying: 'Weet je zeker dat je het corrigeren van {0} wilt annuleren?',
            stopModifyingDefault: 'Weet je zeker dat je het corrigeren wilt annuleren?',
        },
        modify: 'Corrigeer',
        modifyNoChangesWarning: 'Pas minimaal één veld aan om een correctie door te voeren',
    },
    processActorProfession: {
        bariNurse: 'Verpleegkundig specialist',
        basicPsychologist: 'Psycholoog',
        dietitian: 'Diëtist',
        lifestyleCoach: 'Lifestylecoach',
        medicalSpecialist: 'Medisch specialist',
        osasNurse: 'OSAS verpleegkundige',
        outpatientAssistant: 'Polikliniekmedewerker',
    },
    processDetails: {
        accepted: 'Geaccepteerd',
        analyst: 'Analist',
        answeredAt: 'Beantwoord',
        answeredBy: 'Antwoord van',
        attachments: 'Bijlagen',
        cancelEdits: 'Wijzigingen annuleren',
        careGroup: 'Zorggroep huisarts',
        checkUploadJson: 'Let op: controleer de ecgFile(s) en vul de size en totalEcgSize in',
        closedAt: 'Afgesloten',
        confirmationRerunStep: 'Weet je zeker dat je deze stap wilt rerunnen?',
        consultBy: 'Vraag van',
        contactBy: 'Contact door',
        counselor: 'Begeleider',
        coupledProcesses: 'Gekoppelde processen',
        coupledRecorder: 'Gekoppelde recorder',
        createdAt: 'Aangemaakt',
        dateConfigured: 'Datum ingesteld',
        dateUploadFinished: 'Upload afgerond',
        dateUploadStarted: 'Upload gestart',
        diary: 'Dagboekje',
        dischargeBy: 'Ontslag door',
        dischargedAt: 'Ontslag',
        doUpdateSteps: 'Wijzigingen uitvoeren',
        durationExamination: 'Duur onderzoek',
        emailWarning: 'Bericht kan niet verzonden worden omdat er geen e-mail adres bekend is',
        examinedAt: 'Naar beoordelaar',
        examinedBy: 'Onderzoek door',
        export: 'Export',
        failedSteps: 'Gefaalde stappen',
        financialStatus: 'Financiële statussen',
        generateConfiguredByKsyos: 'Genereer ConfiguredByKsyos.dat',
        generateUploadJson: 'Genereer upload.json',
        gradedAt: 'Beoordeeld',
        gradedBy: 'Beoordeling door',
        intakeAt: 'Intake-rapport',
        intakeBy: 'Intake door',
        newUploadReport: 'Nieuw rapport geüpload',
        notAvailable: 'Niet beschikbaar',
        notYetAccepted: 'Nog niet geaccepteerd',
        notYetAnswered: 'Nog niet beantwoord',
        notYetClosed: 'Nog niet afgesloten',
        notYetDischarged: 'Nog niet ontslagen',
        notYetExamined: 'Nog niet onderzocht',
        notYetGraded: 'Nog niet beoordeeld',
        notYetIntake: 'Nog geen intake-rapport',
        notYetInvited: 'Nog niet uitgenodigd',
        notYetRequested: 'Nog niet aangevraagd',
        notYetSecondRoundAnswer: 'Nog niet beantwoord',
        notYetSecondRoundQuestion: 'Nog niet verstuurd',
        notYetSent: 'Nog niet verstuurd',
        notYetStarted: 'Nog niet gestart',
        notYetSupervised: 'Nog niet gecontroleerd',
        paper: 'Papier',
        performed: 'Uitgevoerd',
        performedByOrganization: 'Uitgevoerd door organisatie',
        performedByPractitioner: 'Uitgevoerd door medewerker',
        performedOn: 'Uitgevoerd op',
        referredBy: 'Verwezen door',
        requestedAt: 'Aangevraagd',
        requestedBy: 'Aanvraag door',
        rerunStep: 'Rerun stap',
        resend: 'Herzend',
        resendSuccess: 'De berichten zijn klaargezet voor herzending.',
        resendThirdPartyMessages: 'Herzend berichten derden',
        resendThirdPartyMessagesTitle: 'Herzend berichten naar derden',
        secondRoundAnswerAt: '2de Ronde beantwoord',
        secondRoundQuestionAt: '2de Ronde',
        sendEdifact: 'Verstuur EDIFACT',
        sentAt: 'Verstuurd',
        showCurrent: 'Toon ongewijzigd',
        showEdited: 'Toon gewijzigd',
        stepName: 'Stap naam',
        steps: 'Stappen',
        supervisedAt: 'Gecontroleerd',
        supervisedBy: 'Controle door',
        technicalStatus: 'Bekijk de technische statussen',
        therapielandIdFieldLabel: 'Programma ID',
        thirdPartyMessages: 'Bekijk berichten van/aan derden',
        unknown: 'Niet bekend',
        updateActiveToCanceled:
            'Indien je het proces wilt annuleren gebruik dan de annuleerknop hierboven. Die zorgt ervoor dat het proces op een nette manier wordt geannuleerd.',
        updateConfirm: 'Weet je zeker dat je de status van het proces wilt aanpassen?',
        updateOk: 'Het proces is bijgewerkt',
        updateWarning:
            'Je past nu alleen de status van het proces aan. Daarmee komt het proces in een onduidelijke situatie. Je bent zelf verantwoordelijk voor het oplossen daarvan.',
        updatedAt: 'Laatst bewerkt',
        updatesDone: 'Updates zijn uitgevoerd',
        updatesFailed: 'Update mislukt',
        verifyUpdateSteps: 'Wijzigingen controleren',
        view: 'Bekijk',
        viewFinancialStatus: 'Bekijk de financiële statussen',
        zorgmailGeneralPractitioner: 'Zorgmail huisarts',
    },
    processSearchField: {
        automaticRight: 'Recht automatisch toegekend',
        groupName: 'Groepsnaam',
        identifierSocialSecurity: 'Patiënt (BSN)',
        organization: 'Organisatie (Naam/ID)',
        organizationName: 'Organisatienaam',
        patientBirthdate: 'Patiënt (Geboortedatum)',
        patientName: 'Patiënt (Naam)',
        practitioner: 'Betrokken zorgverlener (Naam/ID)',
        processId: 'Proces ID (ClientSafe)',
        processNumber: 'Procesnummer (ClientSafe)',
        relationNumber: 'Relatienummer',
        requesterPractitionerUid: 'Aanvragende zorgverlener (ID)',
        txcsProcesId: 'Proces ID (TxCS)',
    },
    processThirdPartyMessages: {
        cancelled: 'Geannuleerd',
        failed: 'Mislukt',
        finishedDateTime: 'Gereed op',
        firstScheduledDateTime: 'Aangemaakt op',
        messageSubtype: 'Subtype',
        messageType: 'Bericht type ',
        nextScheduledDateTime: 'Versturen op',
        numberOfFailures: 'Aantal mislukte pogingen',
        patientName: 'Naam Patiënt',
        paused: 'Gepauzeerd',
        process: 'Proces',
        recipient: 'Geadresseerde',
        results: 'Resultaten',
        retrying: 'Opnieuw proberen',
        scheduled: 'Wordt verstuurd',
        sending: 'Bezig',
        sent: 'Verstuurd',
        status: 'Status',
        viewResults: 'Bekijk resultaten',
    },
    processThirdPartyMessagesEnum: {
        cancelled: 'Geannuleerd',
        failed: 'Mislukt',
        paused: 'Gepauzeerd',
        retrying: 'Opnieuw proberen',
        scheduled: 'Wordt verstuurd',
        sending: 'Bezig',
        sent: 'Verstuurd',
    },
    processType: {
        consultationRequest: 'dit consult',
        contact: 'dit contact',
        counseling: 'deze begeleiding',
        diagnosticReport: 'deze beoordeling',
        examinationRequest: 'deze aanvraag',
        procedure: 'dit onderzoek',
        referral: 'deze verwijzing',
        referralSmall: 'deze verwijzing',
        supervision: 'deze controle',
        supervisionBiometricsSummary: 'deze controle',
    },
    professionEnum: {
        addictionSpecialist: 'Verslavingsarts',
        assistant: 'Assistent(e)',
        audiologist: 'Audioloog',
        bariNurse: 'Verpleegkundig specialist obesitas',
        basicPractitioner: 'Basisarts',
        basicPsychologist: 'Basispsycholoog',
        clinicalPsychologist: 'Klinisch psycholoog',
        cognitiveBehavioralTherapist: 'Cognitief gedragstherapeut',
        contactLensSpecialist: 'Contactlensspecialist',
        dentist: 'Tandarts',
        dietitian: 'Diëtist',
        emdrTherapist: 'EMDR therapeut',
        exerciseTherapist: 'Oefentherapeut',
        functionAssistant: 'Functielaborant',
        generalPractitioner: 'Huisarts',
        generalPractitionerAios: 'Huisarts (AIOS)',
        generalPractitionerHidha: 'Huisarts (HIDHA)',
        generalPractitionerMentalHealthSpecialist: 'Kaderhuisarts GGZ',
        generalPractitionerWaarnemer: 'Huisarts (waarnemer)',
        gzPsychologist: 'GZ-psycholoog',
        haptotherapist: 'Haptotherapeut',
        hearingCareProfessional: 'Audicien',
        hospitalSecretarialAssistant: 'Secretariaatsmedewerker Ziekenhuis',
        imhSpecialist: 'IMH specialist (infant mental health)',
        laborant: 'Laborant',
        lifestyleCoach: 'Lifestylecoach',
        lossMentor: 'Verliesbegeleider',
        medicalSpecialist: 'Specialist',
        nurse: 'Verpleegkundige',
        nurseConsultant: 'Verpleegkundig consulent',
        nursePractitioner: 'Praktijkondersteuner Huisarts',
        nursePractitionerMentalHealth: 'POH-GGZ',
        nursePractitionerSomatic: 'POH-S',
        nurseSpecialist: 'Verpleegkundig specialist',
        obstetrician: 'Verloskundige',
        occupationalTherapist: 'Ergotherapeut',
        optician: 'Opticiën',
        optometrist: 'Optometrist',
        optometristIo: 'Optometrist i.o.',
        orthoptist: 'Orthoptist',
        osasNurse: 'OSAS verpleegkundige',
        other: 'Overig',
        outpatientAssistant: 'Polikliniekmedewerker',
        pharmacist: 'Apotheker',
        phdStudent: 'PhD-student',
        physicianAssistant: 'Physician Assistent',
        physicianAssistantOphthalmology: 'Physician assistant oogheelkunde',
        physiotherapist: 'Fysiotherapeut',
        podiatrist: 'Podotherapeut',
        practiceManager: 'Praktijkmanager',
        psychologistSecretarialAssistant: 'Secretariaatsmedewerker Psycholoog',
        psychosocialHypnotherapist: 'Psychosociaal Hypnotherapeut',
        psychotherapist: 'Psychotherapeut',
        relationshipAndFamilyTherapist: 'Relatie- en gezinstherapeut',
        remedialEducationalist: 'Orthopedagoog',
        resident: 'AIOS',
        sexologist: 'Seksuoloog',
        skinTherapist: 'Huidtherapeut',
        socialPedagogicalCareProvider: 'Sociaal pedagogische hulpverlener (SPH)',
        socialPsychiatricNurses: 'Sociaal psychiatrisch verpleegkundige',
        socialWorker: 'Maatschappelijk werker',
        specialistGeriatricMedicine: 'Specialist ouderengeneeskunde',
        speechTherapist: 'Logopedist',
        systemTherapist: 'Systeemtherapeut',
        technicalOphthalmicAssistant: 'Technisch oogheelkundig assistent (TOA)',
        teenageCoach: 'Tienercoach',
        visualTherapist: 'Beeldend therapeut',
    },
    professionEnumPlural: {
        addictionSpecialist: 'Verslavingsartsen',
        assistant: 'Assistenten',
        bariNurse: 'Verpleegkundig specialisten obesitas',
        basicPractitioner: 'Basisartsen',
        basicPsychologist: 'Basispsychologen',
        clinicalPsychologist: 'Klinisch psychologen',
        cognitiveBehavioralTherapist: 'Cognitief gedragstherapeuten',
        contactLensSpecialist: 'Contactlensspecialists',
        dentist: 'Tandartsen',
        dietitian: 'Diëtisten',
        emdrTherapist: 'EMDR therapeuten',
        exerciseTherapist: 'Oefentherapeuten',
        functionAssistant: 'Functielaboranten',
        generalPractitioner: 'Huisartsen',
        generalPractitionerAios: 'Huisartsen (AIOS)',
        generalPractitionerHidha: 'Huisartsen (HIDHA)',
        generalPractitionerMentalHealthSpecialist: 'Kaderhuisartsen GGZ',
        generalPractitionerWaarnemer: 'Huisartsen (waarnemer)',
        gzPsychologist: 'GZ-psychologen',
        haptotherapist: 'Haptotherapeuten',
        hearingCareProfessional: 'Audiciens',
        hospitalSecretarialAssistant: 'Secretariaatsmedewerker Ziekenhuissen',
        imhSpecialist: 'IMH specialisten (infant mental health)',
        laborant: 'Laboranten',
        lifestyleCoach: 'Lifestylecoaches',
        lossMentor: 'Verliesbegeleiders',
        medicalSpecialist: 'Specialisten',
        nurse: 'Verpleegkundigen',
        nurseConsultant: 'Verpleegkundig consulenten',
        nursePractitioner: 'Praktijkondersteuners Huisarts',
        nursePractitionerMentalHealth: 'POH-GGZ',
        nursePractitionerSomatic: 'POH-S',
        nurseSpecialist: 'Verpleegkundig specialisten',
        obstetrician: 'Verloskundigen',
        occupationalTherapist: 'Ergotherapeuten',
        optician: 'Opticiëns',
        optometrist: 'Optometristen',
        optometristIo: 'Optometrists i.o.',
        orthoptist: 'Orthoptisten',
        osasNurse: 'OSAS verpleegkundigen',
        other: 'Overige',
        outpatientAssistant: 'Polikliniekmedewerkers',
        pharmacist: 'Apothekers',
        phdStudent: 'PhD-students',
        physicianAssistant: 'Physician Assistents',
        physicianAssistantOphthalmology: 'Physician assistants oogheelkunde',
        physiotherapist: 'Fysiotherapeuten',
        podiatrist: 'Podotherapeuten',
        practiceManager: 'Praktijkmanagers',
        psychologistSecretarialAssistant: 'Secretariaatsmedewerker Psychologen',
        psychosocialHypnotherapist: 'Psychosociaal Hypnotherapeuten',
        psychotherapist: 'Psychotherapeuten',
        relationshipAndFamilyTherapist: 'Relatie- en gezinstherapeuten',
        remedialEducationalist: 'Orthopedagogen',
        resident: 'AIOS',
        sexologist: 'Seksuologen',
        skinTherapist: 'Huidtherapeuten',
        socialPedagogicalCareProvider: 'Sociaal pedagogische hulpverleners (SPH)',
        socialPsychiatricNurses: 'Sociaal psychiatrisch verpleegkundigen',
        socialWorker: 'Maatschappelijk werkers',
        specialistGeriatricMedicine: 'Specialisten ouderengeneeskunde',
        speechTherapist: 'Logopedisten',
        systemTherapist: 'Systeemtherapeuten',
        technicalOphthalmicAssistant: 'Technisch oogheelkundig assistenten (TOA)',
        teenageCoach: 'Tienercoaches',
        visualTherapist: 'Beeldend therapeuten',
    },
    professionalSummary: {
        all: 'Alles',
        allergies: 'allergieën',
        allergiesAdd: 'Voeg allergieën toe',
        allergiesRead: 'Allergieën',
        current: 'Huidige',
        diagnosticDeterminations: 'diagnostische bepalingen',
        diagnosticDeterminationsAdd: 'Voeg diagnostische bepalingen toe',
        diagnosticDeterminationsRead: 'Diagnostische bepalingen',
        episode: 'episode',
        episodesAndJournals: 'episodes en journaalregels',
        episodesAndJournalsAdd: 'Voeg episodes en journaalregels toe',
        episodesAndJournalsRead: 'Episodes en journaalregels',
        loadError:
            'De {0} kan/kunnen niet geladen worden. Neem contact op met de servicedesk via 020 - 600 00 60 zodat wij je verder kunnen helpen.',
        loading: 'Professionele samenvatting wordt opgehaald...',
        medication: 'medicatie',
        medicationAdd: 'Voeg medicatie toe',
        medicationRead: 'Medicatie',
        noAllergiesFound: 'Er zijn geen allergieën aangetroffen',
        noEpisodesFound: 'Er zijn geen episodes aangetroffen',
        noLabResultsFound: 'Er zijn geen diagnostische bepalingen aangetroffen',
        none: 'Geen',
        professionalSummary: 'Professionele samenvatting',
        selectionCount: '({0} geselecteerd)',
    },
    profile: {
        absence: 'Afwezigheid',
        absentFrom: 'Afwezig van',
        account: 'Account',
        accountActivated: 'Account geactiveerd',
        accountDetails: 'Basis gegevens',
        accountStatusNotActivated: 'Account niet geactiveerd',
        accountStatusTerminated: 'Geen geldige dienstverbanden',
        activationEmailResentTo: 'De activatiemail is opnieuw verzonden naar {0}',
        activationEmailSent: 'Activatiemail verzonden',
        activationEmailSentTo: 'De activatiemail zal verzonden worden naar',
        activationEmailUpdated: 'De activatiemail is succesvol aangepast en zal verzonden worden op {0}',
        addPeriod: 'Afwezigheid toevoegen',
        addProvider: 'Voeg een afhandelaar toe',
        archive: 'Archiveren',
        areYouSure: 'Weet je het zeker?',
        btnChangePassword: 'Wachtwoord opslaan',
        cannotUndoDeactivation:
            'Let op: Wanneer de ingevulde einddatum verstreken is, kan het inactief maken niet ongedaan worden gemaakt',
        changeAccountDetails: 'Rekeninggevens wijzigen',
        changePassword: 'Verander wachtwoord',
        changePassword2: 'Wachtwoord wijzigen',
        changePasswordCurrentCredentialsText: 'Voer eerst je huidige wachtwoord en de Google Authenticator code in',
        changePasswordNewPasswordText:
            'Kies nu een nieuw wachtwoord. Een goed wachtwoord is moeilijk te raden door zowel mensen als computers. Vermijd veel gebruikte woorden. Kies bijvoorbeeld een aantal ongewone woorden en verbind die met een teken naar keuze. Onderaan verschijnt een balk die inschat hoe goed je gekozen wachtwoord is. De minimaal vereiste score is 80%.',
        closeRequestMessage:
            'Verzoeken zoals een onderzoek of een consult die je hebt ingediend, kunnen automatisch worden afgesloten zodra ze zijn afgerond, zonder dat deze expliciet door jou moeten worden goedgekeurd. Je kunt hier aangeven welke type verzoeken direct gesloten kunnen worden.',
        closeRequests: 'Sluiten verzoeken',
        closeRequestsAuto: 'Sluit verzoek automatisch af',
        currentPassword: 'Huidig wachtwoord',
        deactivate: 'Inactief maken',
        defaultOrganization: 'Geselecteerde organisatie',
        defaultOrganizations: 'Geselecteerde organisaties',
        defaultProviders: 'Geselecteerde afhandelaars',
        disableMfaTemporarily: 'Tweestapsverificatie niet verplichten voor 72 uur',
        emailsNotMatch: 'De opgegeven e-mailadressen komen niet overeen',
        employmentPeriod: 'Dienstverband(en)',
        errorPasswordNotCorrect: 'Foutief wachtwoord',
        errorPasswordsNotEqual: 'Wachtwoorden zijn niet gelijk',
        explainDeactivate:
            'Wanneer je een zorgverlener op inactief zet, kan de zorgverlener geen werkzaamheden meer uitvoeren.',
        generalSettings: 'Algemene gegevens',
        holidayAbsence:
            'Ga je op vakantie of ben je om een andere reden niet aanwezig? Geef het door zodat anderen zien wanneer je beschikbaar bent.',
        isEmailAddressCorrect: 'Klopt de e-mail niet? Pas dit dan eerst aan in het profiel van de zorgverlener.',
        ksyosAccount: 'Ksyos account',
        lastActivationEmailSentAt:
            'Deze zorgverlener is nog niet actief bij Ksyos. De laatste activatiemail is {0} verzonden.',
        myAccount: 'Mijn account',
        newPassword: 'Nieuw wachtwoord',
        newPasswordRepeat: 'Herhaal wachtwoord',
        noConsumer: 'Er zijn geen standaard afnemers voor de dienst "{0}" toegevoegd.',
        noProvider:
            'Er zijn geen standaard leveranciers voor de dienst "{0}" toegevoegd. Als een verzoek wordt gedaan voor deze dienst dan kan er gekozen worden uit alle beschikbare leveranciers.',
        notificationSaved: 'Notificatie instellingen opgeslagen',
        passwordChanged: 'Wachtwoord is aangepast',
        practitionerNotActive: ' Deze zorgverlener is nog niet actief bij Ksyos.',
        practitionerShouldLogin: 'Gaat de zorgverlener actief gebruik maken van Clientsafe?',
        practitionerShouldLoginText:
            'Wanneer de zorgverlener toegang krijgt tot één of meerdere zorgpaden zal er een activatie e-mail verstuurd worden en kan de zorgverlener het eigen account activeren. Wanneer het account geactiveerd is kan de zorgverlener inloggen en gebruik maken van de  diensten.',
        profilePhoto: 'Profielfoto',
        provider: 'Afhandelaar',
        recommended: 'Aanbevolen',
        resendActivationEmail: 'Activatiemail opnieuw versturen',
        security: 'Beveiliging',
        sendToEmployment: 'Notificatie naar betrekking',
        sendToOrganization: 'Notificatie naar organisatie',
        sendToPrimary: 'Notificatie naar eigen e-mailadres',
        stop: 'Stopzetten',
        twoStepVerification: 'Tweestapsverificatie',
        verificationApp: 'Verificatieapp',
        viewProfile: 'Profiel bekijken',
    },
    radiologyPatientTaskType: {
        remaining: 'Overig',
        remainingOther: 'Overig',
    },
    readers: {
        explanation:
            'Met inzage rechten kunnen zorgverleners alle actieve diensten (binnen de organisatie) van een zorgpad inzien.',
        readingService: 'Inzage dienst',
        readingServices: 'Inzage diensten',
    },
    referral: {
        change: 'Pas aan',
        clickToAddOrganizationStamp: 'Klik hier om je verwijzing op te slaan en de praktijkstempel toe te voegen.',
        downloadPatientInformation: 'Download en print PDF',
        gpHasNoOrganizationStamp:
            'Je wilt deze patiënt verwijzen uit naam van de huisarts {0}. Er is nog een praktijkstempel nodig om de verwijzing te kunnen doen.',
        medicalData: 'Medische gegevens',
        patientInformation: 'Patiëntinformatie',
        referral: 'Medische verwijsbrief',
        referralAccepted: 'Je hebt de verwijzing geaccepteerd',
        referredTo: 'Verwezen naar',
        referrer: 'Verwijzer',
        sendViaEmail: 'Verstuur via e-mail',
        toRefer: 'Verwijzen',
        youHaveNoOrganizationStamp: 'We zien dat je organisatie nog geen praktijkstempel beschikbaar heeft in Ksyos.',
    },
    request: {
        abortProcess: 'Proces afbreken',
        abortProcessAdmin: 'Weet je zeker dat je het proces met ID {0} van patiënt {1} wilt afbreken?',
        abortProcessConfirmation: 'Reden voor afbreken',
        abortProcessMessage: 'Reden voor het afbreken van dit proces',
        abortProcessesAdmin:
            'Het onderzoek dat je wilt afbreken is omgezet naar een consult. Weet je zeker dat je zowel het onderzoek (PID: {0}) als het consult (PID: {1}) wilt afbreken?',
        absentUpToAndIncluding: 'Afwezig tot en met',
        action_acceptAnalysis: 'Accepteer analyse',
        action_addConsultReport: 'Behandel consult',
        action_addDischargeReport: 'Ontslagbrief',
        action_addExaminationReport: 'Start beoordeling',
        action_addIntakeReport: 'Intake-rapport',
        action_addProcedure: 'Start onderzoek',
        action_changeEmailAddressInvitations: 'Pas e-mailadres aan',
        action_changeProcedurePerformer: 'Wijzig biometrist',
        action_changeRecipient: 'Toewijzen aan andere medewerker',
        action_evaluateConsult: 'Beoordeel consult',
        action_getAccessToCounseling: 'Krijg toegang',
        action_openConsultReport: 'Consult rapport',
        action_openDischargeReport: 'Ontslagbrief',
        action_openExaminationReport: 'Onderzoeksresultaten',
        action_openIntakeReport: 'Intake-rapport',
        action_openProcedure: 'Open onderzoek',
        action_openReport: 'Open rapport',
        action_takeAnalysisFromColleague: 'Analyse overnemen van collega',
        activityDetails: 'Activiteitsgegevens',
        addedFiles: 'Toegevoegde bestanden',
        allProcesses: 'Alle processen',
        alreadyReprocessing: 'Aanvraag {0} staat reeds klaar om opnieuw verwerkt te worden.',
        alreadyScheduledQuestionnaires:
            'Er staat voor deze patiënt al een vragenlijst gepland ({0}). Als je doorgaat worden de vragenlijsten die gepland stonden geannuleerd.',
        askQuestionToSpecialist: 'Stel een vraag aan de specialist...',
        assign: 'Toewijzen',
        assignOther: 'Toewijzen andere zorgverlener',
        assignTo: 'Toewijzen aan',
        attachment: 'Bijlage',
        autoRights: '(Recht automatisch toegekend)',
        cancelledRequest: 'Geannuleerde taak',
        cancelledRequestAfterSendingMail:
            'Deze taak is geannuleerd nadat we de notificatiemail naar je hebben verzonden.',
        cannotAbortProcessesAdmin:
            'Proces {0} van patiënt {1} kan niet worden afgebroken. Het heeft een afhankelijkheid met processen: {2}. Breek eerst deze processen af.',
        careProviderIsAlsoRequester: 'Is de huisarts ook de aanvrager?',
        carryOnProcess: 'Proces doorzetten',
        change: 'Wijzig',
        changeConsultType: 'Wijzig consulttype',
        checkCareProvider: 'Controleer of de juiste huisarts geselecteerd is',
        checkGeneralPractitioner: 'Controleer huisartspraktijk',
        chooseGeneralPractitioner: 'Kies huisarts',
        chooseOrganization: 'Kies organisatie',
        choosePractitionerOrOrganizationOrCity: 'Zoek op zorgverlener, organisatie of plaatsnaam',
        chooseSearchOrganization: 'Typ en zoek, of klik en kies uit de lijst...',
        closeProcess: 'Sluit proces',
        closedByConsumer: 'Afsluiting door afnemer',
        closedRequests: 'Afgeronde taken',
        colleagueRequests: 'Bij collega',
        confirmCancelContact: 'Weet je zeker dat je dit contact wilt annuleren?',
        confirmCancelCounseling: 'Weet je zeker dat je deze begeleiding wilt annuleren?',
        confirmCancelReferral: 'Weet je zeker dat je deze verwijzing wilt annuleren?',
        confirmCancelRequest: 'Weet je zeker dat je deze aanvraag wilt annuleren?',
        confirmCancelSupervision: 'Weet je zeker dat je deze controle wilt annuleren?',
        confirmCloseProcess: 'Weet je zeker dat je {0} proces(sen) wilt sluiten?',
        confirmabortProcess: 'Definitief afbreken',
        consult: 'Consult',
        consultationResults: 'Uitslag',
        contactAdmin: 'Geef de runnummers door aan de administratie om de noodzakelijke correcties te laten uitvoeren.',
        contactWithRequesterOptionsHeader: 'Contact met de specialist',
        contactWithRequesterOptionsLabel: 'Contact mogelijkheden',
        contactWithRequesterOptionsPlaceholder:
            'Geef hier aan hoe en wanneer je te bereiken bent voor de specialist, voor eventuele vragen.',
        contactWithRequesterOptionsRequestedHeader: 'Contact met de aanvrager',
        counselingReports_notImplemented: 'Begeleiding rapporten niet geimplementeerd',
        createConsult: 'Maak consult aan',
        date: 'Datum',
        dateContact: 'Datum contact',
        dateSent: 'Verstuurd',
        declarations: 'declaratierecords aangemaakt in run',
        deleteContactModalText: 'Kies Ja als je het contact wilt verwijderen. Kies Nee als je het wilt behouden.',
        deleteContactModalTitle: 'Weet je zeker dat je het contact wilt verwijderen?',
        description: 'Omschrijving',
        diagnostic: 'Diagnostiek aanvraag',
        directProcedureAdded: 'Onderzoek gestart zonder aanvraag',
        draftReferral: 'Nieuwe verwijzing',
        editRecipient: 'Ontvanger aanpassen',
        editRequest: 'Aanvraag wijzigen',
        emptyClosedRequestsWorklist: 'Je hebt nog geen afgeronde taken',
        emptyColleagueRequestsWorklist: "Je collega's hebben geen openstaande taken",
        emptyMyRequestsWorklist: 'Je hebt geen openstaande taken',
        emptyOngoingRequestsWorklist:
            'Je hoeft niet te wachten op nieuwe resultaten, want er zijn op dit moment geen taken in behandeling bij andere partijen ',
        endHours: 'Eind uur',
        endMinutes: 'Eind minuten',
        endTime: 'Eind tijd',
        examinationResults: 'Conclusies',
        exportedRecords: 'Voor het afgebroken proces (PID: {0}) zijn al',
        feedbackProcessesClosed: '{0} proces(sen) zijn succesvol gesloten.',
        feedbackProcessesNotClosed: '{0} proces(sen) zijn niet gesloten wegens een verkeerde status.',
        files: 'Bestanden',
        fillLabValues: 'Vul hier relevante labwaarden in...',
        filterStatus: 'Filter op status:',
        fundingError: 'Bekostigingsfout',
        fundingErrorInformation: 'Afhandelinformatie',
        fundingErrorInformationAbbreviation: 'Afhandelinfo',
        fundingSetTo: 'Bekosting gezet op',
        goTo: 'Ga naar',
        headerMeasurement: 'Meting',
        hideConsult: 'Klap de vraag in',
        hideContact: 'Klap het contact in',
        hideCounseling: 'Klap de begeleiding in',
        hideDetails: 'Klap de details in',
        hideReferral: 'Klap de verwijsbrief in',
        hideRequest: 'Klap de aanvraag in',
        history: 'Geschiedenis',
        indication: 'Indicatie / Vraag',
        invitationSendTo: 'Uitnodiging verstuurd naar',
        invoiceDeclSurpressed: 'Fact. en decl. onderdrukt',
        invoices: 'facturatierecords aangemaakt in run',
        labResearchHeader: 'Laboratoriumonderzoek',
        lastUpdated: 'Laatste wijziging',
        mostRecentTasks: 'Meest recente open taken',
        myRequests: 'Mijn taken',
        new: 'Nieuwe aanvraag',
        newConsult: 'Nieuw consult',
        newCounseling: 'Nieuwe begeleiding',
        newPatient: 'Nieuwe patiënt',
        noConsumerForRequest: 'Er zijn geen ontvangende organisaties beschikbaar. Neem contact op met de Servicedesk.',
        noProcesses: 'Er zijn geen processen gevonden in je organisatie die voldoen aan de opgegeven criteria.',
        noProvider: 'Er zijn geen leveranciers voor deze dienst beschikbaar',
        noProviderOrganization: 'Geen dienst-leveranciers beschikbaar.',
        noProviderPractitioner: 'Geen zorgverlener beschikbaar voor deze dienst.',
        noProvidersForRequest: 'Geen zorgverleners beschikbaar, neem contact op met de Servicedesk.',
        noResultFoundForSearch: 'We hebben geen resultaat gevonden op basis van je zoekopdracht',
        noSearchResultWorklist: 'We hebben geen taken gevonden op basis van je zoekactie.',
        noSupervisorForRequest: 'Er zijn geen supervisors beschikbaar in jouw organisatie.',
        ongoingRequests: 'In behandeling',
        open: 'Open',
        openProcesses: 'Openstaande processen',
        openRequests: 'Actueel',
        patientEmail: 'E-mailadres patiënt',
        patientName: 'Patiënt',
        patientTasks: 'Poli taken',
        payoutDetails: 'Uitbetaalgegevens',
        payoutSetTo: 'Uitbetaling {0} gezet op',
        payouts: 'uitbetaalrecords aangemaakt in run',
        phoneNumberRequester: 'Telefoonnummer aanvrager',
        procedureState_unknown: 'Onbekend onderzoeksstatus',
        proceedWithOnlyAdditionalFiles:
            "We zien dat je nog geen fundusfoto's hebt toegevoegd. Wil je {0} alsnog versturen?",
        proceedWithoutFiles: 'We zien dat je nog geen bijlage hebt toegevoegd. Wil je {0} alsnog versturen?',
        processAlreadyBeingProcessed: 'Let op: Dit consult is al in behandeling genomen',
        processAlreadyCompleted: 'Let op: Dit consult is al afgerond',
        processAlreadyTakenByColleague: 'De {taskName} taak is al opgepakt door een collega',
        processDetails: 'Procesgegevens',
        processId: 'Id',
        processes: 'Processen',
        question: 'Vraag',
        questionPlaceholder: 'Stel je vraag',
        questionnairesNotStarted:
            'De patiënt heeft een vragenlijst ontvangen op ({0}) en is nog niet begonnen. Als je doorgaat kan de patient deze vragenlijst niet meer invullen.',
        questionnairesStartedAndFinished:
            'De patiënt heeft een vragenlijst ontvangen op {0} en deze afgerond. Weet je zeker dat je een nieuwe vragenlijst wilt sturen?',
        questionnairesStartedButNotFinished:
            'De patiënt heeft een vragenlijst ontvangen op {0} en is begonnen. Als je doorgaat kan de patient deze vragenlijst niet meer invullen.',
        reasonNoInvoiceDecl: 'Reden geen fact. en decl.',
        reasonNoInvoiceOrDeclaration: 'Reden geen bekostiging',
        reasonNoInvoiceOrDeclarationAbbreviation: 'Reden geen bekost.',
        recipient: 'Ontvanger',
        referralLetter: 'Verwijsbrief',
        referralReasons: 'Verwijsreden',
        referralResults: 'Rapporten',
        reportType_wrong: 'Verkeerd rapport type',
        reprocessDeclarations: 'Verwerk declaraties opnieuw',
        reprocessMessage:
            'De declaraties van de geselecteerde aanvragen zullen opnieuw verwerkt worden. Het kan enige tijd duren voor dit gereed is.',
        request: 'Aanvraag',
        requestConsult: 'Vraag consult aan',
        requestCriteria: 'Aanvraag criteria',
        requestDate: 'Aanvraagdatum',
        requestedBy: 'Aangevraagd bij',
        requester: 'Aanvrager',
        requesterSelectedRouting: 'De grader voor dit onderzoek is al bepaald door de aanvrager.',
        requests: 'Aanvragen',
        resendQuestionnaires: 'Herzenden vragenlijsten',
        resendQuestionnairesModalTitle: 'Herzenden FOSQ-10 en ESS vragenlijsten',
        saveSend: 'Verstuur',
        searchInPatientTasks: 'Zoek op naam, geboortedatum of taak',
        searchInWorklist: 'Zoek een taak op geboortedatum, patiëntnaam, zorgverlener of proces ID...',
        searchProcessIdTooltip: 'Zoek op procesId van een aanvraag',
        searchProcesses: 'Zoek processen',
        searchProcessesTooltip: 'Voor meedere processen, plak hier een met komma’s gescheiden PID-lijst',
        searchTextTooltip: 'Zoek op type aanvraag, beschrijving, geboortedatum, proces of patiëntnaam',
        selectCounselor: 'Selecteer een begeleider',
        selectGrader: 'Selecteer een beoordelaar',
        selectableOnOrganization: 'Alleen instelling kiesbaar',
        sendTo: 'Verstuur naar',
        sentTo: 'Verstuurd naar',
        serviceType_unknown: 'Onbekend dienstsoort',
        showAgenda: 'Toon agenda',
        showCancelled: 'Toon geannuleerd',
        showCompleted: 'Toon afgesloten',
        showConsult: 'Bekijk de hele vraag',
        showContact: 'Bekijk het contact',
        showCounseling: 'Bekijk de hele begeleiding',
        showDetails: 'Bekijk de details',
        showDraft: 'Toon concepten',
        showOrganization: 'Toon alles in huidige organisatie',
        showReferral: 'Bekijk de verwijsbrief',
        showRejected: 'Toon geweigerd',
        showRequest: 'Bekijk de hele aanvraag',
        showSuspended: 'Toon gestopt',
        startHours: 'Start uur',
        startMinutes: 'Start minuten',
        startTime: 'Start tijd',
        startWith: 'Ga aan de slag met',
        startWithOther: 'Ga aan de slag met je overige taken:',
        startedBy: 'Gestart door',
        status: 'Status',
        status_aborted: 'Afgebroken',
        status_accepted: 'Geaccepteerd',
        status_active: 'Actief',
        status_active_consult_recipient_closed: 'Rapport ontvangen',
        status_active_consult_recipient_open: 'Rapport verstuurd',
        status_active_consult_recipient_open_answer: 'Antwoord verstuurd',
        status_active_consult_recipient_open_question: 'Vraag ontvangen',
        status_active_consult_requester_closed: 'Rapport geaccepteerd',
        status_active_consult_requester_open: 'Rapport beschikbaar',
        status_active_consult_requester_open_answer: 'Antwoord ontvangen',
        status_active_consult_requester_open_question: 'Vraag verstuurd',
        status_active_discharge_recipient_closed: 'Ontslagbrief ontvangen',
        status_active_discharge_recipient_open: 'Ontslagbrief verstuurd',
        status_active_discharge_requester_closed: 'Ontslagbrief geaccepteerd',
        status_active_discharge_requester_open: 'Onstlagbrief beschikbaar',
        status_active_intake_recipient_closed: 'Intake ontvangen',
        status_active_intake_recipient_open: 'Intake verstuurd',
        status_active_intake_requester_closed: 'Intake geacepteerd',
        status_active_intake_requester_open: 'Intake beschikbaar',
        status_active_procedure_aborted: 'Onderzoek afgebroken',
        status_active_procedure_completed: 'Onderzoek afgerond',
        status_active_procedure_inProgress: 'Onderzoek bezig',
        status_active_procedure_report_recipient_closed: 'Rapport ontvangen',
        status_active_procedure_report_recipient_open: 'Rapport verstuurd',
        status_active_procedure_report_requester_closed: 'Rapport geaccepteerd',
        status_active_procedure_report_requester_open: 'Rapport beschikbaar',
        status_active_procedure_suspended: 'Onderzoek gestopt',
        status_cancelled: 'Geannuleerd',
        status_colleague: 'Ligt bij collega',
        status_completed: 'Afgesloten',
        status_draft: 'Concept',
        status_reason_aborted: 'Reden voor afbreken',
        status_reason_cancelled: 'Reden voor annulering',
        status_rejected: 'Geweigerd',
        status_requested_recipient: 'Te accepteren',
        status_requested_requester: 'Verstuurd',
        status_suspended: 'Niet actief',
        status_unknown: 'Onbekend rapport status',
        statusAction_aborted: 'Verzoek afbreken',
        statusAction_accepted: 'Accepteer',
        statusAction_active: 'Oppakken',
        statusAction_addIntakeReport: 'Maak intake-rapport',
        statusAction_addLetterOfDischarge: 'Maak ontslagbrief',
        statusAction_archiveReferral: 'Archiveer',
        statusAction_becomeRequesterPractitioner: 'In behandeling nemen',
        statusAction_cancelled: 'Annuleer',
        statusAction_changeService: 'Welk consult moet het worden?',
        statusAction_completed: 'Verzoek afsluiten',
        statusAction_convertToConsult: 'Zet om naar consult',
        statusAction_convertToReferral: 'Zet om naar verwijzing',
        statusAction_print: 'Print',
        statusAction_printReferral: 'Print verwijsbrief',
        statusAction_printRequest: 'Print aanvraag',
        statusAction_rejected: 'Wijs af',
        statusAction_requested: 'Versturen',
        statusAction_resendReferral: 'Verwijs opnieuw',
        statusAction_suspended: 'Stoppen',
        statusAction_takeFromColleague: 'Overnemen van collega',
        statusAction_takeFromOrganization: 'Oppakken',
        subsequentService: 'Volgt uit eerder proces',
        successabortProcess: 'Proces is afgebroken',
        supervisedBy: 'Gesuperviseerd door',
        supervision: 'Supervisie',
        supervisions: 'Supervisies',
        taskType: 'Type taak',
        typeAndChoose: 'Typ en kies uit de lijst...',
        typeOfConsult: 'Welke type consult moet het worden?',
        typeOfReferral: 'Welke type verwijzing moet het worden?',
        unpaid: 'Onbetaald',
        updated: 'Aangepast',
        viewAllTasks: 'Bekijk al je taken',
        whoIsTheRequester: 'Wie is de aanvrager?',
        worklist: 'Takenlijst',
        worklistPageHeader: 'Of ga verder met',
    },
    requestMessage: {
        appointmentConfirmation: 'Afspraak bevestiging',
        closeConsultation: 'Consult afsluiten',
        confirmation: 'Bevestiging',
        message: 'Bericht',
        messageForRequester: 'Bericht voor de aanvrager',
        multipleMessages: '{0} berichten',
        notifyWhen: 'Laat de aanvrager weten wanneer de afspraak is ingepland',
        optionallySendMessageToGP: 'Optioneel kun je bij het afsluiten nog een bericht sturen naar de huisarts',
        sendAMessage: 'Stuur een bericht',
        sendMessage: 'Stuur bericht',
        sendTo: 'Verstuur naar',
        singleMessage: '{0} bericht',
    },
    restEcgMeterSoftwareNamesEnum: {
        CardioPerfect: 'CardioPerfect',
    },
    service: {
        GGZBmind: 'Minddistrict',
        GGZBther: 'Therapieland',
        GGZOembl: 'Embloom',
        GGZOint: 'Interapy',
        activeServices: 'Actieve diensten',
        addRight: 'Rechten toevoegen',
        addService: 'Voeg een dienst toe',
        addServiceButton: 'Dienst toevoegen',
        compensation: 'Compensatie',
        compensationPayout: 'Vergoeding',
        compensationPayouts: 'Vergoedingen',
        consumingInOrganization: 'Afenemen in',
        contractFunding: 'Bekostiging middels contract',
        customControl: 'Custom control',
        customControlVersion: 'Custom control versie',
        details: 'Gegevens dienst',
        doNotDeclareSet: 'Niet declareren ingesteld',
        doNotDeclareSetTooltip: 'Ook geen patient facturatie',
        fileUpload: 'Bestanden toevoegen',
        icd10: 'ICD-10 uitvragen',
        inactiveServices: 'Inactieve diensten',
        isFundable: 'Bekostigen in nieuwe bekostigingssysteem',
        isFundableTooltip: 'Deze instelling bepaalt of de dienst bekostigd wordt in het nieuwe bekostigingssysteem',
        isStandaloneGraderNotAllowed: 'Losse Grader niet mogelijk',
        isStandaloneGraderNotAllowedTooltip:
            'Bij deze dienst moet de grader ook biometrist zijn opdat het onderzoek overgenomen kan worden door de grader',
        isSuppressNotifications: 'Onderdruk notificaties',
        isSuppressZorgmail: 'Onderdruk ZorgMail',
        ledgerNumber: 'Grootboekrekening',
        noDefault: 'Geen standaard dienst beschikbaar',
        noGeneralPractitionerInvolved: 'Geen inzage en terugkoppeling voor huisarts',
        noServices: 'Er zijn geen actieve diensten beschikbaar',
        noServicesSelected: 'Geen diensten geselecteerd',
        notificationOnCloseSubsequentService: 'Notificatie na opvolgende dienst',
        notificationOnCloseSubsequentServiceTooltip:
            'De notificatie van deze dienst wordt pas bij het sluiten van de opvolgende dienst verstuurd',
        provider: 'Dienstverlener',
        providingForOrganization: 'Levering in',
        rights: 'Rechten',
        runFundingAfterEachActivity: 'Doorloop de bekostiging na iedere afgeronde activiteit',
        runFundingAfterEachActivityTooltip:
            'Vink deze instelling aan wanneer DBC’s bekostigd moeten worden voordat het hele zorgpad is afgerond',
        sendZorgmailOnCloseSubsequentService: 'ZorgMail na opvolgende dienst',
        sendZorgmailOnCloseSubsequentServiceToolTip:
            'Het ZorgMail bericht van een dienst wordt pas bij het sluiten van de opvolgende dienst, naar het HIS verstuurd.',
        service: 'Dienst',
        serviceType: 'Soort dienst',
        serviceWorkflow: 'Dienst Workflow',
        services: 'Diensten',
        specialism: 'Specialisme',
        stopService: '{0} stopzetten',
        type: 'Type',
        type_consultation: 'Consultatie',
        type_contact: 'Contact',
        type_counseling: 'Begeleiding',
        type_examination: 'Onderzoek',
        type_inquiry: 'Aanvraag',
        type_invitation: 'Uitnodiging',
        type_other: 'Anders',
        type_referral: 'Verwijzing',
        type_referralSmall: 'Verwijzing',
        useMedicationRequest: 'Medicatie in aanvraag',
        useNewFundingSystem: 'Gebruik het nieuwe bekostigingssysteem',
        useNewFundingSystemTooltip:
            'Deze instelling bepaalt of voor deze dienst het oude of het nieuwe bekostigingssysteem wordt gebruikt',
        zorgmailType: 'ZorgMailberichttype',
    },
    serviceActivityType: {
        answer: 'antwoord',
        answerPronoun: 'dit antwoord',
        assessment: 'beoordeling',
        assessmentPronoun: 'deze beoordeling',
        consultation: 'consult',
        consultationPronoun: 'dit consult',
        evaluation: 'evaluatie',
        evaluationPronoun: 'deze evaluatie',
        examination: 'onderzoek',
        examinationPronoun: 'dit onderzoek',
        inquiry: 'aanvraag',
        inquiryPronoun: 'deze aanvraag',
        question: 'vraag',
        questionPronoun: 'deze vraag',
        referral: 'verwijzing',
        referralPronoun: 'deze verwijzing',
    },
    serviceConsumer: {
        checkIfEmptyOrganizationEmailField:
            'Wordt hier geen e-mailadres ingevuld dan wordt de notificatie verstuurd naar het algemene organisatie e-mailadres. Deze staat ingesteld op: ',
        compensation: 'Compensatie',
        compensationPayoutRequest: 'Uitbetaling aanvraag',
        consumer: 'Afnemer',
        consumers: 'Afnemers',
        createContact: 'Kan contact starten',
        createRequest: 'Kan aanvraag starten',
        editMessage: 'Toon overlappende configuratie',
        emailAddressOrganizationInbox: 'E-mailadres organisatie inbox',
        emailAddressOrganizationInboxAlt: 'Alternatief e-mailadres',
        emailAddressOrganizationInboxAndAlt: 'E-mailadres organisatie inbox & Alternatief e-mailadres',
        emptyGenericOrganizationEmailField: 'Er is geen algemene inbox voor de organisatie geconfigureerd',
        graderSelector: 'Kan grader selecteren',
        isAutoCloseRequest: 'Automatisch sluiten afgehandelde verzoeken',
        isKetenDbc: 'Keten-DBC vraag bij biometrie',
        noEmploymentChosen: 'Kies een betrekking om dit e-mailadres te weergeven',
        overlapMessage: 'Overlappende configuratie gevonden',
        restEcgMeterSoftwareName: 'Rust ECG meter software naam',
        sendFollowUpMeasurementGgz: 'Standaard nameting sturen',
        sendNotificationEmployment: 'Notificatie naar e-mailadres betrekking zorgverlener',
        sendNotificationEmploymentAlt: 'Notificatie naar alternatieve e-mailadres betrekking zorgverlener',
        sendNotificationOrganizationInbox: 'Notificatie naar organisatie inbox',
        sendNotificationPractitioner: 'Notificatie naar primair e-mailadres zorgverlener',
        sendNotificationPractitionerWhenDeviant: 'Alleen wanneer onderzoek afwijkend is',
        sendZorgMailOnExaminationRequest: 'ZorgMail bij aanvraag onderzoek',
        sendZorgMailOnReferralIntake: 'ZorgMail bij intake verwijzing',
        sendZorgMailOnReferralRequest: 'ZorgMail bij aanvraag verwijzing',
        serviceConsumer: 'Diensten afnemer',
        serviceConsumers: 'Diensten afnemers',
        serviceConsumersGroupSetting: 'Groepsinstellingen afnemers',
        serviceConsumersGroupSettings: 'Groepsinstellingen afnemers',
        spirometerSoftwareName: 'Spirometer software naam',
        usingRestEcgMeter: 'Gebruikt rust ECG meter',
        usingSpirometer: 'Gebruikt spirometer',
    },
    serviceConsumersGroupSettings: {
        addSupervisor: 'Voeg een Controleur toe',
        noSupervisor: 'Geen controleurs geselecteerd',
        overlapMessage: 'Overlappende configuratie gevonden',
        selectedSupervisor: 'Selecteer controleur voor de dienst',
        supervisor: 'Controleur',
        supervisors: 'Controleurs',
    },
    serviceDeclarationActivities: {
        activityType: 'Activiteittype',
        amount: 'Vergoeding',
        appliesWhenCustomControlDataQuery: 'Query',
        appliesWhenCustomControlDataQueryLabel: 'Query (oude bekostigingsmodule)',
        careProductType: 'Zorgproduct type',
        codeActiviteit: 'Activiteitcode',
        codeAfsluitreden: 'Afsluitreden',
        codeDiagnose: 'Diagnosecode',
        codeSpecialisme: 'Specialisme',
        codeZorgtype: 'Zorgtype',
        cpapTreatment: 'CPAP behandeling (nieuwe bekostigingsmodule)',
        defaultIcd10: 'Standaard ICD10 code',
        editMessage: 'Toon de overlappende declaratieregel',
        icd10Text: 'ICD10 code in declaratie record als deze niet voortkomt uit afhandeling van aanvraag',
        isHonorariumDeclareren: 'Honorarium declareren',
        isKostendeelDeclareren: 'Kostendeel declareren',
        overlapMessage:
            'Overlappende declaratieregel(s) gevonden. Meerdere geldige declaratieregels geven bij het declaratieproces ook meerdere declaraties. Dit is in sommige gevallen gewenst maar kan nu mogelijk niet de intentie zijn.',
        queryText:
            'Een query voor een specifiek gegeven met een bepaalde waarde in aanvraag, onderzoek of rapport. Veld kan leeg gelaten worden. Alleen van toepassing op de oude bekostigingsmodule.',
        spirogramIncludingReversibilityTest: 'Spirogram incl. reversibiliteitstest (nieuwe bekostigingsmodule)',
        uzoviMessage: 'Regel moet voor één of meer UZOVI codes gelden',
    },
    serviceDeclarationRules: {
        addDeclarationActivity: 'Declaratie-activiteit toevoegen',
        addDeclarationSetting: 'Declaratie-instelling toevoegen',
        careProductType: 'Zorgproducttype',
        declarationActivities: 'Declaratie-activiteiten',
        declarationActivity: 'Declaratie-activiteit',
        declarationSetting: 'Declaratie-instelling',
        declarationSettings: 'Declaratie-instellingen',
        exportUntilDate: 'Exporteer tot en met',
        exportUntilDateTooltip: 'Declaraties van processen aangemaakt na deze datum, worden niet geëxporteerd',
        insurerServiceDeclarationRule: 'Verzekeraar declaratieregel',
        insurerServiceDeclarationRules: 'Verzekeraar declaratieregels',
        serviceDeclarationRule: 'Declaratieregel',
        serviceDeclarationRules: 'Declaratieregels',
        supplementPriceActivity: 'Toeslagprijs',
    },
    serviceGraderPool: {
        addProvider: 'Voeg een beoordelaar toe',
        canGradeForAnalyst: 'Beoordeelt holter analyses voor',
        chooseOption: 'Maak een keuze',
        editMaximumGradings: 'Maximaal aantal beoordelingen wijzigen',
        editServiceGraderPool: 'Wijzig beoordelaars pool',
        maxPerDay: 'Max aantal per dag',
        maxPerMonth: 'Max aantal per maand',
        name: 'Pool naam',
        newServiceGraderPool: 'Nieuwe beoordelaars pool',
        noServiceGraderPoolProviders: 'Geen beoordelaars geselecteerd',
        perDay: 'Per dag',
        perMonth: 'Per maand',
        provider: 'Beoordelaar',
        selectSerivceGraderPoolProvider: 'beoordelaars in de pool',
        selectServiceGraderPool: 'Beoordelaars pool selecteren',
        serviceGraderPool: 'Beoordelaars pool',
        serviceGraderPoolDetails: 'Service beoordelaars pool details',
        serviceGraderPoolProvider: 'Service beoordelaars pool',
        toolTipText:
            'De analyses van de holteropnames worden door verschillende partijen gedaan. Selecteer hier van welke analysepartij graders in deze pool de analyses kunnen graden.',
    },
    serviceGroup: {
        counseling: 'Welk systeem gaat de organisatie gebruiken binnen begeleiding?',
        examination: 'Welk systeem gaat de organisatie gebruiken binnen onderzoek?',
    },
    serviceOrganizationRoleEnum: {
        dietetics: 'Diëtetiek',
        hospital: 'Ziekenhuis',
        lifestyleCoaching: 'Lifestyle coaching',
        policlinic: 'Polikliniek',
        psychology: 'Psychologie',
    },
    serviceOrganizationSetting: {
        canPerformGlaucoomExamination: 'OCT scan aanwezig voor glaucoom onderzoek',
        currentCareGroup: 'Huidige zorggroep',
        diagnosticEquipment: 'Diagnostische apparatuur',
        emailNotificationOnlyToOrganization: 'E-mailnotificaties alleen naar organisatie e-mailadres',
        emailNotifications: 'E-mailnotificaties',
        hideCareGroupLogbook: 'Zorggroep logboek verbergen',
        hideRequestedExaminationTaskFeature: 'Taken verbergen',
        holterAnalystOrganization: 'Analist organisatie',
        isRequestRecipient: 'Organisatie te selecteren als ontvanger',
        isSendingDeviceToPatientsAddress: 'Organisatie verstuurt holters naar huisadres patiënten',
        isUsingHideRequestedExaminationTaskFeature: 'Ingeplande taken verbergen in takenlijst',
        ksyosHolterAnalystOrganization: 'Ksyos analist organisatie',
        optoConnectPracticeId: 'Optoconnect id',
        organizationIsRequestRecipientForSubsequentService:
            'De organisatie moet de ontvanger zijn van de aanvraag, omdat deze in de vorige stap (in de geavanceerde instellingen) als ontvanger van de automatisch opvolgende dienst is ingesteld.',
        'organizationRole(s)': 'Organisatierol(len)',
        receiveZorgmailOnConsultOpen: 'Ontvangt zorgmail bij versturen consult',
        requestRecipientTitle: 'Ontvanger aanvraag',
        'role(s)OfOrganization': 'Rol(len) van organisatie',
        selectCareGroup: 'Zorggroep selecteren',
        selectOptoConnectPracticeId: 'Optoconnect id invullen',
        selectSoftware: 'Software selecteren',
        selectSomnologyOrganizationType: 'Type selecteren',
        sendBittiumRecordingsTo: 'Bittium opnames verzenden naar',
        sendingHolter: 'Opsturen holter',
        showCareGroupLogbook: 'Zorggroep logboek tonen',
        software: 'Software',
        somnologyCanReferPatientWithoutSmartphone: 'Kan patient verwijzen zonder smartphone',
        somnologyOrganizationType: 'Slaap organisatie type',
        validate: 'Validatie',
        validateAssetName: 'Validatie bestandsnamen',
        validationOnField: 'Validatie op',
        zorgMail: 'ZorgMail',
        zorgMailNumber: 'ZorgMail nummer',
    },
    serviceOrganizationSettingRoleDistributions: {
        addOrganization: 'Organisatie {0} toevoegen',
        cannotDeleteRole:
            'De rol {0} kan niet verwijderd worden bij deze organisatie, omdat deze diensten vervult voor {1}.',
        cannotDeleteServiceOrganizationSetting:
            'De dienst kan niet stopgezet worden, omdat er rollen verdeeld zijn bij {0}',
        distributeInEqualPercentages: 'Verdeel in gelijke percentages',
        distributionOrganizations: 'Verdeling organisaties',
        noPartnerOrganizationsAvailable: 'Geen partnerorganisaties beschikbaar',
        partnerOrganizations: 'Partnerorganisaties',
        percentageHigherThan0:
            'Een percentage moet hoger dan 0 zijn. Verwijder de organisatie uit de lijst of voer een percentage hoger dan 0 in.',
        percentageRequired: ' Vul percentages in voor de organisatie(s)',
        totalPercentageNot100:
            "De percentages moeten opgeteld 100% zijn. Controleer de ingevulde percentages of klik op 'Verdeel in gelijke percentages'.",
    },
    serviceProvider: {
        alreadyFinancedTooltip: 'Deze dienst wordt gratis geleverd als voorafgegaan door andere dienst',
        analyst: 'Analist',
        biometrician: 'Biometrist',
        canSelectCareProviderAsServiceConsumer: 'Kan ook huisarts als ontvanger selecteren',
        changedError: 'Fout opgetreden',
        changedSuccess: 'Succesvol aangepast',
        checkIfEmptyOrganizationEmailField:
            'Wordt hier geen e-mailadres ingevuld dan wordt de notificatie verstuurd naar het algemene organisatie e-mailadres. Deze staat ingesteld op: ',
        compensation: 'Compensatie',
        compensationPayout: 'Compensatie uitbetaling',
        compensationPayoutGlaucoma: 'Uitbetaling glaucoomonderzoek',
        compensationPayoutProcedure: 'Uitbetaling biometrie',
        compensationPayoutReport: 'Uitbetaling onderzoek/consult',
        compensations: 'Onderzoek / Consult & Uitbetalingen Biometrie',
        consumerSelector: 'Kan ontvanger selecteren',
        editMessage: 'Toon overlappende configuratie',
        emailAddressOrganizationInbox: 'E-mailadres organisatie inbox',
        emailAddressOrganizationInboxAlt: 'Alternatief e-mailadres',
        emailAddressOrganizationInboxAndAlt: 'E-mailadres organisatie inbox & Alternatief e-mailadres',
        emptyGenericOrganizationEmailField: 'Er is geen algemene inbox voor de organisatie geconfigureerd',
        grader: 'Grader',
        graderSelector: 'Kan grader selecteren',
        isAlternativeFinancing: 'Alternatieve bekostiging',
        isAnalyst: 'Levering analyse',
        isBiometrician: 'Levering biometrie',
        isGrader: 'Levering grading',
        isRequestRecipient: 'Is de ontvanger van de aanvraag',
        messageNoServiceProviderAndOrganizationRecipient:
            'Let op: zowel de organisatie als de zorgverleners kunnen geen aanvraag ontvangen',
        messageNoServiceProviderRecipient: 'Let op: Er is nu geen zorgverlener die de aanvraag kan ontvangen',
        messageOrganizationNoRecipient: 'Let op: Organisatie is nu geen ontvanger van aanvraag',
        messagePayout: 'De prijsafspraak voor uitbetalingen zijn op organisatieniveau vastgelegd',
        noEmploymentChosen: 'Kies een betrekking om dit e-mailadres te weergeven',
        optoConnectPracticeId: 'OptoConnect practiceID',
        organizationCompensations: 'Uitbetaling voor Onderzoek/Consult & Biometrie',
        overlapMessage: 'Overlappende configuratie gevonden',
        preferredConsumer:
            'Er moet minimaal één standaard afnemer gekozen worden als de dienst leverancier een ontvanger kan selecteren.',
        provider: 'Leverancier',
        providers: 'Leveranciers',
        receiveZorgmailOnConsultOpen: 'Ontvang ZorgMail bij beantwoorden consult',
        requestCreator: 'Kan onderzoeken starten',
        restEcgMeterSoftwareName: 'Rust ECG meter software naam',
        selectSpirometerSoftwareName: 'Selecteer spirometer software',
        sendBittiumRecordingsTo: 'Bittium opnames versturen naar',
        sendInvoice: 'Verstuur factuur',
        sendNotificationEmployment: 'Notificatie naar e-mailadres betrekking zorgverlener',
        sendNotificationEmploymentAlt: 'Notificatie naar alternatieve e-mailadres betrekking zorgverlener',
        sendNotificationOrganizationInbox: 'Notificatie naar organisatie inbox',
        sendNotificationPractitioner: 'Notificatie naar primair e-mailadres zorgverlener',
        serviceOrganizationSetting: 'Organisatie-instelling leverancier',
        serviceOrganizationSettings: 'Organisatie-instellingen',
        serviceProvider: 'Diensten leverancier',
        serviceProviders: 'Diensten leveranciers',
        somnologyOrganizationType: 'Slaap organisatie type',
        specializationProvider: 'Specialisatie zorgverlener',
        specializations: 'Specialisatie(s)',
        spirometerSoftwareName: 'Spirometer software naam',
        usingRestEcgMeter: 'Gebruikt rust ECG meter',
        usingSpirometer: 'Gebruikt spirometer',
    },
    serviceRightStopModal: {
        bodyEmployment:
            'Weet je het zeker? Wanneer je een zorgverlener op inactief zet, kan de zorgverlener geen werkzaamheden meer uitvoeren.',
        bodyEpisodeCategory:
            'Weet je het zeker? Wanneer je het zorgpad stopzet kan de zorgverlener geen werkzaamheden meer uitvoeren binnen alle actieve {0} diensten ({1}).',
        bodyOrganization:
            'Weet je het zeker? Wanneer je een organisatie archiveert, kunnen zorgverleners binnen de organisatie geen werk meer uitvoeren.',
        bodyRight:
            'Weet je het zeker? Wanneer je de rechten uitzet kan de zorgverlener geen werkzaamheden meer uitvoeren waar {0} {1} rechten voor nodig zijn',
        bodyServiceGroup:
            'Weet je het zeker? Wanneer je de dienst stopzet kan de zorgverlener geen werkzaamheden meer uitvoeren binnen {0}',
        serviceRightType: {
            ServiceConsumer: 'Aanvrager',
            ServiceProvider: 'Uitvoerder',
            ServiceReader: 'Inzage',
            ServiceSupervisor: 'Controleur',
        },
        titleEmployment: 'Zorgverlener inactief maken',
        titleMakeOrganizationInactive: 'Organisatie archiveren',
        titleRemoveRights: '{0} {1} rechten verwijderen',
        titleRight: '{0} {1} rechten uitzetten',
        titleServiceGroup: '{0} stopzetten',
        warningEmployment: 'Let op, de zorgverlener heeft mogelijk nog actieve processen.',
        warningOrganization: 'Let op, de organisatie heeft mogelijk nog actieve processen.',
        warningRight:
            'Let op, de zorgverlener heeft mogelijk nog actieve processen waar {0} {1} rechten voor nodig zijn.',
        warningRightServiceReader:
            'Let op, als de inzage rechten worden stopgezet, dan heeft de zorgverlener binnen heel het zorgpad {0} geen inzage meer.',
        warningServiceGroup: 'Let op, de zorgverlener heeft mogelijk nog actieve processen binnen {0}',
    },
    serviceSettings: {
        practitioners: {
            activeSince: 'Actief sinds',
            name: 'Naam',
            priceAgreement: 'Prijsafspraak',
            profession: 'Beroep',
            rights: 'Rechten',
        },
    },
    serviceTypeEnum: {
        consultation: 'Consultatie',
        contact: 'Contact',
        counseling: 'Begeleiding',
        examination: 'Onderzoek',
        inquiry: 'Aanvraag',
        invitation: 'Uitnodiging',
        other: 'Anders',
        referral: 'Verwijzing',
        referralSmall: 'Verwijzing OSAS',
    },
    setting: {
        settings: 'Instellingen',
    },
    sms: {
        dermaSnelOverdueToRecipientPractitionerReminder:
            'Een huisarts wacht op je beoordeling van een nieuw Snelconsult. Kun je ernaar kijken?',
    },
    somnologyOrganizationTypeEnum: {
        ksyosMedicalCenter: 'Ksyos Slaap',
        nsi: 'Nsi',
    },
    somnologyPatientTaskType: {
        choiceOfTreatment: 'Keuze behandeling',
        cpap: 'CPAP',
        cpapAanvraag: 'CPAP Aanvraag',
        cpapAnnualControl: 'CPAP, Jaarcontrole',
        cpapClassA: 'CPAP, Classificatie A',
        cpapClassB: 'CPAP, Classificatie B',
        cpapClassC: 'CPAP, Classificatie C',
        cpapClassD: 'CPAP, Classificatie D',
        cpapControlDoctor: 'CPAP, Controle arts',
        cpapEvaluation: 'CPAP, Evaluatie',
        cpapO2Measurement: 'CPAP, O2 meting',
        cpapOther: 'CPAP, Overig',
        followUpResearchFailed: 'Vervolgonderzoek mislukt',
        followUpResearchSuccessful: 'Vervolgonderzoek succesvol',
        lifestyle: 'Leefstijl',
        lifestyleControlDoctor: 'Leefstijl, Controle arts',
        lifestylePGControl: 'Leefstijl, Controle onderzoek',
        mra: 'MRA',
        mraAanvraag: 'MRA Aanvraag',
        mraClassA: 'MRA, Classificatie A',
        mraClassB: 'MRA, Classificatie B',
        mraClassC: 'MRA, Classificatie C',
        mraClassD: 'MRA, Classificatie D',
        mraOther: 'MRA, Overig',
        onderzoekWithoutProgress: 'Onderzoek zonder voortgang',
        pGControl: 'Controle onderzoek',
        planAppointment: 'Afspraak inplannen',
        practitionerControl: 'Controle arts',
        proefPeriodeAfbreken: 'Stopverklaring indienen',
        proefPeriodeContinueren: 'Proefperiode continueren',
        proefPeriodeSuccesvol: 'Akkoordverklaring indienen',
        questionFromMedicalSpecialist: 'Vraag van medisch specialist',
        reference: 'Verwijzing',
        remaining: 'Overig',
        remainingOther: 'Overig',
        researchFailed: 'Onderzoek mislukt',
        researchSuccessful: 'Onderzoek succesvol',
        spt: 'SPT',
        sptAanvraag: 'SPT Aanvraag',
        sptAnnualControl: 'SPT, Jaarcontrole',
        sptClassA: 'SPT, Classificatie A',
        sptClassB: 'SPT, Classificatie B',
        sptOther: 'SPT, Overig',
        sptPGControl: 'SPT, Controle onderzoek',
        zDIntake: 'ZorgDomein intake',
    },
    specialismCodeProfession: {
        '0301': 'Oogarts',
        '0302': 'KNO-arts',
        '0310': 'Dermatoloog',
        '0313': 'Internist',
        '0320': 'Cardioloog',
        '0322': 'Longarts',
        '0326': 'Allergoloog',
        '0329': 'Psychiater',
        '1900': 'Audioloog',
    },
    spirometerSoftwareNamesEnum: {
        CardioPerfect: 'CardioPerfect',
        CareFusion: 'CareFusion',
    },
    stopServiceOrganizationSettings: {
        activeProcessesWarning: 'Let op, er zijn mogelijk nog actieve processen binnen {0} bij {1}',
        stopAll: 'Alle {0} diensten stopzetten',
        sureToStop:
            'Weet je het zeker? Wanneer je {0} stopzet kunnen zorgverleners binnen de organisatie geen werkzaamheden meer uitvoeren binnen {1}.',
        theCarePath: 'het zorgpad',
        theService: 'de dienst',
    },
    subsequentService: {
        autoSubsequentServices: 'Auto opvolgende diensten',
        isDeviation: 'Alleen bij afwijking',
        noOrganizationsAvailable: 'Geen dienst-leveranciers beschikbaar',
        noProvidersAvailable: 'Er zijn geen providers beschikbaar, probeer een andere organisatie',
        providerOrganization: 'Organisatie leverancier',
        providerPractitioner: 'Zorgverlener',
        selectOrganization: 'Selecteer een leverancier organisatie voor de volgende dienst',
        selectProvider: 'Selecteer een leverancier voor de dienst voor de volgende dienst',
        subsequentService: 'Opvolgende dienst',
        subsequentServiceNeedsToBeActive:
            'Alleen wanneer de zorgverlener de rechten heeft voor een opvolgende dienst kan deze dienst worden opgestart',
        subsequentServices: 'Opvolgende diensten',
    },
    supervision: {
        assessmentBiometrics: 'Onderzoek biometrie',
        biometrics: 'meting',
        diagnosticReportRemark: 'Grader opmerking',
        edit: 'Bewerk controle',
        findings: 'Bevindingen controleur',
        grading: 'bevinding',
        hideSupervision: 'Klap de controle in',
        list: 'Controle aanvragen',
        percentage: 'Controle percentage',
        percentageBiometrician: 'Controle percentage Biometrist',
        percentageGrader: 'Controle percentage Grader',
        procedureRemark: 'Biometrist opmerking',
        qualityCheck: 'Kwaliteit meting',
        qualityProcedure: 'Kwaliteitscontrole biometrie',
        qualityReport: 'Kwaliteitscontrole bevindingen',
        remarkBiometrics: 'Toelichting kwaliteitscontrole biometrie',
        remarkGrading: 'Toelichting kwaliteitscontrole bevinding',
        remarksProcedure: 'Toelichting biometrist',
        showSupervision: 'Bekijk de hele controle',
        start: 'Controle starten',
        supervision: 'Kwaliteitscontrole',
        takeOver: 'Controle overnemen',
    },
    supervisors: {
        checkIfEmptyOrganizationEmailField:
            'Wordt hier geen e-mailadres ingevuld dan wordt de notificatie verstuurd naar het algemene organisatie e-mailadres. Deze staat ingesteld op: ',
        compensationPayOut: 'Compensation payout',
        editMessage: 'Toon overlappende configuratie',
        emailAddressOrganizationInbox: 'E-mailadres organisatie inbox',
        emailAddressOrganizationInboxAlt: 'Alternatief e-mailadres',
        emailAddressOrganizationInboxAndAlt: 'E-mailadres organisatie inbox & Alternatief e-mailadres',
        emptyGenericOrganizationEmailField: 'Er is geen algemene inbox voor de organisatie geconfigureerd',
        noEmploymentChosen: 'Kies een betrekking om dit e-mailadres te weergeven',
        overlapMessage: 'Overlappende configuratie gevonden',
        payout: 'Uitbetaling',
        sendNotificationEmployment: 'Notificatie naar e-mailadres betrekking zorgverlener',
        sendNotificationOrganizationInbox: 'Notificatie naar organisatie inbox',
        sendNotificationPractitioner: 'Notificatie naar primair e-mailadres zorgverlener',
        supervisingService: 'Supervisie dienst',
        supervisingServices: 'Supervisie diensten',
        supervisor: 'Supervisor',
        supervisorNeededInfoTooltipText:
            'Let op, als er een supervisor wordt gekozen, dan is de AGB-code van de supervisor leidend. Indien de AGB-code van de gebruiker niet toegestaan is voor consultatie, dient er een supervisor geselecteerd te worden met een geldige AGB-code (bijv. een huisarts of psychiater).',
        supervisors: 'Supervisors',
    },
    supervisorsAssessmentBiometricsEnum: {
        good: 'Goed',
        moderate: 'Matig',
        poor: 'Slecht',
    },
    support: {
        anythingElse: 'Wil je nog iets kwijt? (Vermeld geen patiëntgegevens)',
        body: 'Onze servicedesk staat voor je klaar!',
        contactServiceDesk: 'Neem contact op met de Servicedesk.',
        exportFileColumns: {
            analysisCompletedDate: 'Analyse Afgerond Datum',
            clarificationQARequesterSentDateTime: 'Datum Vraag Verstuurd',
            dateSent: 'Datum Aanvraag Verzonden',
            diaryType: 'Dagboek Type',
            episodeCategoryName: 'Zorgpad',
            graderName: 'Grader Naam',
            holterAnalystOrganizationName: 'Analist Organisatie',
            holterSendDate: 'Holter Verstuurdatum',
            holterSetDate: 'Instel Datum Holter',
            numberOfDaysActive: 'Aantal Dagen Open',
            performerOrganizationName: 'Biometrist Organisatie Naam',
            performerOrganizationTxcsId: 'Biometrist Organisatie Id',
            processId: 'PID',
            recipientOrganizationName: 'Uitvoerende Organisatie Naam',
            recipientOrganizationTxcsId: 'Uitvoerende Organisatie Id',
            recipientPractitionerName: 'Uitvoerende Specialist Naam',
            recipientPractitionerUid: 'Uitvoerende Specialist Id',
            recorderType: 'Recorder Type',
            requesterOrganizationName: 'Aanvragende Organisatie Naam',
            requesterOrganizationTxcsId: 'Aanvragende Organisatie Id',
            requesterPractitionerName: 'Aanvrager Naam',
            requesterPractitionerUid: 'Aanvrager Id',
            responseAnalyse: 'Response Analyse',
            responseGrading: 'Response Grading',
            serviceName: 'Dienst',
            startUploadDate: 'Start Upload Datum',
            status: 'Status (CS)',
            uploadProgress: 'Upload (%)',
        },
        feedback: 'Feedback',
        giveFeedback: 'Feedback geven',
        happyForFeedback: 'Ksyos is erg blij met je feedback!',
        header: 'Zelf regelen',
        openingHours: 'Maandag - vrijdag van 8:30 - 17:00 uur',
        rateQuestion: 'Hoe beoordeel je het Ksyos platform?',
        send: 'Verstuur',
        sendEmail: 'Of stuur een e-mail naar info@ksyos.nl',
        submit: 'Verzenden',
        submitted: 'We hebben je vraag ontvangen en nemen zo snel mogelijk contact met je op',
        support: 'Hulp nodig?',
        supportTeamviewerBody:
            'Om op jouw scherm te kunnen meekijken, vraagt onze Servicedeskmedewerker je om TeamViewer te installeren.',
        supportTeamviewerHeader: 'Wil je dat we even meekijken?',
        thanks: 'Dank je wel!',
        yourQuestion: 'Stel je vraag hier...',
    },
    tcohProcedureTypeEnum: {
        eventEcg: 'Event',
        holter: 'Holter',
    },
    therapieland: {
        saveTreatments: 'Opslaan',
        tooltipTextBegeleiding:
            'Je collega is begeleider van deze patiënt. Daarom kun je niet de voortgang in Therapieland bekijken.',
        typeToFindTreatments: 'Start met typen om het juiste programma te vinden',
    },
    thirdParty: {
        apiToken: 'API token',
        baseUrl: 'Base url',
        connectionFailed: 'Mislukt',
        connectionOk: 'Test ok',
        secretKey: 'Secret key',
        testConnection: 'Test verbinding',
    },
    ticketTypeEnum: {
        jiraTicket: 'Jira ticket nummer',
        oplossenKlacht: 'Oplossen klacht ticket nummer',
        perfectviewTicket: 'Perfectview ticket nummer',
    },
    ticketTypeEnumShort: {
        jiraTicket: 'Jira',
        oplossenKlacht: 'Oplossen klacht',
        perfectviewTicket: 'Perfectview',
    },
    timeout: {
        login: 'Log opnieuw in',
        messageLoggedOut:
            'Doordat je {0} minuten niet actief bent geweest in het platform hebben we je automatisch uitgelogd. Log opnieuw in om verder te gaan waar je gebleven was.',
        messageSessionWarning:
            'Wanneer je {0} minuten niet actief bent geweest in het platform word je automatisch uitgelogd. Druk op de knop om te voorkomen dat je uitgelogd wordt.',
        reset: 'Blijf ingelogd',
        titleLoggedOut: 'Je sessie is verlopen',
        titleSessionWarning: 'Je sessie is bijna verlopen',
    },
    toast: {
        answerSendSuccess: 'Je antwoord is verstuurd',
        answerSentToOrganizationSuccess: 'Je antwoord is verstuurd naar {0}',
        cancelDefaultMessage: 'Geannuleerd',
        cancelExistingSuccess: 'Het bewerken van je {0} is geannuleerd',
        cancelModificationSuccess: 'Correctie geannuleerd',
        cancelSuccess: 'Je {0} is geannuleerd',
        changesSaveSuccess: 'Je veranderingen zijn opgeslagen',
        changesSaveWarning: 'Er is een fout opgetreden bij het opslaan van je veranderingen',
        clarificationSendSuccess: 'Je reactie is verstuurd',
        confirmedRequestDefaultMessage: 'Het formulier is afgesloten',
        confirmedRequestDefaultMessageAndSentToHis:
            'Het formulier is afgesloten, we versturen de uitslag ook naar je HIS',
        consentSaved: 'Toestemmingen opgeslagen',
        consultRequestSubmit: 'Het consult is verstuurd naar {0}. Volg de status via je taken > in behandeling',
        consultTypeChanged: 'Het consult is omgezet naar een {0} consult',
        convertConsult: 'De resultaten van het onderzoek zijn gekoppeld aan je consult',
        copyTextSuccess: 'Tekst is gekopieerd naar het klembord',
        draftDefaultMessage: 'Bewaard als concept, je vindt het terug in je takenlijst',
        draftSuccess: 'Je {0} is bewaard als concept, je vindt het terug in je takenlijst',
        duplicatedMdoPatient: 'Je kunt een patiënt niet twee keer aan hetzelfde MDO toevoegen',
        modificationSuccess: 'Correctie succesvol doorgevoerd',
        procedureSubmitted: 'Je {0} is verstuurd ter beoordeling',
        questionSendSuccess: 'Je vraag is verstuurd',
        questionnaireScheduled: 'Vragenlijst succesvol ingepland',
        removeSuccess: 'Je {0} is verwijderd',
        removeSuccessDefault: 'Verwijderd',
        requestConfirmed: 'Je {0} is afgesloten',
        requestConfirmedAndSentToHis: 'Je {0} is afgesloten, we versturen de uitslag ook naar je HIS',
        requestMessageSend: 'Het bericht is verstuurd naar {0}',
        savedSuccess: 'Je {0} is bewaard',
        sentSuccess: 'Je {0} is verstuurd',
        submitDefaultMessage: 'Het formulier is verstuurd',
        submitExaminationToOphthalmologist: 'Je onderzoek is naar de oogarts verstuurd',
        submitSuccess: 'Je {0} is verstuurd, volg de status via je takenlijst',
        submitSuccessSimple: 'Je {0} is afgerond',
    },
    toesturenKsyosMedicalCenter: {
        bewegen: 'Bewegen',
        leefstijl: 'Leefstijl',
        lekker_slapen: 'Lekker slapen',
        ontspanning: 'Ontspanning',
        piekeren: 'Piekeren',
        slaap_en_shifts: 'Slaap en Shifts',
        slaaprestrictie: 'Slaaprestrictie',
    },
    tooSupervision: {
        assessmentBiometrics: "Beoordeling foto's",
        assessmentCompleteness: 'Volledigheid aangeleverde informatie',
        assessmentDiagnosis: 'Kwaliteit van de voorlopige diagnose',
        assessmentQuestion: 'Kwaliteit van de vraagstelling',
        feedbackTitle: 'Feedback kwaliteit onderzoek',
        remarkBiometrics: 'Toelichting kwaliteitscontrole biometrie',
        remarkGrading: 'Toelichting kwaliteitscontrole bevindingen',
        supervisionAlert:
            "Er is feedback gegeven op het onderzoek en de bevindingen. Je vindt dit terug onder 'Feedback kwaliteit onderzoek'.",
    },
    twoFactorAuthenticationType: {
        none: '(Nog) geen',
        verificationApp: 'Verificatieapp',
        verificationDevice: 'Hardware Token',
    },
    txcs: {
        conflict: 'Er zijn verschillende gegevens gevonden',
        conflictIdentifierSocialSecurity: 'Toe te kennen BSN',
        conflictIdentifierSocialSecurityText:
            'De patiënt die je hebt geselecteerd heeft nog geen BSN die bij Ksyos bekend is. We kennen het onderstaande BSN uit je HIS toe aan deze patiënt.',
        conflictText:
            'De gegevens uit het HIS en in Ksyos komen niet overeen, kies a.u.b. welke gegevens je wilt aanhouden:',
        fromHIS: 'In het HIS:',
        inKSYOS: 'Bij Ksyos bekend:',
        matchingPatientInformation: 'Deze patientgegevens zijn hetzelfde:',
        sbvzError: 'De persoonsgegevens van deze patiënt konden niet gevonden worden',
        selectCorrectInformation: 'Je kunt hieronder de juiste gegevens selecteren',
        selectPractitioner: 'Bepaal de huisarts',
        vecozoError: 'De verzekeringsgegevens konden niet gevonden worden.',
    },
    unavailableServices: {
        dermasnelc: {
            closedOutsideOfBusinessHours: {
                alertAndTooltip:
                    'Het snelconsult is alleen beschikbaar op werkdagen tussen 8:00 en 17:00. Je kunt wel een regulier consult starten nu.',
            },
        },
        dermasnelcsc: {
            closedOutsideOfBusinessHours: {
                alertAndTooltip:
                    'Het snelconsult is alleen beschikbaar op werkdagen tussen 8:00 en 17:00. Je kunt wel een regulier consult starten nu.',
            },
        },
        holter: {
            youngerThanEighteen: {
                alertAndTooltip: 'Onze cardiologen beoordelen geen holteronderzoeken van patiënten onder de 18 jaar',
            },
        },
        osas: {
            youngerThanEighteen: {
                alertAndTooltip: 'Onze slaapartsen beoordelen geen onderzoeken van patiënten onder de 18 jaar',
            },
        },

        respialler: {
            youngerThanEighteen: {
                alertAndTooltip: 'Onze allergologen beoordelen geen onderzoeken van patiënten onder de 18 jaar',
            },
        },
        tcch: {
            youngerThanEighteen: {
                alertAndTooltip: 'Onze cardiologen beoordelen geen consulten van patiënten onder de 18 jaar',
            },
        },
        tccr: {
            youngerThanEighteen: {
                alertAndTooltip: 'Onze cardiologen beoordelen geen consulten van patiënten onder de 18 jaar',
            },
        },
        tcoh: {
            youngerThanEighteen: {
                alertAndTooltip: 'Onze cardiologen beoordelen geen holteronderzoeken van patiënten onder de 18 jaar',
            },
        },
        tcor: {
            youngerThanEighteen: {
                alertAndTooltip: 'Onze cardiologen beoordelen geen onderzoeken van patiënten onder de 18 jaar',
            },
        },
    },
    units: {
        packYears: 'pakjaren',
    },
    upload: {
        busyUploading: 'Bezig met uploaden...',
        chooseFile: 'Kies bestand',
        dhd: 'Importeer Dutch Hospital Data',
        noCsvFormat: 'Geen CSV formaat',
        orStartTheCardiograph: 'Of start nu de cardiograaf',
        orStartTheSpirometer: 'Of start nu de spirometer',
        renew: 'Nieuw',
        selectCsvFile: 'Selecteer CSV-bestand',
        startOrAddECG: 'Start een nieuw Rust ECG of voeg een eerder ECG toe',
        startRestEcg: 'start een rust ECG opname',
        startSpirogram: 'start een spirogram opname',
        upload: 'Uploaden',
        uploadToAnalyst: 'Versturen naar analist',
        uploading: 'Uploaden',
        uzovi: 'Importeer UZOVI Data',
        uzoviImported: 'Imported Records',
        viaKsyosDesktop: 'via Ksyos desktop',
        wrongFileFormat: "Het bestand '{0}' is van het verkeerde formaat",
        wrongFirstRow: 'Eerste rij bevat niet "Exact Relatienummer;TxCS ID nr;"',
        wrongTxcsId:
            "Er is in CS geen organisatie met TxCS ID nr: '{0}'. Ga na waarom niet. Verwijder deze organisatie zonodig uit de te importeren lijst",
    },
    uzovi: {
        add: 'Voeg UZOVI toe',
        code: 'UZOVI code',
        codeOrName: 'Code of naam',
        label: 'Label',
        stored: 'Niet beschikbaar',
        uzovi: 'UZOVI',
        uzovis: 'UZOVIs',
    },
    validation: {
        EXTERNALID_EXISTS: 'Dit serienummer bestaal al',
        IDENTIFIER_DECLARATION_EXISTS: 'Dit nummer is reeds in gebruik',
        IDENTIFIER_REGISTRATION_BIG_EXISTS: 'Dit nummer is reeds in gebruik',
        IDENTIFIER_REGISTRATION_KP_EXISTS: 'Dit nummer is reeds in gebruik',
        NO_MATCH_DATE: 'Geboortedatum komt niet overeen met BSN',
        NO_MATCH_DATE_BSN: 'Combinatie BSN en geboortedatum bestaat niet',
        NO_PASSWORD: 'Kies een wachtwoord om verder te gaan',
        NO_VALID_INSURANCE: 'Geen geldige verzekering',
        OTP_SECRET_DOES_NOT_MATCH_TOTP_TOKEN: 'Onjuiste Google Authenticator code',
        REGISTRATION_CODE_EXPIRED: 'Je registratiecode is verlopen',
        RELATION_NUMBER_EXISTS: 'Dit nummer is reeds in gebruik',
        REQUIRED: 'Verplicht',
        SHORT_PASSWORD: `Je wachtwoord moet minimaal uit ${MINIMUM_PASSWORD_LENGTH} karakters bestaan`,
        USERNAME_EXISTS: 'Deze gebruikersnaam is reeds in gebruik',
        USERNAME_NOT_FOUND: 'Deze gebruikersnaam is niet bij ons bekend. Gebruikersnaam vergeten?',
        VERY_SHORT_PASSWORD: `Je wachtwoord moet minimaal uit ${MINIMUM_PASSWORD_LENGTH} karakters bestaan`,
        WRONG_PASSWORD: 'De combinatie van wachtwoord en gebruikersnaam klopt niet.',
        WRONG_PASSWORD_WARNING: 'De combinatie van wachtwoord en gebruikersnaam klopt niet.',
        WRONG_REGISTRATION_CODE: 'Onjuiste registratiecode',
        WRONG_TOTP_TOKEN: 'Onjuiste Google Authenticator code',
        agbCodeRequired: 'Moet een getal met lengte van 8 cijfers bevatten',
        agbStartDateMismatch: 'De startdatum valt voor de startdatum van {0} in Vektis',
        any_allowOnly: 'Vul een geldige waarde in',
        any_custom: 'Kies één van de opties',
        any_empty: 'Vul alsjeblieft dit veld in',
        any_emptySupport: 'Vul alsjeblieft dit veld in',
        any_invalid: 'De ingevulde waarde is incorrect',
        any_only: 'Dit is een verplicht veld',
        any_required: 'Vul alsjeblieft dit veld in',
        any_selected: 'Kies één van de opties',
        array_base: 'Selecteer tenminste één optie',
        array_length: 'Moet op zijn minst 1 item bevatten',
        array_max: 'Selecteer maximaal {{#limit}} items',
        array_min: 'Selecteer tenminste 1',
        asset_required: 'Je moet nog een bestand toevoegen',
        bmi: 'Moet een getal zijn tussen {min} en {max}',
        boolean_base: 'Kies één van de opties',
        bothTimesRequired: 'Vul zowel de start- als eindtijd in',
        bsnAlreadyExists: 'BSN is al gekoppeld aan een patiënt',
        bsnIsNeededToContinueOork: 'Heeft de patiënt geen BSN? Dan kan de keuring niet via Ksyos worden gefaciliteerd.',
        changeEmailAddress: 'Je hebt het e-mailadres niet aangepast. Pas het eerst aan voordat je opnieuw verstuurt',
        chooseCurrentDateOrBefore: 'Kies de huidige datum of een datum in het verleden',
        date_afterEndServiceRight: 'De gekozen datum mag niet na de einddatum van de dienst liggen',
        date_base: 'Moet een datum zijn',
        date_beforeStartServiceRight: 'De gekozen datum mag niet voor de startdatum van de dienst liggen',
        date_future: 'Datum mag niet in de toekomst liggen',
        date_past: 'Datum mag niet in het verleden liggen',
        date_valid: 'Datum moet geldig zijn',
        dateOverlap: 'Datum overlapt met andere periode',
        datePeriodInvalid: 'De periode is ongeldig, de einddatum moet later zijn dan de startdatum.',
        datePeriodPast: 'Datumperiode mag niet in het verleden liggen',
        dateRanges_hasOverlap: 'Overlappende periodes zijn niet toegestaan',
        dateRanges_noActiveItem: 'Er moet minimaal 1 actieve periode zijn',
        duplicateAgbCode: 'Deze AGB-code is al geregistreerd in het Ksyos platform',
        duplicateEmailTherapieland:
            "Dit e-mailadres wordt al door iemand anders gebruikt om programma's van Therapieland te ontvangen. Kies een ander e-mailadres om door te gaan.",
        duplicateTreatment: 'De patiënt heeft dit programma al een keer doorlopen.',
        duplicateTreatmentModal:
            "Een of meerdere programma's in deze begeleiding zijn al een keer verstuurd naar dit e-mailadres. Kies een ander e-mailadres om de programma's naar te versturen.",
        duplicatedEmailTherapieland: 'Dit e-mailadres is al in gebruik. Kies een ander e-mailadres om verder te gaan.',
        duplicatedUserName: 'Gebruikersnaam is al in gebruik',
        email_required: 'Er moet een e-mailadres worden ingevuld',
        endTime_required: 'Kies een eindtijd',
        endTimeBeforeStartTime: 'De eindtijd kan alleen later zijn dan de starttijd',
        enter_question: 'Voer je vraag in',
        excelParser: {
            any_invalid: 'Geen geldige waarde',
            columnsNotFound: "De volgende kolommen konden niet gevonden worden: '{0}'",
            excelContainsMultiplePatientsWithSameBsn: 'Excel bevat patiënten met hetzelfde BSN',
            fileSizeMustBeLessThan: 'De bestandsgrootte moet minder dan {0} MB zijn',
            groupWithIdentificationNumberNotFound: 'Geen groep met identificatie-nummer "{0}" gevonden',
            groupWithNameNotFound: 'Geen groep met naam "{0}" gevonden',
            identifierDeclarationRequired: 'Geen geldige AGB code',
            invalidFileText:
                'Het bestand dat je probeert te uploaden is niet toegestaan. Je kunt hier alleen Excel bestanden uploaden.',
            invalidFileTitle: 'Bestand toevoegen is mislukt',
            koi_date: 'Datum niet in het juiste formaat (dd-mm-jjjj)',
            koi_enum: 'Geen geldige waarde',
            multipleGroupsWithSameNameFound: 'Er zijn meerdere groepen met AGB-code "{0}" gevonden',
            multipleOrganizationsWithSameAgbCodeFound: 'Er zijn meerdere organisaties met AGB-code "{0}" gevonden',
            number_base: 'Geen geldig getal',
            organizationWithAgbCodeNotFound: 'Geen organisatie met AGB code "{0}" gevonden',
            string_base: 'Geen geldige tekst',
            string_elfproef: 'Geen geldige BSN',
            string_email: 'Ongeldig e-mailadres',
            string_length: 'Text heeft niet de juiste lengte',
            vipLiveReferralDateOlderThanOneYear: 'De verwijsdatum mag niet ouder zijn dan 1 jaar',
            worksheetNotFound: 'Werkblad "{0}" niet gevonden',
        },
        expiredAgb: 'Deze AGB-code is verlopen',
        failedToGetUpdatedInvitationProgress:
            'Voortgang kan niet volledig worden getoond. Ga naar Therapieland om de voortgang te bekijken.',
        failedToSendAllInvitations:
            "Het versturen van de programma's naar de patiënt is mislukt. Probeer het opnieuw door een nieuwe begeleiding te starten. Lukt dat niet? Neem dan contact op met Ksyos.",
        failedToSendInvitation:
            'Het versturen van dit programma naar de patiënt is mislukt. Probeer het opnieuw door een nieuwe begeleiding te starten. Lukt dat niet? Neem dan contact op met Ksyos.',
        fev1: 'Moet een getal zijn tussen 0 en 9 met twee decimalen',
        fileRequired: 'Upload een bestand',
        fundusScreening: {
            any_only: 'moet een van de volgende waarden hebben {{#valids}}',
            array_max: 'mag {{#limit}} item(s) bevatten',
            date_base: 'moet een datum zijn',
            number_base: 'moet een getal zijn',
            numberAsString_notANumber: 'moet een getal zijn',
            numberAsString_notAString: 'moet een getal zijn',
            string_base: 'moet een tekst zijn',
            string_empty: 'moet een tekst zijn',
        },
        general: 'Er is iets mis gegaan, probeer het alstublieft nogmaals.',
        graderRequired: 'Grader is verplicht',
        greater: '"{0}" moet groter zijn dan "{1}"',
        height: 'Moet een getal zijn tussen {min} en {max}',
        identifierDeclarationOrReferenceRequired:
            'Voor dit beroep is de AGB-code of het gebruik van de AGB-code van een collega verplicht',
        identifierDeclarationOrSupervisorRequired: 'Vul of een AGB-code in of selecteer een supervisor',
        identifierDeclarationRequired: 'Moet een getal met lengte van 8 cijfers bevatten',
        identifierDeclarationRequiredToRetrieveFundusPatientList:
            'Vul de AGB-code in om de fundus patiëntenlijst bij HealthConnected op te kunnen halen',
        identifierRegistrationBigNumberRequiredBackend: 'Moet een getal met lengte van 11 cijfers bevatten',
        identifierRegistrationBigNumberRequiredFrontend:
            'Moet een getal bevatten met een lengte tussen 9 en 11 cijfers',
        identifierRegistrationKpNumberRequired: 'Moet een getal met lengte van 11 cijfers bevatten',
        image_required: 'Je moet hier nog een plaatje selecteren',
        invalidAgb: 'Deze AGB-code is ongeldig',
        invalidPhoneNumber: 'Het telefoonnummer lijkt niet te kloppen. Controleer het nummer.',
        invalidTime: 'De tijd is ongeldig',
        koi_date: 'Je moet de datum (dd-mm-jjjj) invullen',
        koi_dateSameOrBefore: 'Datum mag niet in de toekomst liggen',
        koi_endDate: 'Einddatum kan niet eerder zijn dan de startdatum',
        koi_enum: 'Kies één van de opties',
        koi_isNotToday: 'Datum moet vandaag zijn',
        koi_missingStartDate: 'Begindatum ontbreekt',
        koi_time: 'Je moet de tijd invullen',
        koi_timeWithoutSeconds: 'Je moet de tijd invullen',
        koi_todayOrAfter: 'Datum mag niet in het verleden liggen',
        koi_todayOrBefore: 'Datum mag niet in de toekomst liggen',
        min: '"{0} moet groter of gelijk zijn aan "{1}"',
        minOneHourTimePeriodRequired: 'Vul minimaal een tijdsperiode van één uur in',
        noCurrentServiceOrganizationGroupEmbloom:
            'Er is geen huidige groep gekozen. Daarom kan er geen gebruik gemaakt worden van Embloom',
        noMatchDateBsn:
            'We kunnen geen patiënt vinden met deze combinatie van geboortedatum en BSN. Controleer je invoer.',
        noMatchDateBsnTextCreate:
            'De combinatie BSN en geboortedatum komt niet overeen. Weet je zeker dat je deze patiënt wilt aanmaken?',
        noMatchDateBsnTextUpdate:
            'De combinatie BSN en geboortedatum komt niet overeen. Weet je zeker dat je de patiëntgegevens wilt overschrijven?',
        noMatchPatientFilters: 'Geen patiënt gevonden in jouw organisatie.',
        noServiceGraderPoolProviders: 'Geen beoordelaars toegevoegd',
        nonExistingBSN: 'BSN bestaat niet',
        not_serviceConsumer_subsequent_service:
            'De zorgverlener heeft geen actieve afnemer recht voor de auto-opvolgende dienst. Dus deze kan niet ingesteld worden.',
        not_valid_iban: 'Dit is geen geldig IBAN.',
        notValidWeekend: 'Kan niet op een zaterdag of zondag',
        number_base: 'Moet een getal zijn',
        number_integer: 'Getal moet een geheel getal zijn',
        number_max: 'Getal moet kleiner zijn',
        number_min: 'Getal moet groter zijn',
        number_unsafe: 'Getal moet kleiner zijn',
        numberAsString_decimalComma: 'Scheidingsteken moet een komma zijn',
        numberAsString_decimalPoint: 'Scheidingsteken moet een punt zijn',
        numberAsString_greater: 'Getal moet groter zijn',
        numberAsString_leadingZero: 'Voorloopnullen niet toegestaan',
        numberAsString_less: 'Getal moet kleiner zijn',
        numberAsString_max: 'Getal moet kleiner zijn',
        numberAsString_maxDecimals: 'Teveel decimalen',
        numberAsString_min: 'Getal moet groter zijn',
        numberAsString_minDecimals: 'Te weinig decimalen',
        numberAsString_negativeZero: 'Moet een getal zijn',
        numberAsString_noDecimals: 'Mag geen decimalen bevatten',
        numberAsString_notANumber: 'Moet een getal zijn',
        numberAsString_notAString: 'Moet een getal zijn',
        object_and: 'Vul alsjeblieft dit veld in',
        object_anyFilledIn: 'Kies één van de opties',
        object_base: 'Vul alsjeblieft dit veld in',
        object_childAnyFilledIn: 'Je moet minstens één subcategorie selecteren als je een hoofdcategorie selecteert',
        oneDayPeriodRequired: 'Kies minstens één dag met een periode van minstens één uur',
        onePhoneNumberRequired: 'Vul ten minste 1 telefoonnummer in',
        organizationAndGroupSet: 'Er kan alleen een organisatie of een groep worden ingesteld, niet beide tegelijk.',
        passwordNotGoodEnough: 'Moet een score van 80% of meer hebben',
        permission_required: 'Om door te gaan moet er toestemming worden gegeven',
        phoneNumberCareMediationWithoutDate: 'Je moet een datum invoeren voordat je het telefoonnummer opslaat',
        practitionerAlreadyExisting: 'Deze zorgverlener bestaat al',
        requiredForGp: 'Verplicht',
        requiredForRole: 'Verplicht',
        selectAtLeastOneSubCategory:
            'Je moet minstens één subcategorie selecteren als je een hoofdcategorie selecteert.',
        selectTreatment: 'Selecteer minimaal 1 programma',
        serviceActivity_same: 'De actviteit kan niet afhankelijk zijn van zichzelf',
        startTime_required: 'Kies een starttijd',
        string_base: 'Vul alsjeblieft dit veld in',
        string_elfproef: 'Ongeldig BSN',
        string_email: 'Ongeldig e-mailadres',
        string_empty: 'Vul alsjeblieft dit veld in',
        string_guid: 'Vul een geldig UUID in',
        string_kvkNumber: 'Vul een geldig KVK-nummer in',
        string_ledgerNumber: 'Moet bestaan uit vier cijfers',
        string_max: 'De ingevoerde tekst bevat te veel karakters',
        string_min: 'De ingevoerde tekst bevat te weinig karakters',
        string_newPasswordRepeat: 'Moet gelijk zijn aan je gekozen wachtwoord',
        string_numeric: 'Moet bestaan uit cijfers',
        string_pattern: {
            base: 'Vul alsjeblieft dit veld in',
        },
        string_regex: 'De ingevoerde waarde matcht niet met het verplichte patroon',
        string_registrationCode: 'Moet bestaan uit twaalf letters',
        string_totpToken: 'Moet bestaan uit zes cijfers',
        string_trim: 'De ingevoerde tekst mag aan het begin en eind geen spaties bevatten',
        string_zipCodeNL:
            'Deze postcode bestaat niet. Controleer je invoer en gebruik een geldige postcode met 4 cijfers en 2 letters',
        timeParadox: 'moet na "{0}" zijn',
        timePeriodsOverlap: 'De verschillende tijdsperiodes mogen niet overlappen',
        userNameRegex: 'Toegestane karakters zijn A-Z a-z 0-9 @ . _ -',
        weeks_required: 'Geef aantal weken op',
        weight: 'Moet een getal zijn tussen {min} en {max}',
        zorgMailNumberRequired: 'Moet een getal met lengte van 9 cijfers bevatten',
    },
    versionChecker: {
        newVersionAvailableText:
            'Je werkt nu in een verouderde versie. Klik op "ververs de pagina" om de nieuwste versie te gebruiken.',
        newVersionAvailableTitle: 'Nieuwe versie beschikbaar',
        refreshApplication: 'Ververs de pagina',
    },
    viewer: {
        fullScreen: 'Fullscreen',
        openNewTab: 'Open in nieuw tabblad',
        viewer: 'Viewer',
    },
    waitingTime: {
        applicationWaitingTime: 'Aanmeld wachttijd',
        explanation:
            'Wachttijden die je hier aanpast zijn van toepassing op alle zorgverleners in de organisatie die Ksyos gebruiken.',
        manageWaitingTimes: 'Wachttijden beheren',
        total: 'Aantal',
        treatmentWaitingTime: 'Behandel wachttijd',
        waitingTimeForReferrals: 'Wachttijd bij verwijzingen',
        waitingTimes: 'Wachttijden',
        week: 'week',
        weeks: 'weken',
    },
} as const;

export type NL = typeof nl;
export type NLGeneric = GenericNestedObject<typeof nl>;
